import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import LineChart from '../components/LineChart';
import BarChart from '../components/BarChart';
import DataTable from '../components/DataTable';
import DonutChart from '../components/DonutChart';
import { usePeriod } from '../contexts/PeriodContext';
import { formatPeriodLabel, parseLegacyMonYyRange } from '../utils/formatPeriodLabel';
import { useUseCase } from '../contexts/UseCaseContext';
import { useDemoMode } from '../contexts/DemoModeContext';
import { logger } from '../utils/logger';
import { Download, HelpCircle, Plus, CheckCircle2, XCircle, Trash2, Calendar, Trophy, X, ArrowDownUp } from 'lucide-react';
import { standardiseVehicleId } from '../utils/vehicleIdStandardise';
import { createPortal } from 'react-dom';
import { redactVehicleType, redactStationName, redactVehicleId } from '../utils/demoRedaction';
import { InteractiveServiceAreaMapSection } from '../components/InteractiveServiceAreaMapSection';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import FleetStyleKpiCard from '../components/FleetStyleKpiCard';
import MonthPicker from '../components/MonthPicker';
import { useTruthUtilisation } from '../config/featureFlags';
import {
  fetchTruthUtilisationVehicleDays,
  fetchTruthUtilisationConfig,
  fetchTruthUtilisationStatus,
} from '../services/utilisationTruthClient';
import { useProfitabilityDateRange } from '../hooks/useProfitabilityDateRange';
import {
  isTruthUtilisationCsvRow,
  truthUtilisationRowToCsvRow,
} from '../utils/truthUtilisationRowMapper';
import {
  componentWeightsToUi,
} from '../utils/utilisationFormulaWeights';
import { formatLocalDateIso } from '../utils/profitabilityDateRange';
import { STATUS_COLORS, CHART_PALETTE } from '../config/colors';

interface TimeSeriesData {
  trips: {
    daily: Array<{ date: string; trips: number }>;
    weekly: Array<{ week_start: string; trips: number }>;
    monthly: Array<{ month: string; trips: number }>;
  };
  utilization: {
    daily: Array<{ date: string; avg_utilization: number }>;
    weekly: Array<{ week_start: string; avg_utilization: number }>;
    monthly: Array<{ month: string; avg_utilization: number }>;
  };
  mileage: {
    daily: Array<{ date: string; total_miles: number }>;
    weekly: Array<{ week_start: string; total_miles: number }>;
    monthly: Array<{ month: string; total_miles: number }>;
  };
  metadata?: {
    start_date: string;
    end_date: string;
    generated_at?: string;
  };
}

interface CSVRow {
  [key: string]: string;
}

interface SummaryStats {
  SampleSize: number;
  Min: number;
  Max: number;
  Mean: number;
  Q1: number;
  Q2: number;
  Q3: number;
}

interface VehicleTypeData {
  period?: string;
  label_distribution: Array<{ [key: string]: string | number }>;
  summary_stats?: SummaryStats;
}

interface TripPatternData {
  Duration_Label?: {
    [key: string]: any;
    vehicle_level?: {
      [vehicleId: string]: VehicleTypeData;
    };
    station_level?: {
      [station: string]: VehicleTypeData;
    };
  };
  Speed_Label?: {
    [key: string]: any;
    vehicle_level?: {
      [vehicleId: string]: VehicleTypeData;
    };
    station_level?: {
      [station: string]: VehicleTypeData;
    };
  };
  Distance_Label?: {
    [key: string]: any;
    vehicle_level?: {
      [vehicleId: string]: VehicleTypeData;
    };
    station_level?: {
      [station: string]: VehicleTypeData;
    };
  };
}

/** Normalize trip_patterns dataSection: supports both legacy (single period) and by-period (2026-01, 2026-02) shapes. */
function getTripPatternSectionsByPeriod(dataSection: Record<string, any> | null | undefined): Array<{ period: string; section: Record<string, any> }> {
  if (!dataSection || typeof dataSection !== 'object') return [];
  const keys = Object.keys(dataSection);
  const isByPeriod = keys.length > 0 && keys.every(k => /^\d{4}-\d{2}$/.test(k));
  if (isByPeriod) {
    return keys.sort().map(period => ({ period, section: dataSection[period] || {} }));
  }
  const singlePeriod = (dataSection.vehicle_level && Object.values(dataSection.vehicle_level)[0] as any)?.period
    || (dataSection.Lorry || dataSection.Rigid)?.period
    || '';
  return [{ period: singlePeriod, section: dataSection }];
}

/**
 * Convert trip_patterns period into "YYYY-MM" for month filtering.
 * Demo data uses labels like "Jan 2026", while real exports typically use "2026-01".
 */
function getTripPatternPeriodYearMonth(period: string | null | undefined): string | null {
  const p = (period ?? '').toString().trim();
  if (!p) return null;

  // "2026-01" (or "2026-01-01") -> "2026-01"
  const isoMatch = p.match(/^(\d{4})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}`;

  // "Jan 2026" / "September 2026" -> "2026-09"
  const monthMatch = p.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthMatch) {
    const monthStr = monthMatch[1].slice(0, 3).toLowerCase();
    const year = monthMatch[2];
    const map: Record<string, number> = {
      jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
      jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
    };
    const monthNum = map[monthStr];
    if (monthNum) return `${year}-${String(monthNum).padStart(2, '0')}`;
  }

  return null;
}

/** Normalize utilization_patterns vehicle entry: supports both legacy (single period at top level) and periods dict. */
function getUtilizationPeriodEntries(content: any): Array<{ period: string; vehicle_type?: string; metrics?: any; temporal_patterns?: any; operational_patterns?: any }> {
  if (!content || typeof content !== 'object') return [];
  if (content.periods && typeof content.periods === 'object') {
    return Object.entries(content.periods).map(([period, entry]: [string, any]) => ({
      period,
      vehicle_type: entry?.vehicle_type,
      metrics: entry?.metrics,
      temporal_patterns: entry?.temporal_patterns,
      operational_patterns: entry?.operational_patterns,
    }));
  }
  return [{
    period: content.period || '',
    vehicle_type: content.vehicle_type,
    metrics: content.metrics,
    temporal_patterns: content.temporal_patterns,
    operational_patterns: content.operational_patterns,
  }];
}

/** Aggregate temporal_patterns across all period entries (for multi-month data). */
function aggregateTemporalPatterns(content: any): { hourly_distribution: Record<string, number>; daily_distribution: Record<string, number>; monthly_distribution: Record<string, number> } {
  const entries = getUtilizationPeriodEntries(content);
  const hourly: Record<string, number> = {};
  const daily: Record<string, number> = {};
  const monthly: Record<string, number> = {};
  entries.forEach(e => {
    if (e.temporal_patterns?.hourly_distribution && typeof e.temporal_patterns.hourly_distribution === 'object') {
      Object.entries(e.temporal_patterns.hourly_distribution).forEach(([k, v]) => { hourly[k] = (hourly[k] || 0) + (Number(v) || 0); });
    }
    if (e.temporal_patterns?.daily_distribution && typeof e.temporal_patterns.daily_distribution === 'object') {
      Object.entries(e.temporal_patterns.daily_distribution).forEach(([k, v]) => { daily[k] = (daily[k] || 0) + (Number(v) || 0); });
    }
    if (e.temporal_patterns?.monthly_distribution && typeof e.temporal_patterns.monthly_distribution === 'object') {
      Object.entries(e.temporal_patterns.monthly_distribution).forEach(([k, v]) => { monthly[k] = (monthly[k] || 0) + (Number(v) || 0); });
    }
  });
  return { hourly_distribution: hourly, daily_distribution: daily, monthly_distribution: monthly };
}

/** Format period for display — same style as logistics: "YYYYMMDD to YYYYMMDD" (legacy MONYY → full month range). */
function formatPeriodToDateRange(period: string): string {
  return formatPeriodLabel(period);
}

/** Get end month of period for chart filtering: { year, month } (month 1-12). */
function getChartMonthFromPeriod(period: string | null | undefined): { year: number; month: number } | null {
  if (!period || typeof period !== 'string') return null;
  const p = period.trim();
  const match = p.match(/^(\d{8})_to_(\d{8})/) || p.match(/^(\d{8})\s+to\s+(\d{8})/i);
  if (match) {
    const yyyymmdd = match[2];
    const y = parseInt(yyyymmdd.slice(0, 4), 10);
    const m = parseInt(yyyymmdd.slice(4, 6), 10);
    if (m >= 1 && m <= 12) return { year: y, month: m };
  }
  if (/^\d{8}$/.test(p)) {
    const y = parseInt(p.slice(0, 4), 10);
    const m = parseInt(p.slice(4, 6), 10);
    if (m >= 1 && m <= 12) return { year: y, month: m };
  }
  const legacy = parseLegacyMonYyRange(p);
  if (legacy) {
    const end = legacy.endYmd;
    const y = parseInt(end.slice(0, 4), 10);
    const m = parseInt(end.slice(4, 6), 10);
    if (m >= 1 && m <= 12) return { year: y, month: m };
  }
  return null;
}

/** Get all months covered by a period (for multi-month ranges). */
function getMonthsInPeriod(period: string | null | undefined): Array<{ year: number; month: number }> {
  if (!period || typeof period !== 'string') return [];
  const p = period.trim();
  const match = p.match(/^(\d{8})_to_(\d{8})/) || p.match(/^(\d{8})\s+to\s+(\d{8})/i);
  let startY = 0, startM = 0, endY = 0, endM = 0;
  if (match) {
    const start = match[1];
    const end = match[2];
    startY = parseInt(start.slice(0, 4), 10);
    startM = parseInt(start.slice(4, 6), 10);
    endY = parseInt(end.slice(0, 4), 10);
    endM = parseInt(end.slice(4, 6), 10);
  } else if (/^\d{8}$/.test(p)) {
    startY = endY = parseInt(p.slice(0, 4), 10);
    startM = endM = parseInt(p.slice(4, 6), 10);
  } else {
    const legacy = parseLegacyMonYyRange(p);
    if (legacy) {
      const end = legacy.endYmd;
      const y = parseInt(end.slice(0, 4), 10);
      const m = parseInt(end.slice(4, 6), 10);
      if (m >= 1 && m <= 12) return [{ year: y, month: m }];
    }
    return [];
  }
  if (startM < 1 || startM > 12 || endM < 1 || endM > 12) return [];
  const out: Array<{ year: number; month: number }> = [];
  let y = startY, m = startM;
  for (;;) {
    out.push({ year: y, month: m });
    if (y === endY && m === endM) break;
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

/** Whether the period's date range contains the given month. */
function periodContainsMonth(period: string | null | undefined, month: { year: number; month: number }): boolean {
  const months = getMonthsInPeriod(period);
  return months.some(m => m.year === month.year && m.month === month.month);
}

/** Format { year, month } as "Jan 2026". */
function formatMonthLabel(year: number, month: number): string {
  if (month >= 1 && month <= 12) {
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  }
  return `${String(month).padStart(2, '0')} ${year}`;
}

function pad2UtilDate(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Parse utilisation CSV date cells: ISO YYYY-MM-DD, UK DD/MM/YYYY (and DD/MM/YY), DD-MM-YYYY, or Date-parseable strings.
 * Returns display/export as ISO YYYY-MM-DD for consistent UI and range filters.
 */
function parseUtilisationDateRaw(value: unknown): { display: string; sortKey: number; export: string } {
  if (value == null) return { display: '—', sortKey: 0, export: '' };
  const trimmed = String(value).trim();
  if (!trimmed) return { display: '—', sortKey: 0, export: '' };

  const isoHead = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoHead) {
    const ymd = `${isoHead[1]}-${isoHead[2]}-${isoHead[3]}`;
    const num = parseInt(isoHead[1] + isoHead[2] + isoHead[3], 10);
    return { display: ymd, sortKey: isNaN(num) ? 0 : num, export: ymd };
  }

  const dmy4 = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (dmy4) {
    const day = parseInt(dmy4[1], 10);
    const month = parseInt(dmy4[2], 10);
    const year = parseInt(dmy4[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const dt = new Date(year, month - 1, day);
      if (dt.getFullYear() === year && dt.getMonth() === month - 1 && dt.getDate() === day) {
        const ymd = `${year}-${pad2UtilDate(month)}-${pad2UtilDate(day)}`;
        const num = year * 10000 + month * 100 + day;
        return { display: ymd, sortKey: num, export: ymd };
      }
    }
  }

  const dmy2 = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})(?!\d)/);
  if (dmy2) {
    const day = parseInt(dmy2[1], 10);
    const month = parseInt(dmy2[2], 10);
    const y2 = parseInt(dmy2[3], 10);
    const year = y2 >= 70 ? 1900 + y2 : 2000 + y2;
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const dt = new Date(year, month - 1, day);
      if (dt.getFullYear() === year && dt.getMonth() === month - 1 && dt.getDate() === day) {
        const ymd = `${year}-${pad2UtilDate(month)}-${pad2UtilDate(day)}`;
        const num = year * 10000 + month * 100 + day;
        return { display: ymd, sortKey: num, export: ymd };
      }
    }
  }

  const dmDashY4 = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (dmDashY4) {
    const day = parseInt(dmDashY4[1], 10);
    const month = parseInt(dmDashY4[2], 10);
    const year = parseInt(dmDashY4[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const dt = new Date(year, month - 1, day);
      if (dt.getFullYear() === year && dt.getMonth() === month - 1 && dt.getDate() === day) {
        const ymd = `${year}-${pad2UtilDate(month)}-${pad2UtilDate(day)}`;
        const num = year * 10000 + month * 100 + day;
        return { display: ymd, sortKey: num, export: ymd };
      }
    }
  }

  const fallback = new Date(trimmed);
  if (!isNaN(fallback.getTime())) {
    const ymd = fallback.toISOString().slice(0, 10);
    const num = parseInt(ymd.replace(/-/g, ''), 10);
    return { display: ymd, sortKey: num, export: ymd };
  }

  return { display: '—', sortKey: 0, export: '' };
}

/** Normalise `date` / `Date` from utilisation CSV rows to ISO YYYY-MM-DD for display, sort, and range filters. */
function normalizeUtilisationCsvDateForRow(csvRow: Record<string, any>): { display: string; sortKey: number; export: string } {
  return parseUtilisationDateRaw(csvRow.date ?? csvRow.Date);
}

function utilisationCsvRowIsoDate(csvRow: Record<string, any>): string | null {
  const e = normalizeUtilisationCsvDateForRow(csvRow).export;
  return e || null;
}

interface FleetReportProps {
  variant?: 'full' | 'utilisation-only';
}

export default function Dashboard({ variant = 'full' }: FleetReportProps) {
  const { periods, selectedPeriod, setSelectedPeriod } = usePeriod();
  const { useCase } = useUseCase();
  const { isDemoMode } = useDemoMode();
  const useTruthUtil = useTruthUtilisation();
  const utilisationRefreshToken = 0;
  const utilTruthDateRange = useProfitabilityDateRange();
  const serviceType = useCase === 'emergency' ? 'emergency_services' : 'logistics';
  const [selectedTimeInterval] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [weekOffset, setWeekOffset] = useState<number>(0);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData | null>(null);
  const [vehicleLevelCSV, setVehicleLevelCSV] = useState<CSVRow[]>([]);
  const [fleetListCSV, setFleetListCSV] = useState<CSVRow[]>([]);
  const [vehicleTypeLevelCSV, setVehicleTypeLevelCSV] = useState<CSVRow[]>([]);
  const [stationLevelCSV, setStationLevelCSV] = useState<CSVRow[]>([]);
  const [busyStationsCSV, setBusyStationsCSV] = useState<CSVRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTripCharacteristic, setSelectedTripCharacteristic] = useState<'duration' | 'distance' | 'speed'>('duration');
  const [selectedVehicleTypeFilter, setSelectedVehicleTypeFilter] = useState<string | null>(null);
  const [selectedVehicleTypeForTrips, setSelectedVehicleTypeForTrips] = useState<string | null>(null);
  const [selectedStationForTrips, setSelectedStationForTrips] = useState<string | null>(null);
  const [tripPatternData, setTripPatternData] = useState<TripPatternData | null>(null);
  const [tripPatternLoading, setTripPatternLoading] = useState(true);
  const [selectedTemporalView, setSelectedTemporalView] = useState<'hourly' | 'daily' | 'monthly'>('hourly');
  const [selectedTemporalVehicle, setSelectedTemporalVehicle] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [selectedVehicleType, setSelectedVehicleType] = useState<string | null>(null);
  /** Vehicle type inferred from the clicked row in Vehicle Summary – used only to show that type's targets under the sliders (does not change the Vehicle Type dropdown). */
  const [, setSelectedVehicleTypeForTargets] = useState<string | null>(null);
  /** Row id of the selected Vehicle Summary row (click again to deselect). */
  const [selectedVehicleSummaryRowId, setSelectedVehicleSummaryRowId] = useState<string | null>(null);
  /** Efficiency values from the selected Vehicle Summary row (for formula display). */
  const [, setSelectedRowEfficiencyValues] = useState<{
    milesEff: number; revEff: number; timeEff: number; loadEff: number;
    actualMiles?: number; actualRevenue?: number; actualTimeHrs?: number; actualLoadPct?: number;
    rowTargetMiles?: number; rowTargetRevenue?: number; rowTargetTimeHrs?: number;
  } | null>(null);
  const [showManualTripForm, setShowManualTripForm] = useState(false);
  const [manualTripForm, setManualTripForm] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    vehicle_id: '',
    vehicle_type: '',
    distance: '',
    duration: '',
    start_location: '',
    end_location: '',
    notes: ''
  });
  const [newVehicleId, setNewVehicleId] = useState('');
  const [newVehicleType, setNewVehicleType] = useState('');
  const [showNewVehicleTypeInput, setShowNewVehicleTypeInput] = useState(false);
  const [submittingTrip, setSubmittingTrip] = useState(false);
  const [tripSubmitSuccess, setTripSubmitSuccess] = useState(false);
  const [tripSubmitError, setTripSubmitError] = useState<string | null>(null);
  const [manualTripEntries, setManualTripEntries] = useState<any[]>([]);
  const [selectedEntries, setSelectedEntries] = useState<Set<number>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<number[] | null>(null);
  /** Map vin (Vehicle_ID) to customerVehicleName from optifleet vehicle list */
  const [vinToCustomerNameMap, setVinToCustomerNameMap] = useState<Map<string, string>>(new Map());
  // Reused from ProfitabilityReport: time window and vehicle lookup
  const [periodType, setPeriodType] = useState<'day' | 'week' | 'month'>('day');
  const [selectedSummaryDate, setSelectedSummaryDate] = useState<string>('');
  const [selectedPeriodRange, setSelectedPeriodRange] = useState<{ startDate: string; endDate: string } | null>(null);
  const [calendarPageMonth, setCalendarPageMonth] = useState<{ year: number; month: number } | null>(null);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([]);
  const [performersShowBottom5, setPerformersShowBottom5] = useState(false);
  const [bestDaysShowBottom5, setBestDaysShowBottom5] = useState(false);
  const [vehicleIdInputValue, setVehicleIdInputValue] = useState('');
  const [selectedLookupVehicleType, setSelectedLookupVehicleType] = useState<string | null>(null);
  /** Selected month for charts and maps (from month button bar). When null, use end month of selectedPeriod. */
  const [selectedMonthForCharts, setSelectedMonthForCharts] = useState<{ year: number; month: number } | null>(null);
  /** Months with truth-layer GPS (vehicle_position_observed) — drives Fleet Report month buttons for maps. */
  const [gpsAvailableMonths, setGpsAvailableMonths] = useState<Array<{
    year: number;
    month: number;
    startDate: string;
    endDate: string;
    point_count: number;
  }>>([]);
  const trendSectionRef = useRef<HTMLDivElement>(null);

  // Load months that have truth-layer GPS so the month button bar tracks ingested data.
  useEffect(() => {
    if (isDemoMode) {
      setGpsAvailableMonths([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { API_ENDPOINTS } = await import('../config/api');
        const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.geographic.months());
        if (!res.ok) return;
        const json = await res.json().catch(() => ({})) as {
          months?: Array<{ year: number; month: number; startDate: string; endDate: string; point_count?: number }>;
        };
        if (cancelled || !Array.isArray(json.months)) return;
        const months = json.months
          .filter((m) => m && Number.isFinite(m.year) && m.month >= 1 && m.month <= 12)
          .map((m) => ({
            year: m.year,
            month: m.month,
            startDate: m.startDate || `${m.year}-${String(m.month).padStart(2, '0')}-01`,
            endDate: m.endDate || m.startDate || `${m.year}-${String(m.month).padStart(2, '0')}-01`,
            point_count: Number(m.point_count) || 0,
          }))
          .sort((a, b) => (b.year !== a.year ? b.year - a.year : b.month - a.month));
        setGpsAvailableMonths(months);
      } catch (err) {
        console.warn('[FleetReport] Failed to load GPS available months:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isDemoMode, serviceType]);

  const buildFullMonthPeriod = useCallback((year: number, month1to12: number) => {
    const startDate = `${year}-${String(month1to12).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month1to12, 0).getDate();
    const endDate = `${year}-${String(month1to12).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate };
  }, []);

  /**
   * Utilisation (truth): on first load, discover last_computed coverage and align the report
   * period + month button to the most recent month with data (avoids empty last-7-days + Jan fallback).
   */
  const hasAppliedUtilTruthDefaultRef = useRef(false);
  useEffect(() => {
    if (variant !== 'utilisation-only' || !useTruthUtil || isDemoMode) return;
    if (hasAppliedUtilTruthDefaultRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const today = formatLocalDateIso(new Date());
        // Only need last_computed from the utilisation manifest — use a one-day window.
        const status = await fetchTruthUtilisationStatus(today, today);
        if (cancelled) return;
        const lc = status.last_computed;
        const start = lc?.start_date ? String(lc.start_date).slice(0, 10) : '';
        const end = lc?.end_date ? String(lc.end_date).slice(0, 10) : '';
        if (!start || !end || (lc?.record_count ?? 0) <= 0) return;
        const [yStr, mStr] = end.split('-');
        const year = parseInt(yStr, 10);
        const month = parseInt(mStr, 10);
        if (!year || month < 1 || month > 12) return;
        const full = buildFullMonthPeriod(year, month);
        hasAppliedUtilTruthDefaultRef.current = true;
        // Snap UI + fetches to the latest month with data — not the full last_computed span.
        utilTruthDateRange.setDateRange(full.startDate, full.endDate);
        setSelectedMonthForCharts({ year, month });
        setCalendarPageMonth({ year, month: month - 1 });
        setSelectedPeriodRange({ startDate: full.startDate, endDate: full.endDate });
        setPeriodType('day');
        setSelectedSummaryDate('');
      } catch (err) {
        console.warn('[FleetReport] Failed to discover utilisation coverage for default month:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [variant, useTruthUtil, isDemoMode, buildFullMonthPeriod, utilTruthDateRange.setDateRange]);

  // Score weighting for Overall Util % (Utilisation tab only): Miles Eff %, Rev Eff %, Time Eff %, Load Eff %
  const defaultUtilisationWeights = { milesEff: 25, revEff: 25, timeEff: 25, loadEff: 25 };
  const [utilisationWeights, setUtilisationWeights] = useState({ ...defaultUtilisationWeights });
  /** Edited target rows (batch CSV recompute); null means use config */
  const [editedUtilTargetsRows] = useState<Array<{ vehicleType: string; milesPerDay: number; revenuePerDay: number; timeHrs: number; capacityKg: number }> | null>(null);

  // Fallback utilisation targets (from BackEnd/logistics/config/util_config.json) when API config is not available
  const FALLBACK_UTIL_TARGETS_CONFIG = {
    target_driving_hours: 8,
    target_waiting_hours: 1,
    mileage_target_miles: { Lorry: 200, 'Tractor Unit': 220, 'Rigid Truck': 150, 'Service Van': 120, 'Mini Truck': 80, default: 150 },
    revenue_target_gbp: { Lorry: 650, 'Tractor Unit': 700, 'Rigid Truck': 450, 'Service Van': 280, 'Mini Truck': 180, default: 500 },
    load_capacity_kg: { Lorry: 26000, 'Tractor Unit': 26000, 'Rigid Truck': 18000, 'Service Van': 1500, 'Mini Truck': 800, default: 20000 },
  } as const;

  const ES_UTIL_TARGET_TYPES = [
    'Response car',
    'Small van',
    'Large van',
    'Pumping appliance',
    'Other appliance',
  ] as const;
  const ES_FALLBACK_MILES: Record<string, number> = {
    'Response car': 80,
    'Small van': 100,
    'Large van': 120,
    'Pumping appliance': 60,
    'Other appliance': 90,
    default: 100,
  };

  // Utilisation targets from util_config.json (vehicle-type-based) for display under score sliders
  // Hours may be a legacy fleet-wide scalar or a per-type map (truth config).
  const [utilisationTargetsConfig, setUtilisationTargetsConfig] = useState<{
    target_driving_hours?: number | Record<string, number>;
    target_waiting_hours?: number | Record<string, number>;
    mileage_target_miles?: Record<string, number>;
    revenue_target_gbp?: Record<string, number>;
    load_capacity_kg?: Record<string, number>;
  } | null>(null);

  // Compute weighted Overall Util % (ES: miles + time only; logistics: four components)
  const computeWeightedOverallUtil = (milesEff: number, revEff: number, timeEff: number, loadEff: number): number => {
    const w = utilisationWeights;
    if (serviceType === 'emergency_services') {
      const totalW = w.milesEff + w.timeEff;
      if (totalW === 0) return 0;
      return (w.milesEff * (milesEff || 0) + w.timeEff * (timeEff || 0)) / totalW;
    }
    const totalW = w.milesEff + w.revEff + w.timeEff + w.loadEff;
    if (totalW === 0) return 0;
    return (w.milesEff * (milesEff || 0) + w.revEff * (revEff || 0) + w.timeEff * (timeEff || 0) + w.loadEff * (loadEff || 0)) / totalW;
  };

  useEffect(() => {
    if (variant === 'utilisation-only' && useTruthUtil && !isDemoMode) return;
    if (serviceType === 'emergency_services') {
      setUtilisationWeights({ milesEff: 50, revEff: 0, timeEff: 50, loadEff: 0 });
    } else {
      setUtilisationWeights({ milesEff: 25, revEff: 25, timeEff: 25, loadEff: 25 });
    }
  }, [serviceType, variant, useTruthUtil, isDemoMode]);

  useEffect(() => {
    if (variant !== 'utilisation-only' || !useTruthUtil || isDemoMode) return;
    let cancelled = false;
    void fetchTruthUtilisationConfig().then((cfg) => {
      if (cancelled || !cfg) return;
      if (cfg.utilisation_analysis) {
        setUtilisationTargetsConfig(cfg.utilisation_analysis);
      }
      const cw = cfg?.utilisation_analysis?.component_weights;
      if (!cw) return;
      const emergency = serviceType === 'emergency_services';
      const ui = componentWeightsToUi(cw, emergency);
      setUtilisationWeights(ui);
    });
    return () => {
      cancelled = true;
    };
  }, [variant, useTruthUtil, isDemoMode, serviceType, utilisationRefreshToken]);

  /** Targets by vehicle type for modal Step 2 table (from util_config) */
  const getUtilisationTargetsTableRows = useMemo(() => {
    const c = {
      ...FALLBACK_UTIL_TARGETS_CONFIG,
      ...(utilisationTargetsConfig || {}),
    };
    const hoursFrom = (
      field: 'target_driving_hours' | 'target_waiting_hours',
      vehicleType: string,
      fallback: number,
    ): number => {
      const raw = c[field];
      if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
      if (raw && typeof raw === 'object') {
        const map = raw as Record<string, number>;
        return map[vehicleType] ?? map.Default ?? map.default ?? fallback;
      }
      return fallback;
    };
    if (serviceType === 'emergency_services') {
      const cfgMiles = (c.mileage_target_miles || {}) as Record<string, number>;
      return ES_UTIL_TARGET_TYPES.map(vehicleType => {
        const drive = hoursFrom('target_driving_hours', vehicleType, 8);
        const wait = hoursFrom('target_waiting_hours', vehicleType, 1);
        return {
          vehicleType,
          milesPerDay: cfgMiles[vehicleType] ?? ES_FALLBACK_MILES[vehicleType] ?? ES_FALLBACK_MILES.default ?? 100,
          revenuePerDay: 0,
          timeHrs: drive + wait,
          capacityKg: 0,
        };
      });
    }
    const types = ['Tractor Unit', 'Rigid Truck', 'Lorry', 'Service Van', 'Mini Truck'] as const;
    return types.map(vehicleType => {
      const miles = (c.mileage_target_miles as Record<string, number>)?.[vehicleType] ?? (c.mileage_target_miles as Record<string, number>)?.default ?? 0;
      const revenue = (c.revenue_target_gbp as Record<string, number>)?.[vehicleType] ?? (c.revenue_target_gbp as Record<string, number>)?.default ?? 0;
      const capacity = (c.load_capacity_kg as Record<string, number>)?.[vehicleType] ?? (c.load_capacity_kg as Record<string, number>)?.default ?? 0;
      const drive = hoursFrom('target_driving_hours', vehicleType, 8);
      const wait = hoursFrom('target_waiting_hours', vehicleType, 1);
      return {
        vehicleType,
        milesPerDay: miles,
        revenuePerDay: revenue,
        timeHrs: drive + wait,
        capacityKg: capacity,
      };
    });
  }, [utilisationTargetsConfig, serviceType]);

  /** Normalise vehicle type for target lookup (match backend keys: Rigid Truck, Lorry, etc.) */
  const normaliseVehicleTypeForTargets = (vehicleType: string): string => {
    const t = (vehicleType || '').trim();
    if (serviceType === 'emergency_services') {
      const lower = t.toLowerCase();
      for (const es of ES_UTIL_TARGET_TYPES) {
        if (es.toLowerCase() === lower) return es;
      }
      if (/response|car/i.test(lower)) return 'Response car';
      if (/pumping|appliance/i.test(lower) && !/other/i.test(lower)) return 'Pumping appliance';
      if (/other|appliance/i.test(lower)) return 'Other appliance';
      if (/large\s*van/i.test(lower)) return 'Large van';
      if (/small\s*van|^van$/i.test(lower)) return 'Small van';
      return t || 'Response car';
    }
    if (/rigid|17t|van|d wide/i.test(t)) return 'Rigid Truck';
    if (/artic|33t|tractor|hgv|lorry/i.test(t)) return 'Lorry';
    if (/tractor/i.test(t)) return 'Tractor Unit';
    if (/service van|van/i.test(t)) return 'Service Van';
    if (/mini/i.test(t)) return 'Mini Truck';
    return t || 'Rigid Truck';
  };

  /** Get targets for a vehicle type: user-edited rows > config table. Used to compute efficiency from CSV intermediates. */
  const getTargetsForVehicleType = (vehicleType: string): { targetMiles: number; targetRevenue: number; targetTimeHrs: number } => {
    const normalised = normaliseVehicleTypeForTargets(vehicleType);
    const rows = editedUtilTargetsRows ?? getUtilisationTargetsTableRows;
    const row = rows.find(r => r.vehicleType.toLowerCase() === normalised.toLowerCase());
    if (row) {
      return {
        targetMiles: row.milesPerDay || 150,
        targetRevenue: row.revenuePerDay || 500,
        targetTimeHrs: row.timeHrs || 9,
      };
    }
    const first = getUtilisationTargetsTableRows[0];
    return {
      targetMiles: first?.milesPerDay ?? 150,
      targetRevenue: first?.revenuePerDay ?? 500,
      targetTimeHrs: first?.timeHrs ?? 9,
    };
  };

  const utilisationMetricsSectionRef = useRef<HTMLElement | null>(null);

  // ✅ Parse CSV line - handle quoted values and commas within quotes
  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let currentValue = '';
    let inQuotes = false;
    
    for (let charIdx = 0; charIdx < line.length; charIdx++) {
      const char = line[charIdx];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    // Add the last value
    values.push(currentValue.trim());
    return values;
  };

  // Helper function to parse CSV
  // ✅ For fleet list CSV, only keep VEHICLEINDEX and VEHICLE TYPE columns (exclude MODEL and others)
  const parseCSV = (text: string, isFleetList: boolean = false): CSVRow[] => {
    // Handle both \n and \r\n line endings
    const lines = text.trim().split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length < 2) {
      logger.warn(`[parseCSV] Not enough lines in CSV (got ${lines.length} lines, need at least 2)`);
      return [];
    }
    
    // ✅ Use proper CSV parser for headers (handles quoted values)
    // Normalize headers: trim whitespace and strip UTF-8 BOM (common on Windows-exported CSVs)
    const headers = parseCSVLine(lines[0]).map((h, idx) => {
      const trimmed = (h ?? '').trim();
      return idx === 0 ? trimmed.replace(/^\uFEFF/, '') : trimmed;
    });
    const rows: CSVRow[] = [];
    
    if (isFleetList) {
      logger.debug(`[parseCSV] Fleet list mode - Headers found:`, headers);
    }
    
    // ✅ For fleet list, filter out unwanted columns (MODEL, STATION, etc.)
    const excludedColumns = isFleetList ? ['MODEL', 'STATION', 'STN #', 'STN', 'STATION #', 'STATION#', 'STN#', 'LOCATION', 'BASE'] : [];
    const headerIndicesToKeep = isFleetList 
      ? headers
          .map((header, index) => {
            const upperHeader = header.toUpperCase().trim();
            // Keep VEHICLEINDEX, MAPPED VEHICLE TYPE, and VEHICLE TYPE (for reference/fallback)
            if (upperHeader === 'VEHICLEINDEX' || 
                upperHeader === 'MAPPED VEHICLE TYPE' || 
                upperHeader === 'MAPPEDVEHICLETYPE' || 
                upperHeader === 'MAPPED_VEHICLE_TYPE' ||
                (upperHeader === 'VEHICLE TYPE' && !upperHeader.includes('MAPPED'))) {
              return index;
            }
            // Exclude unwanted columns
            if (excludedColumns.some(excluded => upperHeader.includes(excluded))) {
              return -1;
            }
            // For fleet list, exclude other columns
            return -1;
          })
          .filter(idx => idx >= 0)
      : headers.map((_, index) => index);
    
    if (isFleetList) {
      const keptHeaders = headerIndicesToKeep.map(idx => headers[idx]);
      logger.debug(`[parseCSV] Fleet list mode - Keeping columns:`, keptHeaders);
    }
    
    for (let i = 1; i < lines.length; i++) {
      // ✅ Use proper CSV parser for data rows (handles quoted values)
      const values = parseCSVLine(lines[i]);
      const row: CSVRow = {};
      
      if (isFleetList) {
        // Only store VEHICLEINDEX and VEHICLE TYPE columns
      headers.forEach((header, index) => {
          if (headerIndicesToKeep.includes(index)) {
        row[header] = values[index] || '';
          }
        });
      } else {
        // For other CSVs, store all columns
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
      }
      
      rows.push(row);
    }
    
    return rows;
  };

  // Helper function to parse MultiIndex CSV (used for busy stations CSV)
  const parseMultiIndexCSV = (text: string): CSVRow[] => {
    const lines = text.trim().split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length < 4) {
      logger.warn('[parseMultiIndexCSV] Not enough lines:', lines.length);
      return [];
    }

    // Row 0: Metric, Trip Density (trips/day), Downtime (hours), ...
    // Row 1: Statistic, trip_density, (Mean), (Q2), ...
    // Row 2: Station, (empty), ...
    // Row 3+: Data rows

    const metricRow = lines[0].split(',').map(h => h.trim());
    const statisticRow = lines[1].split(',').map(h => h.trim());
    const stationRow = lines[2].split(',').map(h => h.trim());
    const stationIndex = stationRow.indexOf('Station');

    const columnMap = new Map<number, string>();
    if (stationIndex >= 0) {
      columnMap.set(stationIndex, 'Station');
    }

    let currentMetric = '';
    for (let i = 0; i < metricRow.length; i++) {
      const level1 = metricRow[i] || '';
      const level2 = statisticRow[i] || '';

      if (level1 === 'Metric' || level2 === 'Statistic') {
        continue;
      }

      if (level2 === 'Station' || i === stationIndex) {
        continue;
      }

      if (level1 && level1 !== 'Metric' && level1 !== 'Statistic') {
        currentMetric = level1;
      }

      if (level2 && (level2 === '(Mean)' || level2 === '(Q2)' || level2 === 'trip_density')) {
        let statName = level2.replace(/[()]/g, '');
        if (statName === 'trip_density') {
          statName = 'tripdensity';
        }

        if (currentMetric) {
          const cleanMetric = currentMetric.trim();
          columnMap.set(i, `${cleanMetric}_${statName}`);
        } else if (level2 === 'trip_density') {
          columnMap.set(i, 'Trip Density (trips/day)_tripdensity');
        } else {
          columnMap.set(i, statName);
        }
      }
    }

    const rows: CSVRow[] = [];
    for (let i = 3; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row: CSVRow = {};

      columnMap.forEach((columnName: string, index: number) => {
        if (index < values.length) {
          row[columnName] = values[index] || '';
        }
      });

      if (row['Station'] && row['Station'].trim() !== '') {
        rows.push(row);
      }
    }

    if (rows.length > 0) {
      logger.debug('[parseMultiIndexCSV] Parsed rows:', rows.length);
      logger.debug('[parseMultiIndexCSV] First row keys:', Object.keys(rows[0]));
    }

    return rows;
  };

  // Load fleet list CSV (not period-dependent, so load separately)
  useEffect(() => {
    setFleetListCSV([]);
    let isCancelled = false;
    
    import('../config/api').then(({ API_ENDPOINTS }) => {
      // ✅ Load fleet list CSV (needed for ES mode fleet size calculation)
      // This is not period-dependent, so load it whenever serviceType changes
      const fleetListUrl = API_ENDPOINTS.fleetList(serviceType);
      logger.debug(`Fetching fleet list from: ${fleetListUrl} (serviceType: ${serviceType}, useCase: ${useCase})`);
      authenticatedFetch(fleetListUrl, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache'
        }
      })
        .then((res: Response) => {
          if (isCancelled) return;
          if (!res.ok) {
            throw new Error(`Failed to load fleet list: ${res.status}`);
          }
          return res.text();
        })
        .then((text: string | undefined) => {
          if (isCancelled || !text) {
            logger.warn(`[Fleet List] No text received from API (serviceType: ${serviceType})`);
      return;
    }
    
          logger.debug(`[Fleet List] Received CSV text length: ${text.length} characters`);
          logger.debug(`[Fleet List] First 200 characters of CSV:`, text.substring(0, 200));
          
          // ✅ Parse fleet list CSV and filter out MODEL and other unwanted columns
          const parsed = parseCSV(text, true); // true = isFleetList
          
          logger.debug(`[Fleet List] Parsed ${parsed.length} rows from CSV`);
          
          // ✅ Log breakdown by vehicle type to verify merge
          if (parsed.length > 0) {
            const typeCounts = new Map<string, number>();
            const expectedTypes = ['Response car', 'Small van', 'Large van', 'Pumping appliance', 'Other appliance'];
            expectedTypes.forEach(type => typeCounts.set(type, 0));
            
            parsed.forEach(row => {
              let vehicleType = '';
              for (const key in row) {
                const upperKey = key.toUpperCase().trim();
                if (upperKey === 'MAPPED VEHICLE TYPE' || 
                    upperKey === 'MAPPEDVEHICLETYPE' || 
                    upperKey === 'MAPPED_VEHICLE_TYPE') {
                  vehicleType = row[key] || '';
                  break;
                }
              }
              
              if (vehicleType && vehicleType.trim() !== '') {
                const matchedType = expectedTypes.find(t => t.toLowerCase() === vehicleType.trim().toLowerCase());
                if (matchedType) {
                  typeCounts.set(matchedType, (typeCounts.get(matchedType) || 0) + 1);
                }
              }
            });
            
            const totalCount = Array.from(typeCounts.values()).reduce((sum: number, count: number) => sum + count, 0);
            logger.debug(`[Fleet List] Vehicle type breakdown:`, Object.fromEntries(typeCounts));
            logger.debug(`[Fleet List] Total vehicles counted: ${totalCount} (should match merged total from backend)`);
          }
          
          // ✅ Verify that MODEL column is NOT present in parsed data
          if (parsed.length > 0) {
            const allKeys = new Set<string>();
            parsed.slice(0, 10).forEach(row => {
              Object.keys(row).forEach(key => allKeys.add(key));
            });
            const allKeysArray = Array.from(allKeys);
            logger.debug(`[Fleet List] Columns in parsed data:`, allKeysArray.sort());
            
            // ✅ Check if MODEL column is present (it shouldn't be!)
            const hasModel = allKeysArray.some(key => key.toUpperCase().trim() === 'MODEL');
            if (hasModel) {
              logger.error(`[Fleet List] ⚠️ ERROR: MODEL column is still present in parsed data! This should not happen.`);
            }
            
            // ✅ Verify expected columns are present
            // Allowed columns: VEHICLEINDEX, MAPPED VEHICLE TYPE, and VEHICLE TYPE (for reference)
            const allowedColumns = ['VEHICLEINDEX', 'MAPPED VEHICLE TYPE', 'MAPPEDVEHICLETYPE', 'MAPPED_VEHICLE_TYPE', 'VEHICLE TYPE', 'VEHICLETYPE', 'VEHICLE_TYPE'];
            const unexpectedColumns = allKeysArray.filter(key => {
              const upperKey = key.toUpperCase().trim();
              return !allowedColumns.some(allowed => upperKey === allowed || upperKey.includes(allowed));
            });
            if (unexpectedColumns.length > 0) {
              logger.warn(`[Fleet List] ⚠️ Unexpected columns found:`, unexpectedColumns);
            }
            
            // Check if MAPPED VEHICLE TYPE column exists (required - no fallback)
            const hasMappedVehicleType = allKeysArray.some(key => 
              key.toUpperCase().trim() === 'MAPPED VEHICLE TYPE' || 
              key.toUpperCase().trim() === 'MAPPEDVEHICLETYPE' || 
              key.toUpperCase().trim() === 'MAPPED_VEHICLE_TYPE'
            );
            
            if (hasMappedVehicleType) {
              logger.debug(`[Fleet List] ✅ MAPPED VEHICLE TYPE column found - will use this column`);
              // Log sample mapped vehicle types from first few rows
              const sampleTypes = parsed.slice(0, 10).map(row => {
                for (const key in row) {
                  const upperKey = key.toUpperCase().trim();
                  if (upperKey === 'MAPPED VEHICLE TYPE' || upperKey === 'MAPPEDVEHICLETYPE' || upperKey === 'MAPPED_VEHICLE_TYPE') {
                    return row[key];
                  }
                }
                return null;
              }).filter(t => t !== null);
              logger.debug(`[Fleet List] Sample MAPPED VEHICLE TYPE values (first 10 rows):`, sampleTypes);
            } else {
              logger.error(`[Fleet List] ❌ MAPPED VEHICLE TYPE column not found! Available columns:`, allKeysArray);
            }
          }
          
          setFleetListCSV(parsed);
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error(`Error loading fleet list CSV (serviceType: ${serviceType}, useCase: ${useCase}):`, err);
          setFleetListCSV([]);
        });
    });
    
    return () => {
      isCancelled = true;
    };
  }, [serviceType, useCase]); // Only depend on serviceType and useCase

  // Fetch optifleet vehicle list to map vin to customerVehicleName
  useEffect(() => {
    let isCancelled = false;
    
    import('../config/api').then(({ API_ENDPOINTS }) => {
      authenticatedFetch(API_ENDPOINTS.optifleetVehicleList(serviceType))
        .then(res => {
          if (isCancelled) return null;
          return res.json().catch(() => ({}));
        })
        .then((json: { ok?: boolean; rows?: Array<{ vin?: string; customerVehicleName?: string }> } | null) => {
          if (isCancelled || !json) return;
          if (json.ok && Array.isArray(json.rows)) {
            const mapping = new Map<string, string>();
            json.rows.forEach((row) => {
              // Use exact "vin" column name from CSV
              const vin = row.vin || '';
              const customerName = row.customerVehicleName || '';
              if (vin && customerName) {
                // Normalize vin (remove spaces, uppercase) for matching with Vehicle_ID
                const normalizedVin = vin.trim().replace(/\s+/g, '').toUpperCase();
                mapping.set(normalizedVin, customerName.trim());
                // Also store original case vin for exact match
                mapping.set(vin.trim(), customerName.trim());
                // Store lowercase version too for case-insensitive matching
                mapping.set(vin.trim().toLowerCase(), customerName.trim());
              }
            });
            console.log(`[FleetReport] Loaded ${mapping.size} vin to customerVehicleName mappings`);
            setVinToCustomerNameMap(mapping);
          }
        })
        .catch(err => {
          if (isCancelled) return;
          // Silently fail - Optifleet list is optional
          console.warn('Failed to fetch Optifleet vehicle list:', err);
        });
    });
    
    return () => {
      isCancelled = true;
    };
  }, [serviceType]);

  // Load utilisation targets — truth config when enabled, else legacy util_config.json
  useEffect(() => {
    if (!serviceType) return;
    // Truth path loads config (targets + weights) in the dedicated effect above.
    if (variant === 'utilisation-only' && useTruthUtil && !isDemoMode) return;
    let isCancelled = false;

    import('../config/api').then(({ API_ENDPOINTS }) => {
      authenticatedFetch(API_ENDPOINTS.utilConfig(serviceType), { cache: 'no-store' })
        .then(res => res.ok ? res.json() : null)
        .then((data: { utilisation_analysis?: { target_driving_hours?: number | Record<string, number>; target_waiting_hours?: number | Record<string, number>; mileage_target_miles?: Record<string, number>; revenue_target_gbp?: Record<string, number>; load_capacity_kg?: Record<string, number> } } | null) => {
          if (isCancelled || !data?.utilisation_analysis) return;
          setUtilisationTargetsConfig(data.utilisation_analysis);
        })
        .catch(() => {});
    });
    return () => { isCancelled = true; };
  }, [serviceType, variant, useTruthUtil, isDemoMode, utilisationRefreshToken]);

  /** Map Vehicle ID to display name (customerVehicleName) for Interactive Service Area Map – same as elsewhere in this tab */
  const getVehicleDisplayName = useCallback((vehicleId: string): string => {
    const id = (vehicleId ?? '').trim();
    const normalizedVin = id.replace(/\s+/g, '').toUpperCase();
    const normalizedVinLower = id.replace(/\s+/g, '').toLowerCase();
    return vinToCustomerNameMap.get(normalizedVin)
      ?? vinToCustomerNameMap.get(normalizedVinLower)
      ?? vinToCustomerNameMap.get(id)
      ?? vehicleId;
  }, [vinToCustomerNameMap]);

  // Load time-series data and vehicle level CSV
  useEffect(() => {
    // Reset all state when switching modes or periods
    setTimeSeriesData(null);
    setVehicleLevelCSV([]);
    setVehicleTypeLevelCSV([]);
    setStationLevelCSV([]);
    setBusyStationsCSV([]);
    setData(null);
    setTripPatternData(null);
    setLoading(true);
    setTripPatternLoading(true);
    setSelectedVehicleType(null);
    setSelectedVehicleTypeFilter(null);
    setSelectedVehicleTypeForTrips(null); // Reset vehicle type filter for trips when period changes
    setSelectedStationForTrips(null);
    setSelectedTemporalVehicle('all');
    
    if (!selectedPeriod) {
      // Truth Fleet Report can still load utilisation for the selected month without batch export periods.
      if (!(useTruthUtil && !isDemoMode && selectedMonthForCharts)) {
        setLoading(false);
        setTripPatternLoading(false);
        return;
      }
    }
    
    const period = (selectedPeriod as string) || '';
    let isCancelled = false; // Flag to prevent state updates if component unmounts or mode changes
    
    import('../config/api').then(({ API_ENDPOINTS }) => {
      // When a month is selected in the button bar, always fetch all exported periods that overlap that month,
      // even if the Lookup "Period type" is set to Day/Week.
      const month = selectedMonthForCharts ?? null;
      const periodsToFetch =
        month && periods.length > 0
          ? periods.filter((p) => periodContainsMonth(p, month))
          : period
            ? [period]
            : [];
      const uniquePeriodsToFetch = Array.from(new Set(periodsToFetch)).filter(Boolean);
      if (uniquePeriodsToFetch.length === 0 && period) uniquePeriodsToFetch.push(period);

      const mergeByKey = <T extends Record<string, any>>(a: T | null, b: T | null): T | null => {
        if (!a && !b) return null;
        if (!a) return b;
        if (!b) return a;
        return { ...(a as any), ...(b as any) };
      };

      const dedupeAndSortBy = <T,>(arr: T[], keyOf: (t: T) => string): T[] => {
        const map = new Map<string, T>();
        for (const item of arr) {
          const k = keyOf(item);
          if (!k) continue;
          if (!map.has(k)) map.set(k, item);
        }
        return Array.from(map.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([, v]) => v);
      };

      const mergeTimeSeries = (list: TimeSeriesData[]): TimeSeriesData | null => {
        if (!list.length) return null;
        const out: TimeSeriesData = {
          trips: { daily: [], weekly: [], monthly: [] },
          utilization: { daily: [], weekly: [], monthly: [] },
          mileage: { daily: [], weekly: [], monthly: [] },
          metadata: list.find((x) => x?.metadata)?.metadata,
        };
        for (const d of list) {
          out.trips.daily.push(...(d?.trips?.daily ?? []));
          out.trips.weekly.push(...(d?.trips?.weekly ?? []));
          out.trips.monthly.push(...(d?.trips?.monthly ?? []));
          out.utilization.daily.push(...(d?.utilization?.daily ?? []));
          out.utilization.weekly.push(...(d?.utilization?.weekly ?? []));
          out.utilization.monthly.push(...(d?.utilization?.monthly ?? []));
          out.mileage.daily.push(...(d?.mileage?.daily ?? []));
          out.mileage.weekly.push(...(d?.mileage?.weekly ?? []));
          out.mileage.monthly.push(...(d?.mileage?.monthly ?? []));
        }
        out.trips.daily = dedupeAndSortBy(out.trips.daily, (x: any) => x.date);
        out.utilization.daily = dedupeAndSortBy(out.utilization.daily, (x: any) => x.date);
        out.mileage.daily = dedupeAndSortBy(out.mileage.daily, (x: any) => x.date);
        out.trips.weekly = dedupeAndSortBy(out.trips.weekly, (x: any) => x.week_start);
        out.utilization.weekly = dedupeAndSortBy(out.utilization.weekly, (x: any) => x.week_start);
        out.mileage.weekly = dedupeAndSortBy(out.mileage.weekly, (x: any) => x.week_start);
        out.trips.monthly = dedupeAndSortBy(out.trips.monthly, (x: any) => x.month);
        out.utilization.monthly = dedupeAndSortBy(out.utilization.monthly, (x: any) => x.month);
        out.mileage.monthly = dedupeAndSortBy(out.mileage.monthly, (x: any) => x.month);
        return out;
      };

      const fetchJson = async (url: string) => {
        const res = await authenticatedFetch(url, { cache: 'no-store', headers: { 'Cache-Control': 'no-cache' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.json();
      };
      const fetchText = async (url: string) => {
        const res = await authenticatedFetch(url, { cache: 'no-store', headers: { 'Cache-Control': 'no-cache' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.text();
      };

      (async () => {
        try {
          const tsList: TimeSeriesData[] = [];
          const vRows: CSVRow[] = [];
          const vtRows: CSVRow[] = [];
          let utilJson: any = null;
          let tripJson: TripPatternData | null = null;

          const truthRange = (() => {
            if (!useTruthUtil || isDemoMode) return null;
            if (variant === 'utilisation-only') {
              return {
                startDate: utilTruthDateRange.startDate,
                endDate: utilTruthDateRange.endDate,
              };
            }
            // Full Fleet Report: load truth vehicle-days for the selected month (KPI + shared tables)
            if (selectedMonthForCharts) {
              const y = selectedMonthForCharts.year;
              const m = selectedMonthForCharts.month;
              const startDate = `${y}-${String(m).padStart(2, '0')}-01`;
              const lastDay = new Date(y, m, 0).getDate();
              const endDate = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
              return { startDate, endDate };
            }
            return null;
          })();

          let usedTruthUtil = false;
          if (truthRange) {
            try {
              const truthRows = await fetchTruthUtilisationVehicleDays(
                truthRange.startDate,
                truthRange.endDate,
              );
              usedTruthUtil = true;
              vRows.push(...truthRows.map(truthUtilisationRowToCsvRow));
            } catch (e: unknown) {
              const msg = e instanceof Error ? e.message : String(e);
              logger.warn(`Truth utilisation vehicle-days not available, falling back to CSV: ${msg}`);
            }
          }

          // Truth utilisation-only: vehicle-days already fill KPIs/tables — skip legacy CSV waterfall.
          const skipLegacyExports = variant === 'utilisation-only' && usedTruthUtil;

          if (!skipLegacyExports) {
            const periodResults = await Promise.all(
              uniquePeriodsToFetch.map(async (p) => {
                const [tsRes, vehicleCsvRes, typeCsvRes, opsRes, tripRes] = await Promise.allSettled([
                  fetchJson(API_ENDPOINTS.dashboardTimeSeries(p, serviceType)),
                  usedTruthUtil || vRows.length > 0
                    ? Promise.resolve(null as string | null)
                    : fetchText(API_ENDPOINTS.vehicleLevelCSV(p, serviceType)),
                  fetchText(API_ENDPOINTS.vehicleTypeLevelCSV(p, serviceType)),
                  fetchJson(API_ENDPOINTS.vehicleLevelOperations(p, serviceType)),
                  fetchJson(API_ENDPOINTS.vehicleTripPatterns(p, serviceType)),
                ]);
                return { p, tsRes, vehicleCsvRes, typeCsvRes, opsRes, tripRes };
              }),
            );

            for (const { p, tsRes, vehicleCsvRes, typeCsvRes, opsRes, tripRes } of periodResults) {
              if (tsRes.status === 'fulfilled') {
                tsList.push(tsRes.value as TimeSeriesData);
              } else {
                logger.warn(`Time-series not found for period ${p}: ${tsRes.reason?.message || tsRes.reason}`);
              }

              if (!usedTruthUtil && vehicleCsvRes.status === 'fulfilled' && vehicleCsvRes.value) {
                vRows.push(...parseCSV(vehicleCsvRes.value));
              } else if (!usedTruthUtil && vehicleCsvRes.status === 'rejected') {
                logger.warn(`Vehicle-level CSV not found for period ${p}: ${vehicleCsvRes.reason?.message || vehicleCsvRes.reason}`);
              }

              if (typeCsvRes.status === 'fulfilled') {
                vtRows.push(...parseCSV(typeCsvRes.value));
              } else {
                logger.warn(`Vehicle-type CSV not found for period ${p}: ${typeCsvRes.reason?.message || typeCsvRes.reason}`);
              }

              if (opsRes.status === 'fulfilled') {
                utilJson = mergeByKey(utilJson, opsRes.value);
              } else {
                logger.warn(`Utilization patterns not found for period ${p}: ${opsRes.reason?.message || opsRes.reason}`);
              }

              if (tripRes.status === 'fulfilled') {
                const j = tripRes.value as TripPatternData;
                tripJson =
                  tripJson && typeof tripJson === 'object'
                    ? (Object.assign({}, tripJson as any, j as any) as any)
                    : j;
              } else {
                logger.warn(`Trip patterns not found for period ${p}: ${tripRes.reason?.message || tripRes.reason}`);
              }
            }
          }

          if (isCancelled) return;
          const mergedTs = mergeTimeSeries(tsList);
          setTimeSeriesData(mergedTs);
          setVehicleLevelCSV(vRows);
          setVehicleTypeLevelCSV(vtRows);
          setData(utilJson && utilJson.utilization_patterns ? utilJson : utilJson ? { ...utilJson, utilization_patterns: utilJson.utilization_patterns ?? {} } : null);
          setTripPatternData(tripJson);
          setTripPatternLoading(false);
          setLoading(false);
        } catch (err) {
          if (isCancelled) return;
          logger.error('Error loading utilisation data:', err);
          setTimeSeriesData(null);
          setVehicleLevelCSV([]);
          setVehicleTypeLevelCSV([]);
          setData(null);
          setTripPatternData(null);
          setTripPatternLoading(false);
          setLoading(false);
        }
      })();

      // Load station-level and busy-station CSVs for ES mode
      if (serviceType === 'emergency_services' && period) {
        authenticatedFetch(API_ENDPOINTS.stationLevelCSV(period, serviceType))
          .then(res => {
            if (isCancelled) return null;
            if (!res.ok) {
              return null;
            }
            return res.text();
          })
          .then((text: string | null | undefined) => {
            if (isCancelled) return;
            if (text) {
              setStationLevelCSV(parseCSV(text));
            } else {
              setStationLevelCSV([]);
            }
          })
          .catch(err => {
            if (isCancelled) return;
            logger.debug('Station level CSV not available:', err.message);
            setStationLevelCSV([]);
          });

        authenticatedFetch(API_ENDPOINTS.busyStationsCSV(period, serviceType))
          .then(res => {
            if (isCancelled) return null;
            if (!res.ok) {
              return null;
            }
            return res.text();
          })
          .then((text: string | null | undefined) => {
            if (isCancelled) return;
            if (text) {
              setBusyStationsCSV(parseMultiIndexCSV(text));
            } else {
              setBusyStationsCSV([]);
            }
          })
          .catch(err => {
            if (isCancelled) return;
            logger.warn('Busy stations CSV not available:', err.message);
            setBusyStationsCSV([]);
          });
      } else {
        setStationLevelCSV([]);
        setBusyStationsCSV([]);
      }

    });
    
    // Cleanup function to cancel in-flight requests when mode/period changes
    return () => {
      isCancelled = true;
    };
  }, [selectedPeriod, selectedMonthForCharts, periods, serviceType, isDemoMode, variant, useTruthUtil, utilisationRefreshToken, utilTruthDateRange.startDate, utilTruthDateRange.endDate]);

  // Get summary statistics for vehicle-type-level
  const getVehicleTypeSummaryStats = (characteristic: 'duration' | 'distance' | 'speed') => {
    if (!tripPatternData) return [];
    
    const key = characteristic === 'duration' ? 'Duration_Label' : characteristic === 'distance' ? 'Distance_Label' : 'Speed_Label';
    const dataSection = tripPatternData[key as keyof TripPatternData];
    
    if (!dataSection || typeof dataSection !== 'object') return [];
    
    const formatValue = (value: number, char: 'duration' | 'distance' | 'speed'): string => {
      if (char === 'duration') {
        return `${value.toFixed(1)} min`;
      } else if (char === 'distance') {
        return `${value.toFixed(1)} mi`;
      } else {
        return `${value.toFixed(1)} mph`;
      }
    };
    
    // Create maps for vehicle ID to type and days from CSV
    const vehicleIdToTypeMap = new Map<string, string>();
    const vehicleIdToDaysMap = new Map<string, number>();
    vehicleLevelCSV.forEach(row => {
      if (row.Vehicle_ID) {
        const type = row.Vehicle_Type || row.vehicle_type || '';
        vehicleIdToTypeMap.set(row.Vehicle_ID, type);
        const days = parseFloat(
          row.total_days || 
          row.Total_Days || 
          row.days || 
          row.Days || 
          '0'
        );
        vehicleIdToDaysMap.set(row.Vehicle_ID, days);
      }
    });
    
    // Normalize: support both legacy (single period) and by-period (2026-01, 2026-02) shapes
    const sectionsByPeriod = getTripPatternSectionsByPeriod(dataSection);
    const filteredSections = chartMonth
      ? (() => {
          const targetYm = `${chartMonth.year}-${String(chartMonth.month).padStart(2, '0')}`;
          const matched = sectionsByPeriod.filter(({ period }) => {
            const sectionYm = getTripPatternPeriodYearMonth(period);
            if (sectionYm) return sectionYm === targetYm;
            return (period || '').toString().slice(0, 7) === targetYm; // fallback for unexpected formats
          });
          // If filtering results in an empty table for demo exports (e.g. single "Jan 2026" label),
          // show the full dataset instead of an empty state.
          return matched.length > 0 ? matched : sectionsByPeriod;
        })()
      : sectionsByPeriod;
    
    // Create a map to calculate average days per vehicle type (from first section's vehicle_level)
    const vehicleTypeDaysMap = new Map<string, number>();
    const firstSection = filteredSections[0]?.section;
    if (firstSection?.vehicle_level && vehicleLevelCSV.length > 0) {
      for (const vehicleType of Object.keys(firstSection)) {
        if (vehicleType === 'vehicle_level') continue;
        const daysForType: number[] = [];
        for (const vehicleId of Object.keys(firstSection.vehicle_level)) {
          const csvType = vehicleIdToTypeMap.get(vehicleId) || '';
          if (csvType.toLowerCase() === vehicleType.toLowerCase()) {
            const days = vehicleIdToDaysMap.get(vehicleId) || 0;
            if (days > 0) daysForType.push(days);
            }
          }
        if (daysForType.length > 0) {
          const avgDays = daysForType.reduce((sum, d) => sum + d, 0) / daysForType.length;
          vehicleTypeDaysMap.set(vehicleType.toLowerCase(), Math.round(avgDays));
        }
      }
    }

    // Fallback: when CSV has no days, derive days per type from distinct dates in selected month (kpiDateWindow)
    const vehicleTypeDaysFromDistinctDates = new Map<string, number>();
    if (kpiDateWindow && vehicleLevelCSV.length > 0) {
      const byType = new Map<string, Set<string>>();
      vehicleLevelCSV.forEach(row => {
        const iso = utilisationCsvRowIsoDate(row);
        if (!iso || iso < kpiDateWindow.startDate || iso > kpiDateWindow.endDate) return;
        const t = getVehicleTypeFromRow(row);
        if (!t) return;
        const key = t.toLowerCase();
        if (!byType.has(key)) byType.set(key, new Set());
        byType.get(key)!.add(iso);
      });
      byType.forEach((set, key) => vehicleTypeDaysFromDistinctDates.set(key, set.size));
    }

    const result: Array<Record<string, any>> = [];
    
    for (const { period, section } of filteredSections) {
      if (!section || typeof section !== 'object') continue;
      for (const vehicleType of Object.keys(section)) {
      if (vehicleType === 'vehicle_level') continue;
        const vehicleData = (section as any)[vehicleType];
      if (vehicleData && typeof vehicleData === 'object' && 'summary_stats' in vehicleData) {
        const stats = vehicleData.summary_stats;
        if (stats && typeof stats === 'object' && 'SampleSize' in stats) {
      const isSelectedType = selectedVehicleTypeFilter && 
        vehicleType.toLowerCase() === selectedVehicleTypeFilter.toLowerCase();
      const avgDaysRaw = vehicleTypeDaysMap.get(vehicleType.toLowerCase()) ||
        vehicleTypeDaysFromDistinctDates.get(vehicleType.toLowerCase()) || 0;
      // Never show 0 days when this type has trips (avoids 0 days / non-zero trips)
      const avgDays = avgDaysRaw > 0 ? avgDaysRaw : (stats.SampleSize > 0 ? 1 : 0);
      result.push({
            vehicle_type: (
              <span className={isSelectedType ? 'text-warning drop-shadow-glow-warning-lg' : ''}>
                {redactVehicleType(vehicleType, isDemoMode)}
              </span>
            ),
              _vehicle_type_sortKey: vehicleType,
              _isHighlighted: isSelectedType,
        total_days: avgDays.toString(),
        sample_size: Math.round(stats.SampleSize).toString(),
        mean: formatValue(stats.Mean, characteristic),
        median: formatValue(stats.Q2, characteristic),
        typical_range: `${formatValue(stats.Q1, characteristic)} - ${formatValue(stats.Q3, characteristic)}`,
        min: formatValue(stats.Min, characteristic),
        max: formatValue(stats.Max, characteristic),
        _lower_quartile: formatValue(stats.Q1, characteristic),
        _upper_quartile: formatValue(stats.Q3, characteristic),
              ...(filteredSections.length > 1 ? { period } : {}),
      });
          }
    }
      }
    }

    // Fallback 1: some demo exports only provide `vehicle_level` stats and no top-level type buckets.
    // Aggregate vehicle-level stats into vehicle types using the CSV vehicleId -> type mapping.
    if (result.length === 0 && firstSection?.vehicle_level && typeof firstSection.vehicle_level === 'object') {
      const aggregated = new Map<
        string,
        {
          displayType: string;
          weightSum: number;
          sampleSizeSum: number;
          meanSum: number;
          q1Sum: number;
          q2Sum: number;
          q3Sum: number;
          min: number;
          max: number;
        }
      >();

      for (const { section } of filteredSections) {
        const vehicleLevel = (section as any)?.vehicle_level;
        if (!vehicleLevel || typeof vehicleLevel !== 'object') continue;

        for (const [vehicleId, vehicleData] of Object.entries(vehicleLevel as Record<string, any>)) {
          const stats = (vehicleData as any)?.summary_stats;
          if (!stats || typeof stats !== 'object' || !('SampleSize' in stats)) continue;

          const csvType = vehicleIdToTypeMap.get(vehicleId) || '';
          const typeKey = csvType.toLowerCase().trim();
          if (!typeKey) continue;

          const weight = Number((stats as any).SampleSize) > 0 ? Number((stats as any).SampleSize) : 1;
          const cur =
            aggregated.get(typeKey) || {
              displayType: csvType,
              weightSum: 0,
              sampleSizeSum: 0,
              meanSum: 0,
              q1Sum: 0,
              q2Sum: 0,
              q3Sum: 0,
              min: Number.POSITIVE_INFINITY,
              max: Number.NEGATIVE_INFINITY,
            };

          const mean = Number((stats as any).Mean) || 0;
          const q1 = Number((stats as any).Q1) || 0;
          const q2 = Number((stats as any).Q2) || 0;
          const q3 = Number((stats as any).Q3) || 0;
          const min = Number((stats as any).Min) || 0;
          const max = Number((stats as any).Max) || 0;

          cur.weightSum += weight;
          cur.sampleSizeSum += Number((stats as any).SampleSize) || 0;
          cur.meanSum += mean * weight;
          cur.q1Sum += q1 * weight;
          cur.q2Sum += q2 * weight;
          cur.q3Sum += q3 * weight;
          cur.min = Math.min(cur.min, min);
          cur.max = Math.max(cur.max, max);
          aggregated.set(typeKey, cur);
        }
      }

      for (const [typeKey, agg] of aggregated.entries()) {
        const isSelectedType =
          selectedVehicleTypeFilter &&
          agg.displayType.toLowerCase() === selectedVehicleTypeFilter.toLowerCase();
        const avgDaysRaw =
          vehicleTypeDaysMap.get(typeKey) ||
          vehicleTypeDaysFromDistinctDates.get(typeKey) ||
          0;
        const avgDays = avgDaysRaw > 0 ? avgDaysRaw : (agg.sampleSizeSum > 0 ? 1 : 0);
        const mean = agg.weightSum > 0 ? agg.meanSum / agg.weightSum : 0;
        const q1 = agg.weightSum > 0 ? agg.q1Sum / agg.weightSum : 0;
        const q2 = agg.weightSum > 0 ? agg.q2Sum / agg.weightSum : 0;
        const q3 = agg.weightSum > 0 ? agg.q3Sum / agg.weightSum : 0;

        result.push({
          vehicle_type: (
            <span className={isSelectedType ? 'text-warning drop-shadow-glow-warning-lg' : ''}>
              {redactVehicleType(agg.displayType, isDemoMode)}
            </span>
          ),
          _vehicle_type_sortKey: agg.displayType,
          _isHighlighted: isSelectedType,
          total_days: avgDays.toString(),
          sample_size: Math.round(agg.sampleSizeSum).toString(),
          mean: formatValue(mean, characteristic),
          median: formatValue(q2, characteristic),
          typical_range: `${formatValue(q1, characteristic)} - ${formatValue(q3, characteristic)}`,
          min: formatValue(agg.min, characteristic),
          max: formatValue(agg.max, characteristic),
          _lower_quartile: formatValue(q1, characteristic),
          _upper_quartile: formatValue(q3, characteristic),
        });
      }
    }

    // Fallback 2: final guard for demo mode so table is never empty.
    if (result.length === 0 && isDemoMode) {
      const csvTypes = Array.from(vehicleIdToTypeMap.values())
        .map((t) => (t || '').trim())
        .filter(Boolean);
      const uniqueTypes = Array.from(new Set(csvTypes));
      const fallbackTypes =
        uniqueTypes.length > 0
          ? uniqueTypes.slice(0, 6)
          : ['Response car', 'Small van', 'Large van', 'Pumping appliance', 'Other appliance'];

      for (let i = 0; i < fallbackTypes.length; i++) {
        const vehicleType = fallbackTypes[i]!;
        const isSelectedType =
          selectedVehicleTypeFilter &&
          vehicleType.toLowerCase() === selectedVehicleTypeFilter.toLowerCase();
        const sampleSize = 12 + ((i * 11) % 42);
        const avgDays = 4 + ((i * 5) % 23);
        const base =
          characteristic === 'duration' ? 38 : characteristic === 'distance' ? 560 : 34;
        const spread = characteristic === 'duration' ? 16 : characteristic === 'distance' ? 240 : 11;
        const meanVal = base + ((i * 13) % spread);
        const q1Val = meanVal - spread * 0.25;
        const q2Val = meanVal;
        const q3Val = meanVal + spread * 0.35;
        const minVal = q1Val - spread * 0.15;
        const maxVal = q3Val + spread * 0.2;

        result.push({
          vehicle_type: (
            <span className={isSelectedType ? 'text-warning drop-shadow-glow-warning-lg' : ''}>
              {redactVehicleType(vehicleType, true)}
            </span>
          ),
          _vehicle_type_sortKey: vehicleType,
          _isHighlighted: isSelectedType,
          total_days: avgDays.toString(),
          sample_size: sampleSize.toString(),
          mean: formatValue(meanVal, characteristic),
          median: formatValue(q2Val, characteristic),
          typical_range: `${formatValue(q1Val, characteristic)} - ${formatValue(q3Val, characteristic)}`,
          min: formatValue(minVal, characteristic),
          max: formatValue(maxVal, characteristic),
          _lower_quartile: formatValue(q1Val, characteristic),
          _upper_quartile: formatValue(q3Val, characteristic),
        });
      }
    }
    
    // ✅ Apply custom ordering for vehicle types (same as dropdown)
    // Use case-insensitive matching to handle variations like "Response Car" vs "Response car"
    const customOrder = ['Response car', 'Small van', 'Large van', 'Pumping appliance', 'Other appliance'];
    
    return result.sort((a, b) => {
      // Case-insensitive comparison - use sort key for comparison
      const findIndex = (vehicleType: string) => {
        const lowerType = vehicleType.toLowerCase();
        return customOrder.findIndex(order => order.toLowerCase() === lowerType);
      };
      
      const vehicleTypeA = (a as any)._vehicle_type_sortKey || '';
      const vehicleTypeB = (b as any)._vehicle_type_sortKey || '';
      
      const indexA = findIndex(vehicleTypeA);
      const indexB = findIndex(vehicleTypeB);
      
      // If both are in custom order, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only A is in custom order, A comes first
      if (indexA !== -1) return -1;
      // If only B is in custom order, B comes first
      if (indexB !== -1) return 1;
      // If neither is in custom order, sort alphabetically
      return vehicleTypeA.localeCompare(vehicleTypeB);
    });
  };

  // Get unique vehicle types from CSV with custom ordering
  const getAvailableVehicleTypes = (): string[] => {
    const types = new Set<string>();
    vehicleLevelCSV.forEach(row => {
      const type = row.Vehicle_Type || row.vehicle_type || '';
      if (type) {
        types.add(type);
      }
    });
    
    // Custom order for vehicle types
    const customOrder = ['Response car', 'Small van', 'Large van', 'Pumping appliance', 'Other appliance'];
    const typeArray = Array.from(types);
    
    // Sort: first by custom order, then alphabetically for any types not in the custom order
    // Use case-insensitive matching to handle variations like "Response Car" vs "Response car"
    return typeArray.sort((a, b) => {
      const findIndex = (vehicleType: string) => {
        const lowerType = vehicleType.toLowerCase();
        return customOrder.findIndex(order => order.toLowerCase() === lowerType);
      };
      
      const indexA = findIndex(a);
      const indexB = findIndex(b);
      
      // If both are in custom order, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only A is in custom order, A comes first
      if (indexA !== -1) return -1;
      // If only B is in custom order, B comes first
      if (indexB !== -1) return 1;
      // If neither is in custom order, sort alphabetically
      return a.localeCompare(b);
    });
  };

  // Get summary statistics for station-level (Emergency Services only)
  const getStationSummaryStats = (characteristic: 'duration' | 'distance' | 'speed') => {
    if (!tripPatternData || serviceType !== 'emergency_services') return [];
    
    const key = characteristic === 'duration' ? 'Duration_Label' : characteristic === 'distance' ? 'Distance_Label' : 'Speed_Label';
    const dataSection = tripPatternData[key as keyof TripPatternData];
    
    const sectionsByPeriod = getTripPatternSectionsByPeriod(dataSection);
    const firstSection = sectionsByPeriod[0]?.section;
    const hasStationLevel = sectionsByPeriod.some(({ section }) => section?.station_level);
    if (!hasStationLevel) return [];
    
    const formatValue = (value: number, char: 'duration' | 'distance' | 'speed'): string => {
      if (char === 'duration') {
        return `${value.toFixed(1)} min`;
      } else if (char === 'distance') {
        return `${value.toFixed(1)} mi`;
      } else {
        return `${value.toFixed(1)} mph`;
      }
    };
    
    // Create a map of station to days
    // Try multiple approaches to get station days:
    // 1. Check if vehicle-level CSV has Station column directly
    // 2. Map vehicles to stations from fleet list CSV
    // 3. Calculate from trip pattern data (count unique vehicles per station)
    const stationDaysMap = new Map<string, number>();
    
    // Approach 1: Check if vehicle-level CSV has Station column directly
    let hasStationColumn = false;
    let stationColumnName = '';
    if (vehicleLevelCSV.length > 0) {
      const firstRow = vehicleLevelCSV[0];
      for (const key in firstRow) {
        const upperKey = key.toUpperCase().trim();
        if (upperKey === 'STATION_NAME' || upperKey === 'STATIONNAME' || 
            upperKey === 'STATION' || upperKey === 'STN #' || upperKey === 'STN') {
          hasStationColumn = true;
          stationColumnName = key; // Keep original case for lookup
          break;
        }
      }
      
      if (hasStationColumn) {
        // Direct approach: group by station from vehicle-level CSV
        const stationDaysList = new Map<string, number[]>();
        vehicleLevelCSV.forEach(row => {
          // Find station column (use the column name we found)
          let station = '';
          if (stationColumnName && row[stationColumnName]) {
            station = row[stationColumnName];
          } else {
            // Fallback: try all possible station column names
            for (const key in row) {
              const upperKey = key.toUpperCase().trim();
              if (upperKey === 'STATION_NAME' || upperKey === 'STATIONNAME' || 
                  upperKey === 'STATION' || upperKey === 'STN #' || upperKey === 'STN') {
                station = row[key] || '';
                break;
              }
            }
          }
          
          if (station && station.trim() !== '') {
            const days = parseFloat(
              row.total_days || 
              row.Total_Days || 
              row.days || 
              row.Days || 
              '0'
            );
            if (days > 0) {
              const stationTrimmed = station.trim();
              if (!stationDaysList.has(stationTrimmed)) {
                stationDaysList.set(stationTrimmed, []);
              }
              stationDaysList.get(stationTrimmed)!.push(days);
            }
          }
        });
        
        // Calculate average days per station
        stationDaysList.forEach((daysList: number[], station: string) => {
          if (daysList.length > 0) {
            const avgDays = daysList.reduce((sum: number, d: number) => sum + d, 0) / daysList.length;
            stationDaysMap.set(station, Math.round(avgDays));
          }
        });
      } else {
        // Approach 2: Map vehicles to stations from fleet list CSV
        const vehicleToStationMap = new Map<string, string>();
        fleetListCSV.forEach(row => {
          // Get VEHICLEINDEX (try multiple variations)
          let vehicleIndex = '';
          for (const key in row) {
            const upperKey = key.toUpperCase().trim();
            if (upperKey === 'VEHICLEINDEX' || upperKey === 'VEHICLE_INDEX' || upperKey === 'VEHICLEINDEX') {
              vehicleIndex = row[key] || '';
              break;
            }
          }
          
          // Get STATION or STN #
          let station = '';
          for (const key in row) {
            const upperKey = key.toUpperCase().trim();
            if (upperKey === 'STATION' || upperKey === 'STN #' || upperKey === 'STN') {
              station = row[key] || '';
              break;
            }
          }
          
          if (vehicleIndex && station && vehicleIndex.trim() !== '' && station.trim() !== '') {
            vehicleToStationMap.set(vehicleIndex.trim().toUpperCase(), station.trim());
          }
        });
        
        // Calculate average days per station from vehicle-level CSV
        const stationDaysList = new Map<string, number[]>();
        let matchedCount = 0;
        let unmatchedCount = 0;
        
        vehicleLevelCSV.forEach(row => {
          // Try multiple vehicle ID column names (case-insensitive)
          let vehicleId = '';
          for (const key in row) {
            const upperKey = key.toUpperCase().trim();
            if (upperKey === 'VEHICLE_ID' || upperKey === 'VEHICLEID' || upperKey === 'VEHICLE_ID' || upperKey === 'VEHICLEINDEX' || upperKey === 'VEHICLE_INDEX' || upperKey === 'VEHICLE NUMBER' || upperKey === 'VEHICLENUMBER') {
              vehicleId = row[key] || '';
              break;
            }
          }
          
          if (vehicleId) {
            // Try exact match first (case-insensitive)
            let station = vehicleToStationMap.get(vehicleId.trim().toUpperCase());
            
            // If no exact match, try partial match (remove spaces, special chars)
            if (!station) {
              const normalizedVehicleId = vehicleId.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
              for (const [fleetVehicleId, fleetStation] of vehicleToStationMap.entries()) {
                const normalizedFleetId = fleetVehicleId.replace(/[^A-Z0-9]/g, '');
                if (normalizedVehicleId === normalizedFleetId) {
                  station = fleetStation;
                  break;
                }
              }
            }
            
            if (station) {
              matchedCount++;
              const days = parseFloat(
                row.total_days || 
                row.Total_Days || 
                row.days || 
                row.Days || 
                '0'
              );
              if (days > 0) {
                if (!stationDaysList.has(station)) {
                  stationDaysList.set(station, []);
                }
                stationDaysList.get(station)!.push(days);
              }
            } else {
              unmatchedCount++;
            }
          }
        });
        
        // Log matching results for debugging
        if (matchedCount === 0 && unmatchedCount > 0) {
          logger.warn(`[Station Summary] Could not match any vehicles to stations. Matched: ${matchedCount}, Unmatched: ${unmatchedCount}`);
          logger.debug(`[Station Summary] Sample vehicle IDs from vehicleLevelCSV:`, 
            vehicleLevelCSV.slice(0, 5).map(row => {
              for (const key in row) {
                const upperKey = key.toUpperCase().trim();
                if (upperKey === 'VEHICLE_ID' || upperKey === 'VEHICLEID' || upperKey === 'VEHICLEINDEX') {
                  return row[key];
                }
              }
              return 'N/A';
            })
          );
          logger.debug(`[Station Summary] Sample vehicle IDs from fleetListCSV:`, 
            fleetListCSV.slice(0, 5).map(row => {
              for (const key in row) {
                if (key.toUpperCase().trim() === 'VEHICLEINDEX') {
                  return row[key];
                }
              }
              return 'N/A';
            })
          );
        }
        
        // Calculate average days per station
        stationDaysList.forEach((daysList: number[], station: string) => {
          if (daysList.length > 0) {
            const avgDays = daysList.reduce((sum: number, d: number) => sum + d, 0) / daysList.length;
            stationDaysMap.set(station, Math.round(avgDays));
          }
        });
      }
    }
    
    // Approach 3: Calculate days from trip pattern vehicle_level data
    // Match vehicles in vehicle_level to stations by finding which vehicles belong to each station
    // This is more reliable than trying to match vehicle IDs between CSVs
    if (stationDaysMap.size === 0 && firstSection?.vehicle_level && firstSection?.station_level) {
      
      // Try to match vehicles to stations using the trip data structure
      // Since station_level aggregates trips by station, we can't directly map vehicles
      // But we can calculate days per station by:
      // 1. Getting vehicles from vehicleLevelCSV that have days
      // 2. For each station, count how many vehicles from that station have trips
      // 3. Average the days for vehicles that belong to each station
      
      // Alternative: Use the sample size (trips) per station as a proxy
      // But this won't give us actual days...
      
      // Better approach: Calculate days per station by matching vehicles from vehicleLevelCSV
      // to stations using the fleet list, but with better matching
      const vehicleToStationMap = new Map<string, string>();
      
      // Build vehicle-to-station map from fleet list with better matching
      fleetListCSV.forEach(row => {
        let vehicleIndex = '';
        let station = '';
        
        // Get VEHICLEINDEX
        for (const key in row) {
          const upperKey = key.toUpperCase().trim();
          if (upperKey === 'VEHICLEINDEX' || upperKey === 'VEHICLE_INDEX') {
            vehicleIndex = row[key] || '';
            break;
          }
        }
        
        // Get STATION
        for (const key in row) {
          const upperKey = key.toUpperCase().trim();
          if (upperKey === 'STATION' || upperKey === 'STN #' || upperKey === 'STN') {
            station = row[key] || '';
            break;
          }
        }
        
        if (vehicleIndex && station) {
          const normalizedId = vehicleIndex.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
          vehicleToStationMap.set(normalizedId, station.trim());
        }
      });
      
      // Now match vehicles from vehicleLevelCSV to stations
      const stationDaysList = new Map<string, number[]>();
      vehicleLevelCSV.forEach(row => {
        // Try all possible vehicle ID column names
        let vehicleId = '';
        for (const key in row) {
          const upperKey = key.toUpperCase().trim();
          if (upperKey.includes('VEHICLE') && (upperKey.includes('ID') || upperKey.includes('INDEX') || upperKey.includes('NUMBER'))) {
            vehicleId = row[key] || '';
            if (vehicleId) break;
          }
        }
        
        if (vehicleId) {
          const normalizedId = vehicleId.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
          const station = vehicleToStationMap.get(normalizedId);
          
          if (station) {
            const days = parseFloat(
              row.total_days || 
              row.Total_Days || 
              row.days || 
              row.Days || 
              '0'
            );
            if (days > 0) {
              if (!stationDaysList.has(station)) {
                stationDaysList.set(station, []);
              }
              stationDaysList.get(station)!.push(days);
            }
          }
        }
      });
      
      // Calculate average days per station
      stationDaysList.forEach((daysList: number[], station: string) => {
        if (daysList.length > 0) {
          const avgDays = daysList.reduce((sum: number, d: number) => sum + d, 0) / daysList.length;
          stationDaysMap.set(station, Math.round(avgDays));
        }
      });
      
      // If still no matches, use fallback
      if (stationDaysMap.size === 0 && vehicleLevelCSV.length > 0) {
        const allDays: number[] = [];
        vehicleLevelCSV.forEach(row => {
          const days = parseFloat(
            row.total_days || 
            row.Total_Days || 
            row.days || 
            row.Days || 
            '0'
          );
          if (days > 0) {
            allDays.push(days);
          }
        });
        
        if (allDays.length > 0) {
          const overallAvgDays = Math.round(allDays.reduce((sum, d) => sum + d, 0) / allDays.length);
          logger.warn(`[Station Summary] Could not match vehicles to stations after improved matching. Using overall average days (${overallAvgDays}) for all stations.`);
          Object.keys(firstSection.station_level || {}).forEach(station => {
            stationDaysMap.set(station, overallAvgDays);
          });
        }
      }
    }

    // Same idea as Vehicle Type Summary: when CSV has no total_days, count distinct calendar days per station from vehicle-level rows in the KPI window.
    const stationDaysFromDistinctDates = new Map<string, number>();
    const windowForStationDays = kpiDateWindow ?? effectiveDateWindow;
    if (windowForStationDays && vehicleLevelCSV.length > 0) {
      const byStation = new Map<string, Set<string>>();
      vehicleLevelCSV.forEach(row => {
        const iso = utilisationCsvRowIsoDate(row);
        if (!iso || iso < windowForStationDays.startDate || iso > windowForStationDays.endDate) return;
        let stationName = '';
        for (const key in row) {
          const upperKey = key.toUpperCase().trim();
          if (
            upperKey === 'STATION_NAME' ||
            upperKey === 'STATIONNAME' ||
            upperKey === 'STATION' ||
            upperKey === 'STN #' ||
            upperKey === 'STN'
          ) {
            stationName = (row[key] || '').toString().trim();
            break;
          }
        }
        if (!stationName) return;
        const lc = stationName.toLowerCase();
        if (!byStation.has(lc)) byStation.set(lc, new Set());
        byStation.get(lc)!.add(iso);
      });
      byStation.forEach((set, key) => stationDaysFromDistinctDates.set(key, set.size));
    }

    const resolveStationDays = (stationKey: string): number => {
      const lc = stationKey.trim().toLowerCase();
      for (const [k, v] of stationDaysMap) {
        if (k.trim().toLowerCase() === lc) return v;
      }
      return stationDaysFromDistinctDates.get(lc) ?? 0;
    };
    
    const result: Array<Record<string, any>> = [];
    
    for (const { period, section } of sectionsByPeriod) {
      const stationLevel = section?.station_level;
      if (!stationLevel || typeof stationLevel !== 'object') continue;
      for (const [station, stationData] of Object.entries(stationLevel) as [string, VehicleTypeData][]) {
      if (stationData?.summary_stats) {
        const stats = stationData.summary_stats;
        const avgDaysRaw = resolveStationDays(station);
        const avgDays = avgDaysRaw > 0 ? avgDaysRaw : (Math.round(stats.SampleSize) > 0 ? 1 : 0);
      result.push({
          station: redactStationName(station, isDemoMode),
          _station_sortKey: station,
          total_days: avgDays.toString(),
        sample_size: Math.round(stats.SampleSize).toString(),
        mean: formatValue(stats.Mean, characteristic),
        median: formatValue(stats.Q2, characteristic),
          typical_range: `${formatValue(stats.Q1, characteristic)} - ${formatValue(stats.Q3, characteristic)}`,
          min: formatValue(stats.Min, characteristic),
          max: formatValue(stats.Max, characteristic),
          // Keep raw values for export
          _lower_quartile: formatValue(stats.Q1, characteristic),
          _upper_quartile: formatValue(stats.Q3, characteristic),
          ...(sectionsByPeriod.length > 1 ? { period } : {}),
      });
        }
    }
    }
    
    // Sort by station name
    return result.sort((a, b) => a._station_sortKey.localeCompare(b._station_sortKey));
  };

  // Process temporal patterns data - for Vehicle Trips chart
  type BarChartDataItem = { label: string; values: { vehicle: string; value: number; color: string }[] };
  const getTemporalData = (view: 'hourly' | 'daily' | 'monthly'): BarChartDataItem[] => {
    if (!data || !data.utilization_patterns) return [];
    
    const baseColors = CHART_PALETTE;
    let allVehicles = Object.entries(data.utilization_patterns);
    
    if (allVehicles.length === 0) return [];
    
    // Filter by vehicle type if selected
    if (selectedVehicleTypeForTrips && vehicleLevelCSV.length > 0) {
      const vehicleIdsOfType = new Set(
        vehicleLevelCSV
          .filter(row => {
            const rowType = row.Vehicle_Type || row.vehicle_type || '';
            return rowType.toLowerCase() === selectedVehicleTypeForTrips.toLowerCase();
          })
          .map(row => row.Vehicle_ID)
          .filter(Boolean)
      );
      allVehicles = allVehicles.filter(([vehicleId]) => vehicleIdsOfType.has(vehicleId));
    }
    
    // Filter by station if selected
    if (selectedStationForTrips && vehicleLevelCSV.length > 0) {
      const vehicleIdsOfStation = new Set(
        vehicleLevelCSV
          .filter(row => {
            // Check multiple possible station column names
            const rowStation = row.Station_Name || row.Station || row['STN #'] || row.StationName || row.station || '';
            return rowStation.toLowerCase() === selectedStationForTrips.toLowerCase();
          })
          .map(row => row.Vehicle_ID || row.VehicleID || row.vehicle_id || '')
          .filter(Boolean)
      );
      allVehicles = allVehicles.filter(([vehicleId]) => vehicleIdsOfStation.has(vehicleId));
    }
    
    // Determine if we're showing "All Vehicles" (aggregated) or a specific vehicle
    const isAllVehicles = !selectedTemporalVehicle || selectedTemporalVehicle === 'all';
    
    // Get vehicles to display
    let vehicles = allVehicles;
    if (!isAllVehicles) {
      vehicles = allVehicles.filter(([vehicleId]) => vehicleId === selectedTemporalVehicle);
    }
    
    if (vehicles.length === 0) return [];
    
    const colorMap = allVehicles.reduce<Record<string, string>>((acc, [vehicleId], idx) => {
      acc[vehicleId] = baseColors[idx % baseColors.length];
      return acc;
    }, {});
    
    const getColor = (vehicleId: string, fallbackIdx: number) => {
      return colorMap[vehicleId] || baseColors[fallbackIdx % baseColors.length];
    };
    
    // If showing "All Vehicles", aggregate the data
    if (isAllVehicles && vehicles.length > 1) {
      if (view === 'hourly') {
        const aggregatedHours = new Map<string, number>();
        vehicles.forEach(([, vehicleData]: [string, any]) => {
          const temporal = aggregateTemporalPatterns(vehicleData);
          Object.entries(temporal.hourly_distribution).forEach(([hour, trips]: [string, any]) => {
            aggregatedHours.set(hour, (aggregatedHours.get(hour) || 0) + (trips || 0));
          });
        });
        
        const sortedHours = Array.from(aggregatedHours.keys()).sort((a, b) => parseInt(a) - parseInt(b));
        return sortedHours.map(hour => ({
          label: hour,
          values: [{
            vehicle: 'All Vehicles',
            value: aggregatedHours.get(hour) || 0,
            color: baseColors[0],
          }],
        }));
      } else if (view === 'daily') {
        const days = ['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'];
        return days.map(day => {
          const totalTrips = vehicles.reduce((sum, [, vehicleData]: [string, any]) => {
            const temporal = aggregateTemporalPatterns(vehicleData);
            return sum + (temporal.daily_distribution[day] || 0);
          }, 0);
          return {
            label: day,
            values: [{
              vehicle: 'All Vehicles',
              value: totalTrips,
              color: baseColors[0],
            }],
          };
        });
      } else {
        // Monthly - collect all months from all vehicles
        const allMonths = new Set<string>();
        vehicles.forEach(([, vehicleData]: [string, any]) => {
          const temporal = aggregateTemporalPatterns(vehicleData);
          Object.keys(temporal.monthly_distribution).forEach(month => {
            allMonths.add(month);
          });
        });
        
        const sortedMonths = Array.from(allMonths).sort();
        return sortedMonths.map(month => {
          const totalTrips = vehicles.reduce((sum, [, vehicleData]: [string, any]) => {
            const temporal = aggregateTemporalPatterns(vehicleData);
            return sum + (temporal.monthly_distribution[month] || 0);
          }, 0);
          return {
            label: month,
            values: [{
              vehicle: 'All Vehicles',
              value: totalTrips,
              color: baseColors[0],
            }],
          };
        });
      }
    }
    
    // Individual vehicle mode (existing logic)
    if (view === 'hourly') {
      const hoursWithData = new Set<string>();
      vehicles.forEach(([, vehicleData]: [string, any]) => {
        const temporal = aggregateTemporalPatterns(vehicleData);
        Object.keys(temporal.hourly_distribution).forEach(hour => hoursWithData.add(hour));
      });
      
      const sortedHours = Array.from(hoursWithData).sort((a: string, b: string) => parseInt(a, 10) - parseInt(b, 10));
      
      return sortedHours.map(hour => ({
        label: hour,
        values: vehicles.map(([vehicleId, vehicleData]: [string, any], idx) => {
          const temporal = aggregateTemporalPatterns(vehicleData);
          return {
          vehicle: vehicleId,
            value: temporal.hourly_distribution[hour] || 0,
          color: getColor(vehicleId, idx),
          };
        }),
      }));
    } else if (view === 'daily') {
      const days = ['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'];
      return days.map(day => ({
        label: day,
        values: vehicles.map(([vehicleId, vehicleData]: [string, any], idx) => {
          const temporal = aggregateTemporalPatterns(vehicleData);
          return {
          vehicle: vehicleId,
            value: temporal.daily_distribution[day] || 0,
          color: getColor(vehicleId, idx),
          };
        }),
      }));
    } else {
      const monthsSet = new Set<string>();
      vehicles.forEach(([, vehicleData]: [string, any]) => {
        const temporal = aggregateTemporalPatterns(vehicleData);
        Object.keys(temporal.monthly_distribution).forEach(m => monthsSet.add(m));
      });
      const months = Array.from(monthsSet).sort();
      return months.map(month => ({
        label: month,
        values: vehicles.map(([vehicleId, vehicleData]: [string, any], idx) => {
          const temporal = aggregateTemporalPatterns(vehicleData);
          return {
          vehicle: vehicleId,
            value: temporal.monthly_distribution[month] || 0,
          color: getColor(vehicleId, idx),
          };
        }),
      }));
    }
  };

  // Get summary statistics for vehicle-level
  const getVehicleLevelSummaryStats = (characteristic: 'duration' | 'distance' | 'speed') => {
    if (!tripPatternData) return [];
    
    const key = characteristic === 'duration' ? 'Duration_Label' : characteristic === 'distance' ? 'Distance_Label' : 'Speed_Label';
    const dataSection = tripPatternData[key as keyof TripPatternData];
    
    const sectionsByPeriod = getTripPatternSectionsByPeriod(dataSection);
    const hasVehicleLevel = sectionsByPeriod.some(({ section }) => section?.vehicle_level);
    if (!hasVehicleLevel) return [];
    
    const filteredSections = chartMonth
      ? (() => {
          const targetYm = `${chartMonth.year}-${String(chartMonth.month).padStart(2, '0')}`;
          const matched = sectionsByPeriod.filter(({ period }) => {
            const sectionYm = getTripPatternPeriodYearMonth(period);
            if (sectionYm) return sectionYm === targetYm;
            return (period || '').toString().slice(0, 7) === targetYm; // fallback for unexpected formats
          });
          // If filtering results in an empty table for demo exports (e.g. single "Jan 2026" label),
          // show the full dataset instead of an empty state.
          return matched.length > 0 ? matched : sectionsByPeriod;
        })()
      : sectionsByPeriod;
    
    const formatValue = (value: number, char: 'duration' | 'distance' | 'speed'): string => {
      if (char === 'duration') {
        return `${value.toFixed(1)} min`;
      } else if (char === 'distance') {
        return `${value.toFixed(1)} mi`;
      } else {
        return `${value.toFixed(1)} mph`;
      }
    };
    
    // Create a map of vehicle ID to vehicle type and days from CSV
    const vehicleTypeMap = new Map<string, string>();
    const vehicleDaysMap = new Map<string, number>();
    vehicleLevelCSV.forEach(row => {
      const vid = row.Vehicle_ID || row.vehicle_id || row.VehicleID;
      if (vid) {
        const vtype = row.Vehicle_Type || row.vehicle_type || row['Vehicle Type'] || row['MAPPED VEHICLE TYPE'] || '';
        vehicleTypeMap.set(vid, vtype);
        // Get days from CSV (try multiple possible column names)
        const days = parseFloat(
          row.total_days || 
          row.Total_Days || 
          row.days || 
          row.Days || 
          '0'
        );
        vehicleDaysMap.set(vid, days);
      }
    });
    
    const result: Array<{
      vehicle_id: JSX.Element;
      vehicle_id_sortKey: string;
      total_days: string;
      sample_size: string;
      mean: string;
      median: string;
      typical_range: string;
      min: string;
      max: string;
      _lower_quartile?: string;
      _upper_quartile?: string;
      period?: string;
    }> = [];
    
    for (const { period, section } of filteredSections) {
      const vehicleLevel = section?.vehicle_level;
      if (!vehicleLevel || typeof vehicleLevel !== 'object') continue;
      for (const [vehicleId, vehicleData] of Object.entries(vehicleLevel) as [string, VehicleTypeData][]) {
      if (vehicleData?.summary_stats) {
        const stats = vehicleData.summary_stats;
        const vehicleType = vehicleTypeMap.get(vehicleId) ||
          (vehicleData as { vehicle_type?: string; Vehicle_Type?: string })?.vehicle_type ||
          (vehicleData as { vehicle_type?: string; Vehicle_Type?: string })?.Vehicle_Type ||
          '';
        
        if (selectedVehicleTypeFilter && vehicleType.toLowerCase() !== selectedVehicleTypeFilter.toLowerCase()) {
          continue;
        }
        
        const isSelectedType = selectedVehicleTypeFilter && 
          vehicleType.toLowerCase() === selectedVehicleTypeFilter.toLowerCase();
        
        const days = vehicleDaysMap.get(vehicleId) || 0;
        const activeDays = daysActiveByVehicleId.get(vehicleId) ?? 0;
        // When a month is selected, use active days in that month; never show 0 days if vehicle has trips
        const daysToShow = chartMonth
          ? (activeDays > 0 ? activeDays : (stats.SampleSize > 0 ? 1 : 0))
          : (days > 0 ? days : activeDays);
        const normalizedVin = vehicleId.trim().replace(/\s+/g, '').toUpperCase();
        const normalizedVinLower = vehicleId.trim().replace(/\s+/g, '').toLowerCase();
        const displayName = vinToCustomerNameMap.get(normalizedVin) ||
          vinToCustomerNameMap.get(normalizedVinLower) ||
          vinToCustomerNameMap.get(vehicleId.trim()) ||
          vehicleId;
        result.push({
          vehicle_id: (
            <div className="leading-tight">
              <div className="whitespace-nowrap">{redactVehicleId(displayName, isDemoMode)}</div>
              <div className={`text-xs whitespace-nowrap ${
                isSelectedType 
                  ? 'text-warning drop-shadow-glow-warning-lg' 
                  : 'text-muted-foreground'
              }`}>
                {vehicleType ? redactVehicleType(vehicleType, isDemoMode) : '—'}
              </div>
            </div>
          ),
          vehicle_id_sortKey: displayName,
          total_days: Math.round(daysToShow).toString(),
          sample_size: Math.round(stats.SampleSize).toString(),
          mean: formatValue(stats.Mean, characteristic),
          median: formatValue(stats.Q2, characteristic),
          typical_range: `${formatValue(stats.Q1, characteristic)} - ${formatValue(stats.Q3, characteristic)}`,
          min: formatValue(stats.Min, characteristic),
          max: formatValue(stats.Max, characteristic),
          _lower_quartile: formatValue(stats.Q1, characteristic),
          _upper_quartile: formatValue(stats.Q3, characteristic),
            ...(filteredSections.length > 1 ? { period } : {}),
        });
        }
      }
    }
    
    // Sort by vehicle ID
    return result.sort((a, b) => a.vehicle_id_sortKey.localeCompare(b.vehicle_id_sortKey));
  };

  // Dates from time series for period filter and calendar (Jan 2026 onwards only)
  const availableDates = useMemo(() => {
    if (!timeSeriesData) return [];
    const fromTrips = (timeSeriesData.trips?.daily ?? []).map((d: { date: string }) => (d.date || '').slice(0, 10)).filter(Boolean);
    const fromMileage = (timeSeriesData.mileage?.daily ?? []).map((d: { date: string }) => (d.date || '').slice(0, 10)).filter(Boolean);
    const set = new Set<string>([...fromTrips, ...fromMileage]);
    return Array.from(set).filter(d => d >= '2026-01-01').sort().reverse();
  }, [timeSeriesData]);

  const availableWeeks = useMemo(() => {
    if (availableDates.length === 0) return [];
    const weeks: { label: string; startDate: string; endDate: string }[] = [];
    const sortedDates = [...availableDates].sort();
    const earliest = new Date(sortedDates[0]);
    const latest = new Date(sortedDates[sortedDates.length - 1]);
    const startDate = new Date(earliest);
    const dayOfWeek = startDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startDate.setDate(startDate.getDate() + daysToMonday);
    let currentWeekStart = new Date(startDate);
    let weekNumber = 1;
    while (currentWeekStart <= latest) {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const startStr = currentWeekStart.toISOString().split('T')[0];
      const endStr = weekEnd.toISOString().split('T')[0];
      const hasData = availableDates.some((d: string) => d >= startStr && d <= endStr);
      if (hasData) {
        weeks.push({ label: `Week ${weekNumber} (${startStr} to ${endStr})`, startDate: startStr, endDate: endStr });
        weekNumber++;
      }
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    }
    return weeks.reverse();
  }, [availableDates]);

  const availableMonths = useMemo(() => {
    if (availableDates.length === 0) return [];
    const monthsMap = new Map<string, { min: string; max: string }>();
    availableDates.forEach((date: string) => {
      const yearMonth = date.substring(0, 7);
      if (!monthsMap.has(yearMonth)) {
        monthsMap.set(yearMonth, { min: date, max: date });
      } else {
        const cur = monthsMap.get(yearMonth)!;
        if (date < cur.min) cur.min = date;
        if (date > cur.max) cur.max = date;
      }
    });
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return Array.from(monthsMap.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([yearMonth, { min, max }]) => {
        const [y, m] = yearMonth.split('-');
        const monthIndex = parseInt(m, 10) - 1;
        const monthName = (monthIndex >= 0 && monthIndex < 12) ? monthNames[monthIndex] : m;
        const shortYear = (y && y.length >= 2) ? y.slice(-2) : y;
        return { label: `${monthName} ${shortYear} (${min} to ${max})`, startDate: min, endDate: max };
      });
  }, [availableDates]);

  const periodOptions = useMemo(() => {
    switch (periodType) {
      case 'day':
        return availableDates.map((d: string) => ({ label: d, startDate: d, endDate: d }));
      case 'week':
        return availableWeeks;
      case 'month':
        return availableMonths;
      default:
        return [];
    }
  }, [periodType, availableDates, availableWeeks, availableMonths]);

  const effectiveDateWindow = useMemo((): { startDate: string; endDate: string } | null => {
    if (selectedPeriodRange) return selectedPeriodRange;
    if ((periodType === 'day' || periodType === 'week') && calendarPageMonth) {
      const y = calendarPageMonth.year;
      const m = calendarPageMonth.month;
      const startDate = `${y}-${String(m + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      const endDate = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      return { startDate, endDate };
    }
    return null;
  }, [periodType, calendarPageMonth, selectedPeriodRange]);

  // Reset period/calendar when period type changes
  useEffect(() => {
    setSelectedSummaryDate('');
    setSelectedPeriodRange(null);
    setCalendarPageMonth(null);
  }, [periodType]);

  // Default to the most recent available month (full calendar month) instead of a partial exported period.
  const hasSetDefaultMonthFromPeriodsRef = useRef(false);
  useEffect(() => {
    if (variant === 'utilisation-only' && useTruthUtil) return;
    if (hasSetDefaultMonthFromPeriodsRef.current) return;

    // Prefer truth-layer GPS months when present (Samsara tenants / Fleet Report maps).
    // gpsAvailableMonths is sorted newest-first.
    if (gpsAvailableMonths.length > 0) {
      const mostRecent = gpsAvailableMonths[0];
      const full = buildFullMonthPeriod(mostRecent.year, mostRecent.month);
      setPeriodType('day');
      setSelectedPeriodRange(null);
      setSelectedSummaryDate('');
      setSelectedMonthForCharts({ year: mostRecent.year, month: mostRecent.month });
      setCalendarPageMonth({ year: mostRecent.year, month: mostRecent.month - 1 });
      setSelectedPeriodRange({ startDate: full.startDate, endDate: full.endDate });
      hasSetDefaultMonthFromPeriodsRef.current = true;
      return;
    }

    if (!periods || periods.length === 0) return;
    const mostRecent = periods[0];
    const endMonth = getChartMonthFromPeriod(mostRecent);
    if (!endMonth) return;
    // Keep Period type = Day by default; month selection is a calendar filter.
    setPeriodType('day');
    setSelectedPeriodRange(null);
    setSelectedSummaryDate('');
    setSelectedMonthForCharts({ year: endMonth.year, month: endMonth.month });
    setCalendarPageMonth({ year: endMonth.year, month: endMonth.month - 1 });
    hasSetDefaultMonthFromPeriodsRef.current = true;
  }, [periods, gpsAvailableMonths, buildFullMonthPeriod, setSelectedPeriod, variant, useTruthUtil]);

  // If GPS months arrive after an initial period-based default (e.g. Jan 2026), switch to the latest GPS month.
  useEffect(() => {
    if (variant === 'utilisation-only') return;
    if (gpsAvailableMonths.length === 0) return;
    const mostRecent = gpsAvailableMonths[0];
    const selectedIsGps = selectedMonthForCharts
      ? gpsAvailableMonths.some(
          (m) => m.year === selectedMonthForCharts.year && m.month === selectedMonthForCharts.month,
        )
      : false;
    if (selectedIsGps) return;
    const full = buildFullMonthPeriod(mostRecent.year, mostRecent.month);
    setSelectedMonthForCharts({ year: mostRecent.year, month: mostRecent.month });
    setCalendarPageMonth({ year: mostRecent.year, month: mostRecent.month - 1 });
    setSelectedPeriodRange({ startDate: full.startDate, endDate: full.endDate });
    hasSetDefaultMonthFromPeriodsRef.current = true;
  }, [variant, gpsAvailableMonths, selectedMonthForCharts, buildFullMonthPeriod]);

  // Default calendar to most recent month when in day/week mode and we have data
  useEffect(() => {
    if ((periodType === 'day' || periodType === 'week') && availableMonths.length > 0 && calendarPageMonth === null) {
      const mostRecent = availableMonths[0];
      const [y, m] = mostRecent.startDate.split('-');
      setCalendarPageMonth({ year: parseInt(y, 10), month: parseInt(m, 10) - 1 });
    }
  }, [periodType, availableMonths, calendarPageMonth]);

  // Vehicle options for lookup (from vehicle-level CSV + fleet list display names)
  const vehicleOptions = useMemo(() => {
    const ids = new Set<string>();
    vehicleLevelCSV.forEach(row => {
      const vid = row.vehicle || row.Vehicle_ID || row.vehicle_id || row.VehicleID || '';
      if (vid) ids.add(standardiseVehicleId(vid));
    });
    return Array.from(ids).sort();
  }, [vehicleLevelCSV]);

  // Normalise vehicle type from a CSV row (check all common column name variants, including MAPPED VEHICLE TYPE for fleet list / S3)
  // Prioritise "VehicleType" as per CSV; fall back to case-insensitive key match so any header casing works
  const getVehicleTypeFromRow = (row: Record<string, string | undefined>): string => {
    const knownKeys = [
      'VehicleType',
      'Vehicle_Type',
      'vehicle_type',
      'Vehicle Type',
      'MAPPED VEHICLE TYPE',
      'MAPPEDVEHICLETYPE',
      'MAPPED_VEHICLE_TYPE',
    ];
    for (const key of knownKeys) {
      const val = row[key];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        return String(val).trim().replace(/\s+/g, ' ');
      }
    }
    // Case-insensitive fallback: CSV may have "vehicletype", "Vehicle Type", etc.
    const normalized = (k: string) => k.replace(/\s+/g, ' ').trim().toLowerCase();
    const targetNames = ['vehicletype', 'vehicle type', 'mapped vehicle type'];
    for (const key of Object.keys(row)) {
      if (targetNames.includes(normalized(key))) {
        const val = row[key];
        if (val !== undefined && val !== null && String(val).trim() !== '') {
          return String(val).trim().replace(/\s+/g, ' ');
        }
      }
    }
    return '';
  };

  // When Lookup Vehicle Type is set, restrict vehicle reg options to that type
  const vehicleOptionsForLookup = useMemo(() => {
    if (!selectedLookupVehicleType) return vehicleOptions;
    const typeKey = getVehicleTypeFromRow({ VehicleType: selectedLookupVehicleType }).toLowerCase();
    if (!typeKey) return vehicleOptions;
    const ids = new Set<string>();
    vehicleLevelCSV.forEach(row => {
      const rowType = getVehicleTypeFromRow(row);
      if (rowType.toLowerCase() !== typeKey) return;
      const vid = row.vehicle || row.Vehicle_ID || row.vehicle_id || row.VehicleID || '';
      if (vid) ids.add(standardiseVehicleId(vid));
    });
    return Array.from(ids).sort();
  }, [vehicleOptions, selectedLookupVehicleType, vehicleLevelCSV]);

  // Top dates: filled after mapCsvRowToTableUtilisationMetrics (same source as Vehicle Summary)

  // Get time interval subtitle for KPI cards — show actual date range when available
  const getDisplayDateRange = (): string => {
    const fmt = (s: string) => {
      const d = new Date(s);
      return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };
    if (variant === 'utilisation-only' && useTruthUtil && !isDemoMode) {
      return utilTruthDateRange.startDate === utilTruthDateRange.endDate
        ? fmt(utilTruthDateRange.startDate)
        : `${fmt(utilTruthDateRange.startDate)} – ${fmt(utilTruthDateRange.endDate)}`;
    }
    // Prefer the user's calendar selection (e.g. full March) over export metadata ranges.
    if (effectiveDateWindow) {
      return effectiveDateWindow.startDate === effectiveDateWindow.endDate
        ? fmt(effectiveDateWindow.startDate)
        : `${fmt(effectiveDateWindow.startDate)} – ${fmt(effectiveDateWindow.endDate)}`;
    }
    if (timeSeriesData?.metadata?.start_date && timeSeriesData?.metadata?.end_date) {
      const fmt = (s: string) => {
        const d = new Date(s);
        return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      };
      return `${fmt(timeSeriesData.metadata.start_date)} – ${fmt(timeSeriesData.metadata.end_date)}`;
    }
    if (selectedPeriod) return formatPeriodToDateRange(selectedPeriod);
    return '';
  };

  // Get time interval - now uses dedicated state instead of date filter
  const getTimeInterval = (): 'daily' | 'weekly' | 'monthly' => {
    return selectedTimeInterval;
  };

  // Chart month: explicit selection from month buttons.
  // Avoid falling back to export selectedPeriod (often Jan 2026) when truth util / GPS months drive the bar.
  const chartMonth = useMemo(() => {
    if (selectedMonthForCharts) return selectedMonthForCharts;
    if (variant === 'utilisation-only' && useTruthUtil && !isDemoMode) {
      const end = utilTruthDateRange.endDate;
      if (end && /^\d{4}-\d{2}-\d{2}/.test(end)) {
        const year = parseInt(end.slice(0, 4), 10);
        const month = parseInt(end.slice(5, 7), 10);
        if (year && month >= 1 && month <= 12) return { year, month };
      }
      return null;
    }
    if (variant !== 'utilisation-only' && gpsAvailableMonths.length > 0) {
      return { year: gpsAvailableMonths[0].year, month: gpsAvailableMonths[0].month };
    }
    return getChartMonthFromPeriod(selectedPeriod);
  }, [
    selectedMonthForCharts,
    selectedPeriod,
    variant,
    useTruthUtil,
    isDemoMode,
    utilTruthDateRange.endDate,
    gpsAvailableMonths,
  ]);

  // Dates present in loaded vehicle-level utilisation rows (truth or CSV).
  const utilisationDataDates = useMemo(() => {
    const set = new Set<string>();
    vehicleLevelCSV.forEach((row) => {
      const iso = utilisationCsvRowIsoDate(row);
      if (iso && iso >= '2026-01-01') set.add(iso);
    });
    return Array.from(set).sort();
  }, [vehicleLevelCSV]);

  const buildMonthOptionsFromDates = useCallback((dates: string[]) => {
    const monthsMap = new Map<string, { year: number; month: number; startDate: string; endDate: string }>();
    dates.forEach((date) => {
      const yearMonth = date.substring(0, 7);
      const [y, m] = yearMonth.split('-');
      const year = parseInt(y, 10);
      const month = parseInt(m, 10);
      if (!year || month < 1 || month > 12) return;
      const existing = monthsMap.get(yearMonth);
      if (!existing) {
        monthsMap.set(yearMonth, { year, month, startDate: date, endDate: date });
      } else {
        if (date < existing.startDate) existing.startDate = date;
        if (date > existing.endDate) existing.endDate = date;
      }
    });
    return Array.from(monthsMap.values()).sort((a, b) =>
      b.year !== a.year ? b.year - a.year : b.month - a.month,
    );
  }, []);

  // Month options for the button bar:
  // - util tab: months with loaded utilisation rows
  // - fleet tab: prefer truth-layer GPS months when available; else exported periods
  const monthOptions = useMemo(() => {
    if (variant === 'utilisation-only') {
      return buildMonthOptionsFromDates(utilisationDataDates).map((m) => ({
        ...m,
        hasData: true,
      }));
    }

    if (gpsAvailableMonths.length > 0) {
      return gpsAvailableMonths
        .slice()
        .sort((a, b) => (b.year !== a.year ? b.year - a.year : b.month - a.month))
        .map((m) => {
          const full = buildFullMonthPeriod(m.year, m.month);
          return {
            year: m.year,
            month: m.month,
            startDate: full.startDate,
            endDate: full.endDate,
            hasData: true,
          };
        });
    }

    const seen = new Set<string>();
    const list: Array<{ year: number; month: number; startDate: string; endDate: string; hasData: boolean }> = [];
    for (const period of periods) {
      for (const m of getMonthsInPeriod(period)) {
        if (m.year < 2026) continue;
        const key = `${m.year}-${m.month}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const full = buildFullMonthPeriod(m.year, m.month);
        list.push({ year: m.year, month: m.month, startDate: full.startDate, endDate: full.endDate, hasData: true });
      }
    }
    return list.sort((a, b) => (b.year !== a.year ? b.year - a.year : b.month - a.month));
  }, [variant, utilisationDataDates, periods, gpsAvailableMonths, buildMonthOptionsFromDates, buildFullMonthPeriod]);

  // Compact month stepper: ‹ › walk the available months (newest-first), "Latest" jumps to the newest.
  const selectMonthOption = useCallback(
    (mo: { year: number; month: number }) => {
      const full = buildFullMonthPeriod(mo.year, mo.month);
      setSelectedMonthForCharts({ year: mo.year, month: mo.month });
      setCalendarPageMonth({ year: mo.year, month: mo.month - 1 });
      setPeriodType('day');
      setSelectedPeriodRange({ startDate: full.startDate, endDate: full.endDate });
      setSelectedSummaryDate('');
      if (variant === 'utilisation-only' && useTruthUtil) {
        utilTruthDateRange.setDateRange(full.startDate, full.endDate);
      } else {
        const periodThatContains = periods.find((p) => periodContainsMonth(p, mo));
        if (periodThatContains) setSelectedPeriod(periodThatContains);
      }
    },
    [buildFullMonthPeriod, variant, useTruthUtil, utilTruthDateRange, periods, setSelectedPeriod],
  );

  // When selectedPeriod changes, keep selectedMonthForCharts in sync (fleet tab only).
  // Skip when GPS months drive the button bar — period exports may still be Jan-only.
  useEffect(() => {
    if (variant === 'utilisation-only' && useTruthUtil) return;
    if (gpsAvailableMonths.length > 0) return;
    if (!selectedPeriod) return;
    const endMonth = getChartMonthFromPeriod(selectedPeriod);
    if (!endMonth) return;
    if (!selectedMonthForCharts || !periodContainsMonth(selectedPeriod, selectedMonthForCharts)) {
      setSelectedMonthForCharts(endMonth);
    }
  }, [selectedPeriod, variant, useTruthUtil, gpsAvailableMonths, selectedMonthForCharts]);

  // Util tab: when loaded data only covers certain months, select the most recent month that has rows.
  // Also align the truth report period when discovery already set a month but rows arrived for another.
  useEffect(() => {
    if (variant !== 'utilisation-only' || monthOptions.length === 0) return;
    const monthIsInData = selectedMonthForCharts
      ? monthOptions.some((m) => m.year === selectedMonthForCharts.year && m.month === selectedMonthForCharts.month)
      : false;
    if (monthIsInData) {
      // Ensure calendar filter matches the selected month once options exist
      if (selectedMonthForCharts && !selectedPeriodRange) {
        const mo = monthOptions.find(
          (m) => m.year === selectedMonthForCharts.year && m.month === selectedMonthForCharts.month,
        );
        if (mo) {
          const full = buildFullMonthPeriod(mo.year, mo.month);
          setSelectedPeriodRange({ startDate: full.startDate, endDate: full.endDate });
          setCalendarPageMonth({ year: mo.year, month: mo.month - 1 });
        }
      }
      return;
    }
    // Don't steal the month while a fetch is still in flight (stale monthOptions)
    if (loading) return;
    const mostRecent = monthOptions[0];
    const full = buildFullMonthPeriod(mostRecent.year, mostRecent.month);
    setSelectedMonthForCharts({ year: mostRecent.year, month: mostRecent.month });
    setCalendarPageMonth({ year: mostRecent.year, month: mostRecent.month - 1 });
    setSelectedPeriodRange({ startDate: full.startDate, endDate: full.endDate });
    if (useTruthUtil) {
      // Prefer full month so filters and KPI cards match the month button label
      utilTruthDateRange.setDateRange(full.startDate, full.endDate);
    }
  }, [
    variant,
    monthOptions,
    selectedMonthForCharts,
    selectedPeriodRange,
    useTruthUtil,
    utilTruthDateRange.setDateRange,
    buildFullMonthPeriod,
    loading,
  ]);

  // For full Fleet Report, KPI cards use the selected month (chartMonth) so they update when user clicks Jan/Feb.
  const kpiDateWindow = useMemo((): { startDate: string; endDate: string } | null => {
    if (chartMonth) {
      const y = chartMonth.year;
      const m = chartMonth.month;
      const startDate = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      const endDate = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      return { startDate, endDate };
    }
    return effectiveDateWindow;
  }, [chartMonth, effectiveDateWindow]);

  /** Dates in the selected month that have utilisation / vehicle-day rows (ascending). */
  const utilisationDatesInKpiMonth = useMemo(() => {
    const dates = new Set<string>();
    vehicleLevelCSV.forEach((row) => {
      const iso = utilisationCsvRowIsoDate(row);
      if (!iso) return;
      if (kpiDateWindow && (iso < kpiDateWindow.startDate || iso > kpiDateWindow.endDate)) return;
      dates.add(iso);
    });
    return Array.from(dates).sort();
  }, [vehicleLevelCSV, kpiDateWindow]);

  /**
   * Fleet Report KPI grain = one calendar day.
   * Prefer a single-day calendar selection in the month; otherwise the most recent day with data.
   */
  const kpiDayIso = useMemo((): string | null => {
    if (utilisationDatesInKpiMonth.length === 0) return null;
    if (
      selectedPeriodRange &&
      selectedPeriodRange.startDate === selectedPeriodRange.endDate &&
      utilisationDatesInKpiMonth.includes(selectedPeriodRange.startDate)
    ) {
      return selectedPeriodRange.startDate;
    }
    return utilisationDatesInKpiMonth[utilisationDatesInKpiMonth.length - 1];
  }, [selectedPeriodRange, utilisationDatesInKpiMonth]);

  const kpiDayRows = useMemo(() => {
    if (!kpiDayIso) return [];
    return vehicleLevelCSV.filter((row) => utilisationCsvRowIsoDate(row) === kpiDayIso);
  }, [vehicleLevelCSV, kpiDayIso]);

  const formatKpiDayLabel = useCallback((iso: string): string => {
    const d = new Date(`${iso}T12:00:00`);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }, []);

  const getFleetKpiDailySubtitle = (): string => {
    if (!kpiDayIso) return 'Fleet-wide daily';
    return `Fleet-wide daily · ${formatKpiDayLabel(kpiDayIso)}`;
  };

  /** Date window for util-tab tables, Top 5, and charts — calendar selection, else truth report period. */
  const utilisationFilterWindow = useMemo((): { startDate: string; endDate: string } | null => {
    // Day/week/month picked on the utilisation calendar must narrow Vehicle Summary rows.
    if (selectedPeriodRange) return selectedPeriodRange;
    if (variant === 'utilisation-only' && useTruthUtil && !isDemoMode) {
      return {
        startDate: utilTruthDateRange.startDate,
        endDate: utilTruthDateRange.endDate,
      };
    }
    if (effectiveDateWindow) return effectiveDateWindow;
    if (kpiDateWindow) return kpiDateWindow;
    return null;
  }, [
    variant,
    useTruthUtil,
    isDemoMode,
    utilTruthDateRange.startDate,
    utilTruthDateRange.endDate,
    selectedPeriodRange,
    effectiveDateWindow,
    kpiDateWindow,
  ]);

  // Days active per vehicle: distinct dates in vehicleLevelCSV; when a month is selected (kpiDateWindow), count only that month
  const daysActiveByVehicleId = useMemo(() => {
    const window = kpiDateWindow || effectiveDateWindow;
    const byVehicle = new Map<string, Set<string>>();
    vehicleLevelCSV.forEach(row => {
      const vid = row.vehicle || row.Vehicle_ID || row.vehicle_id || row.VehicleID || '';
      if (!vid) return;
      const std = standardiseVehicleId(vid);
      const iso = utilisationCsvRowIsoDate(row);
      if (!iso) return;
      if (window && (iso < window.startDate || iso > window.endDate)) return;
      if (!byVehicle.has(std)) byVehicle.set(std, new Set());
      byVehicle.get(std)!.add(iso);
    });
    const out = new Map<string, number>();
    byVehicle.forEach((set, std) => out.set(std, set.size));
    return out;
  }, [vehicleLevelCSV, kpiDateWindow, effectiveDateWindow]);

  // Top performers: filled after mapCsvRowToTableUtilisationMetrics (same filters & formula as Vehicle Summary)

  // Reset week offset when switching away from daily view or when period changes
  useEffect(() => {
    if (selectedTimeInterval !== 'daily') {
      setWeekOffset(0);
    } else if (timeSeriesData) {
      // When switching to daily view or period changes, show earliest week first
      // Calculate common date range inline
      const normalizeDate = (dateStr: string): string => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      };
      
      const tripsDates = (timeSeriesData.trips?.daily ?? [])
        .map(item => normalizeDate((item as { date: string }).date))
        .filter(Boolean);
      const utilizationDates = (timeSeriesData.utilization?.daily ?? [])
        .map(item => normalizeDate((item as { date: string }).date))
        .filter(Boolean);
      const mileageDates = (timeSeriesData.mileage?.daily ?? [])
        .map(item => normalizeDate((item as { date: string }).date))
        .filter(Boolean);
      
      const commonDates = tripsDates.filter(date => 
        utilizationDates.includes(date) && mileageDates.includes(date)
      ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      
      if (commonDates.length > 7) {
        const totalWeeks = Math.ceil(commonDates.length / 7);
        setWeekOffset(-(totalWeeks - 1)); // Set to earliest week (index 0)
      } else {
      setWeekOffset(0);
    }
    }
  }, [selectedTimeInterval, selectedPeriod, timeSeriesData]);

  // Get common date range across all three datasets
  const getCommonDateRange = (): string[] => {
    if (!timeSeriesData) return [];
    const tripsDaily = timeSeriesData.trips?.daily ?? [];
    const utilizationDaily = timeSeriesData.utilization?.daily ?? [];
    const mileageDaily = timeSeriesData.mileage?.daily ?? [];
    const normalizeDate = (dateStr: string): string => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    };
    const tripsDates = tripsDaily.map(item => normalizeDate((item as { date: string }).date)).filter(Boolean);
    const utilizationDates = utilizationDaily.map(item => normalizeDate((item as { date: string }).date)).filter(Boolean);
    const mileageDates = mileageDaily.map(item => normalizeDate((item as { date: string }).date)).filter(Boolean);
    
    // Find intersection of all three date sets
    const commonDates = tripsDates.filter(date => 
      utilizationDates.includes(date) && mileageDates.includes(date)
    );
    
    // Sort by date
    return commonDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  };

  // Helper to filter daily data to one week using common date range
  const filterDailyDataByWeek = <T extends { date?: string }>(data: T[], weekOffset: number, commonDates: string[]): T[] => {
    if (data.length === 0 || commonDates.length === 0) return [];
    
    // Normalize date for comparison
    const normalizeDate = (dateStr: string): string => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    };
    
    // Filter data to only include dates in commonDates, then sort
    const filteredData = data.filter(item => {
      if (!item.date) return false;
      const normalized = normalizeDate(item.date);
      return commonDates.includes(normalized);
    });
    const sortedData = [...filteredData].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateA - dateB;
    });

    // Calculate which week to show (0 = most recent week, -1 = previous week, etc.)
    const totalWeeks = Math.ceil(sortedData.length / 7);
    const weekIndex = totalWeeks - 1 + weekOffset; // Most recent week is at the end
    
    if (weekIndex < 0 || weekIndex >= totalWeeks) return [];
    
    const startIndex = weekIndex * 7;
    const endIndex = Math.min(startIndex + 7, sortedData.length);
    
    return sortedData.slice(startIndex, endIndex);
  };


  // Calculate vehicle type distribution for ES mode (for donut chart)
  const getVehicleTypeDistribution = (): Array<{ label: string; value: number; color: string }> => {
    // Only calculate for ES mode
    if (serviceType !== 'emergency_services') {
      return [];
    }
    
    // If CSV is empty, return empty array (don't log warning - data might still be loading)
    if (fleetListCSV.length === 0) {
      return [];
    }
    
    const typeCounts = new Map<string, number>();
    const colorMap: Record<string, string> = {
      'Response car': CHART_PALETTE[0],      // Blue
      'Small van': CHART_PALETTE[2],          // Green
      'Large van': CHART_PALETTE[1],          // Amber
      'Pumping appliance': CHART_PALETTE[3],  // Red
      'Other appliance': CHART_PALETTE[4]      // Purple
    };
    
    // The 5 expected vehicle types (already mapped by backend from data_cleaning.json)
    const expectedTypes = ['Response car', 'Small van', 'Large van', 'Pumping appliance', 'Other appliance'];
    
    // Initialize counts for all expected types
    expectedTypes.forEach(type => {
      typeCounts.set(type, 0);
    });
    
    // Track all unique vehicle types found for debugging
    const allTypesFound = new Set<string>();
    let totalVehiclesProcessed = 0;
    let vehiclesWithType = 0;
    
    // Count vehicles by type from the fleet list CSV
    // The backend already applies vehicle type mapping from data_cleaning.json,
    // so we just need to count the mapped types directly (case-insensitive match)
    // ✅ IMPORTANT: Only process rows with valid VEHICLEINDEX (same logic as getTotalFleetSize)
    fleetListCSV.forEach(row => {
      // ✅ First, verify this row has a valid VEHICLEINDEX (only use VEHICLEINDEX and VEHICLE TYPE columns)
      let vehicleIndex = '';
      for (const key in row) {
        if (key.toUpperCase().trim() === 'VEHICLEINDEX') {
          vehicleIndex = row[key] || '';
          break;
        }
      }
      
      // Exclude rows without valid VEHICLEINDEX (exclude empty values and header rows)
      const vehicleIndexTrimmed = vehicleIndex.trim();
      if (vehicleIndexTrimmed === '' || vehicleIndexTrimmed.toUpperCase() === 'VEHICLEINDEX') {
        return; // Skip this row - no valid VEHICLEINDEX
      }
      
      totalVehiclesProcessed++;
      
      // ✅ ONLY use MAPPED VEHICLE TYPE column (no fallback)
      let vehicleType = '';
      const availableKeys = Object.keys(row);
      
      // Find MAPPED VEHICLE TYPE column (handle various formats)
      for (const key of availableKeys) {
        const upperKey = key.toUpperCase().trim();
        // Check for MAPPED VEHICLE TYPE with various formats
        if (upperKey === 'MAPPED VEHICLE TYPE' || 
            upperKey === 'MAPPEDVEHICLETYPE' || 
            upperKey === 'MAPPED_VEHICLE_TYPE' ||
            upperKey.replace(/\s+/g, ' ') === 'MAPPED VEHICLE TYPE' ||
            upperKey.replace(/_/g, ' ') === 'MAPPED VEHICLE TYPE') {
          vehicleType = row[key] || '';
          // Found the column, break out of loop
          break;
        }
      }
      
      // If MAPPED VEHICLE TYPE is empty or doesn't exist, skip this row
      if (!vehicleType || vehicleType.trim() === '') {
        // Check if column actually exists (for debugging)
        const availableKeysUpper = availableKeys.map(k => k.toUpperCase().trim());
        const hasMappedColumn = availableKeysUpper.some(k => 
          k === 'MAPPED VEHICLE TYPE' || 
          k === 'MAPPEDVEHICLETYPE' || 
          k === 'MAPPED_VEHICLE_TYPE'
        );
        
        if (!hasMappedColumn) {
          // Column doesn't exist - log warning (but only for first row to avoid spam)
          if (totalVehiclesProcessed === 1) {
            logger.warn(`[Vehicle Type Distribution] ⚠️ MAPPED VEHICLE TYPE column not found. Available keys:`, availableKeys);
            logger.warn(`[Vehicle Type Distribution] ⚠️ Available keys (upper):`, availableKeysUpper);
          }
        }
        return; // Skip this row
      }
      
      // Process the vehicle type from MAPPED VEHICLE TYPE column
      // No need to filter station names - the Python script already mapped everything correctly
      const trimmedType = vehicleType.trim();
      
      if (trimmedType === '') {
        return; // Skip empty values
      }
      
      vehiclesWithType++;
      allTypesFound.add(trimmedType);
      
      // Check if this type matches one of the expected types (case-insensitive)
      // The Python script should have already mapped it correctly to one of the 5 expected types
      // NO MAPPING - only use exact match from MAPPED VEHICLE TYPE column
      const matchedType = expectedTypes.find(expectedType => 
        expectedType.toLowerCase() === trimmedType.toLowerCase()
      );
      
      if (matchedType) {
        typeCounts.set(matchedType, (typeCounts.get(matchedType) || 0) + 1);
      } else {
        // Log unmatched types for debugging (shouldn't happen if Python script mapped correctly)
        logger.warn(`[Vehicle Type Distribution] ⚠️ Unmatched vehicle type from MAPPED VEHICLE TYPE column (not in expected 5 types): "${trimmedType}"`);
      }
    });
    
    // Log debug information
    logger.debug(`[Vehicle Type Distribution] Total vehicles processed: ${totalVehiclesProcessed}`);
    logger.debug(`[Vehicle Type Distribution] Vehicles with VEHICLE TYPE column: ${vehiclesWithType}`);
    logger.debug(`[Vehicle Type Distribution] All unique vehicle types found (${allTypesFound.size}):`, Array.from(allTypesFound).sort());
    logger.debug(`[Vehicle Type Distribution] Counts by type:`, Object.fromEntries(typeCounts));
    
    // Build result array with all expected types
    // Show all 5 types even if some have 0 counts, so user can see what's missing
    const distribution = expectedTypes
      .map(type => ({
        label: redactVehicleType(type, isDemoMode),
        value: typeCounts.get(type) || 0,
        color: colorMap[type]
      }));
    
    const zeroCountTypes = distribution.filter(item => item.value === 0);
    if (zeroCountTypes.length > 0) {
      logger.debug(`[Vehicle Type Distribution] Types with 0 vehicles (still shown on donut):`, zeroCountTypes.map(item => item.label));
    }

    logger.debug(`[Vehicle Type Distribution] Final distribution (all ${distribution.length} ES types):`, distribution);

    return distribution;
  };

  // Fleet Report KPI cards: fleet-wide daily rollup from vehicle-day rows (truth or CSV).
  const getAverageUtilization = (): string => {
    if (kpiDayRows.length === 0) return '—';
    const scores = kpiDayRows.map((row) => {
      if (isTruthUtilisationCsvRow(row)) {
        return parseFloat(String(row.utilisation_score ?? '0')) || 0;
      }
      return parseFloat(row.utilisation_score || row.utilization_score || row.Utilisation_Score || row.utilisation_rate || row.utilization_rate || row.Utilisation_Rate || '0') || 0;
    });
    const average = scores.reduce((sum, n) => sum + n, 0) / scores.length;
    return `${average.toFixed(1)}%`;
  };

  const getTotalMiles = (): string => {
    if (kpiDayRows.length === 0) return '—';
    const total = kpiDayRows.reduce((sum, row) => {
      return sum + parseFloat(row.total_miles || row.Total_Miles || row.miles || row.Miles || '0');
    }, 0);
    return total >= 1000 ? `${(total / 1000).toFixed(1)}K` : total.toFixed(0);
  };

  /** Distinct vehicles with a vehicle-day row on the KPI day (worked that day — not live telematics). */
  const getActiveVehicles = (): string => {
    if (kpiDayRows.length === 0) return '—';
    const ids = new Set<string>();
    kpiDayRows.forEach((row) => {
      const vid = row.vehicle || row.Vehicle_ID || row.vehicle_id || row.VehicleID || '';
      if (vid) ids.add(standardiseVehicleId(String(vid)));
    });
    return ids.size === 0 ? '—' : String(ids.size);
  };

  // Get utilization data for line chart (all days, no week filtering for Logistics mode)
  const getUtilizationData = () => {
    const util = timeSeriesData?.utilization;
    if (!timeSeriesData || !util) return [];
    const interval = getTimeInterval();
    let utilization = util[interval];

    if (!utilization || utilization.length === 0) return [];

    // For Logistics mode, show all days without week filtering
    // For ES mode or other intervals, keep original behavior
    if (interval === 'daily' && serviceType === 'logistics') {
      // Show all days - no filtering
      utilization = utilization;
    } else if (interval === 'daily') {
      // ES mode: filter to one week if daily view
      const commonDates = getCommonDateRange();
      utilization = filterDailyDataByWeek(utilization as Array<{ date: string; avg_utilization: number }>, weekOffset, commonDates) as typeof utilization;
    }

    let result = utilization.map(item => {
      let label = '';
      let weekday = '';
      if (interval === 'daily') {
        const dailyItem = item as { date: string; avg_utilization: number };
        if (dailyItem.date) {
          const date = new Date(dailyItem.date);
          const dateStr = (dailyItem.date || '').toString().slice(0, 10);
          if (serviceType === 'logistics') {
            label = dateStr || date.getDate().toString();
          } else {
          label = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
          weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
          }
        }
      } else if (interval === 'weekly') {
        const weeklyItem = item as { week_start: string; avg_utilization: number };
        label = weeklyItem.week_start ? new Date(weeklyItem.week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
      } else if (interval === 'monthly') {
        const monthlyItem = item as { month: string; avg_utilization: number };
        label = monthlyItem.month || '';
      }
      return {
        label,
        weekday,
        value: item.avg_utilization,
        ...item
      };
    });

    if (chartMonth) {
      const ym = `${chartMonth.year}-${String(chartMonth.month).padStart(2, '0')}`;
      const filtered = result.filter(item => {
        const r = item as Record<string, unknown>;
        if (interval === 'daily' && r.date) return String(r.date).slice(0, 7) === ym;
        if (interval === 'weekly' && r.week_start) return String(r.week_start).slice(0, 7) === ym;
        if (interval === 'monthly' && r.month) return String(r.month).slice(0, 7) === ym || String(r.month).startsWith(ym);
        // Logistics labels are ISO dates — also match on label when date field missing
        if (interval === 'daily' && /^\d{4}-\d{2}-\d{2}$/.test(item.label)) return item.label.slice(0, 7) === ym;
        return false;
      });
      // Keep selected-month filter strict so we never show Jan (or other) when June is selected.
      return filtered;
    }
    return result;
  };

  // Get mileage data for line chart (all days, no week filtering for Logistics mode)
  const getMileageData = () => {
    if (!timeSeriesData) return [];
    const interval = getTimeInterval();
    let mileage = timeSeriesData.mileage[interval];

    if (!mileage || mileage.length === 0) return [];

    // For Logistics mode, show all days without week filtering
    // For ES mode or other intervals, keep original behavior
    if (interval === 'daily' && serviceType === 'logistics') {
      // Show all days - no filtering
      mileage = mileage;
    } else if (interval === 'daily') {
      // ES mode: filter to one week if daily view
      const commonDates = getCommonDateRange();
      mileage = filterDailyDataByWeek(mileage as Array<{ date: string; total_miles: number }>, weekOffset, commonDates) as typeof mileage;
    }

    let result = mileage.map(item => {
      let label = '';
      let weekday = '';
      if (interval === 'daily') {
        const dailyItem = item as { date: string; total_miles: number };
        if (dailyItem.date) {
          const date = new Date(dailyItem.date);
          const dateStr = (dailyItem.date || '').toString().slice(0, 10);
          if (serviceType === 'logistics') {
            label = dateStr || date.getDate().toString();
          } else {
          label = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
          weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
          }
        }
      } else if (interval === 'weekly') {
        const weeklyItem = item as { week_start: string; total_miles: number };
        label = weeklyItem.week_start ? new Date(weeklyItem.week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
      } else if (interval === 'monthly') {
        const monthlyItem = item as { month: string; total_miles: number };
        label = monthlyItem.month || '';
      }
      return {
        label,
        weekday,
        value: item.total_miles,
        ...item
      };
    });

    if (chartMonth) {
      const ym = `${chartMonth.year}-${String(chartMonth.month).padStart(2, '0')}`;
      const filtered = result.filter(item => {
        const r = item as Record<string, unknown>;
        if (interval === 'daily' && r.date) return String(r.date).slice(0, 7) === ym;
        if (interval === 'weekly' && r.week_start) return String(r.week_start).slice(0, 7) === ym;
        if (interval === 'monthly' && r.month) return String(r.month).slice(0, 7) === ym || String(r.month).startsWith(ym);
        if (interval === 'daily' && /^\d{4}-\d{2}-\d{2}$/.test(item.label)) return item.label.slice(0, 7) === ym;
        return false;
      });
      return filtered;
    }
    return result;
  };

  // Get daily utilization data for ES mode chart (filter by effectiveDateWindow when set, or by chart month from period bar)
  const getDailyUtilizationData = () => {
    const daily = timeSeriesData?.utilization?.daily;
    if (!timeSeriesData || !daily) return [];
    let items = daily;
    if (effectiveDateWindow && items.length > 0) {
      items = items.filter((item: { date?: string }) => {
        const dateStr = (item.date || '').toString().slice(0, 10);
        return dateStr >= effectiveDateWindow!.startDate && dateStr <= effectiveDateWindow!.endDate;
      });
    }
    if (chartMonth && items.length > 0) {
      const ym = `${chartMonth.year}-${String(chartMonth.month).padStart(2, '0')}`;
      items = items.filter((item: { date?: string }) => (item.date || '').toString().slice(0, 7) === ym);
    }
    return items.map((item: { date: string; avg_utilization: number }) => {
      const dateStr = (item.date || '').toString().slice(0, 10);
      return {
        label: dateStr || (item.date ? new Date(item.date).getDate().toString() : ''),
        value: item.avg_utilization,
        date: dateStr,
      };
    });
  };

  // Get daily mileage data for ES mode chart (filter by effectiveDateWindow when set, or by chart month from period bar)
  const getDailyMileageData = () => {
    const daily = timeSeriesData?.mileage?.daily;
    if (!timeSeriesData || !daily) return [];
    let items = daily;
    if (effectiveDateWindow && items.length > 0) {
      items = items.filter((item: { date?: string }) => {
        const dateStr = (item.date || '').toString().slice(0, 10);
        return dateStr >= effectiveDateWindow!.startDate && dateStr <= effectiveDateWindow!.endDate;
      });
    }
    if (chartMonth && items.length > 0) {
      const ym = `${chartMonth.year}-${String(chartMonth.month).padStart(2, '0')}`;
      items = items.filter((item: { date?: string }) => (item.date || '').toString().slice(0, 7) === ym);
    }
    return items.map((item: { date: string; total_miles: number }) => {
      const dateStr = (item.date || '').toString().slice(0, 10);
      return {
        label: dateStr || (item.date ? new Date(item.date).getDate().toString() : ''),
        value: item.total_miles,
        date: dateStr,
      };
    });
  };

  /**
   * Fleet Report line plots: prefer vehicle-day rows (truth util) for the selected month.
   * Avoids January batch time-series showing while the month panel is on June.
   */
  const fleetReportDailyMileageSeries = useMemo(() => {
    if (!chartMonth) return [] as Array<{ label: string; value: number; date: string }>;
    const ym = `${chartMonth.year}-${String(chartMonth.month).padStart(2, '0')}`;
    const byDate = new Map<string, number>();
    vehicleLevelCSV.forEach((row) => {
      const iso = utilisationCsvRowIsoDate(row);
      if (!iso || !iso.startsWith(ym)) return;
      const miles = parseFloat(String(row.total_miles || row.Total_Miles || row.miles || row.Miles || '0')) || 0;
      byDate.set(iso, (byDate.get(iso) || 0) + miles);
    });
    return Array.from(byDate.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, value]) => ({ label: date, date, value }));
  }, [vehicleLevelCSV, chartMonth]);

  const fleetReportDailyUtilSeries = useMemo(() => {
    if (!chartMonth) return [] as Array<{ label: string; value: number; date: string }>;
    const ym = `${chartMonth.year}-${String(chartMonth.month).padStart(2, '0')}`;
    const byDate = new Map<string, { sum: number; count: number }>();
    vehicleLevelCSV.forEach((row) => {
      const iso = utilisationCsvRowIsoDate(row);
      if (!iso || !iso.startsWith(ym)) return;
      const score = isTruthUtilisationCsvRow(row)
        ? parseFloat(String(row.utilisation_score ?? '0')) || 0
        : parseFloat(String(row.utilisation_score || row.utilization_score || row.Utilisation_Score || row.utilisation_rate || '0')) || 0;
      const cur = byDate.get(iso) ?? { sum: 0, count: 0 };
      byDate.set(iso, { sum: cur.sum + score, count: cur.count + 1 });
    });
    return Array.from(byDate.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, v]) => ({
        label: date,
        date,
        value: v.count > 0 ? v.sum / v.count : 0,
      }));
  }, [vehicleLevelCSV, chartMonth]);

  // Component for Typical Range label with info icon
  const TypicalRangeLabel = () => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const iconRef = useRef<HTMLDivElement>(null);

    return (
      <>
        <div className="flex items-center gap-1.5">
          <span>Typical Range</span>
          <div
            ref={iconRef}
            className="relative inline-flex items-center"
            onMouseEnter={() => {
              if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
                setShowTooltip(true);
              }
            }}
            onMouseLeave={() => {
              setShowTooltip(false);
              setTooltipPosition(null);
            }}
          >
            <HelpCircle className="w-4 h-4 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help" />
          </div>
        </div>
        {showTooltip && tooltipPosition && createPortal(
          <div
            className="fixed px-3 py-2 bg-popover text-foreground text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y - 8}px`,
              transform: 'translate(-50%, -100%)',
              maxWidth: '280px'
            }}
          >
            <div className="font-semibold mb-1.5 text-warning drop-shadow-glow-warning-lg">Typical Range</div>
            <div className="text-foreground leading-relaxed">
              The middle 50% of trip values. Shows what's normal and helps spot unusual trips that may need attention.
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
          </div>,
          document.body
        )}
      </>
    );
  };

  // ==================== UTILIZATION HELPER FUNCTIONS ====================

  // Formatting helpers for utilisation tables and charts
  const formatNumber = (value: string | number, decimals: number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toFixed(decimals);
  };

  const formatTotalDays = (value: string | number): string => formatNumber(value, 0);
  const formatHours = (value: string | number): string => formatNumber(value, 1);
  const formatMiles = (value: string | number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num >= 100 ? formatNumber(value, 0) : formatNumber(value, 1);
  };
  const formatPercentage = (value: string | number): string => formatNumber(value, 1);
  const formatCount = (value: string | number): string => formatNumber(value, 0);

  /** Read numeric value from CSV row with flexible column names (new S3 utilisation output). */
  const getRowNum = (row: Record<string, string>, ...keys: string[]): number => {
    const lowerKeys = keys.map(k => k.toLowerCase());
    for (const [k, v] of Object.entries(row)) {
      if (v === '' || v == null) continue;
      const keyLower = k.toLowerCase().replace(/\s+/g, '_').replace(/%/g, 'pct');
      if (lowerKeys.some(lk => keyLower.includes(lk) || lk.includes(keyLower))) {
        const n = parseFloat(String(v).replace(/%/g, '').trim());
        if (!isNaN(n)) return n;
      }
    }
    for (const key of keys) {
      const v = row[key];
      if (v !== undefined && v !== '') {
        const n = parseFloat(String(v).replace(/%/g, '').trim());
        if (!isNaN(n)) return n;
      }
    }
    return 0;
  };

  /** Same overall util % as Vehicle Summary (weighted formula + targets). Used for Top 5, calendar, and trend. */
  const getProductiveHoursFromCsvRow = (csvRow: Record<string, any>): number => {
    const explicit = getRowNum(csvRow, 'productive_hours', 'productive hours');
    if (explicit > 0) return explicit;
    return (
      getRowNum(csvRow, 'active_hours', 'active_time', 'Active_hours') +
      getRowNum(csvRow, 'idle_hours', 'idle_hours', 'Idle_hours')
    );
  };

  const utilisationVolumeColumn = useMemo(
    () =>
      serviceType === 'emergency_services'
        ? ({ key: 'trips' as const, label: 'Trips', align: 'right' as const })
        : ({ key: 'productive_hrs' as const, label: 'Productive hrs', align: 'right' as const }),
    [serviceType],
  );

  const mapCsvRowToTableUtilisationMetrics = useCallback((csvRow: Record<string, any>) => {
    const vehicleType = getVehicleTypeFromRow(csvRow);
    const vehicleId = csvRow.vehicle || csvRow.Vehicle_ID || csvRow.VehicleID || csvRow.vehicle_id || csvRow.Vehicle || '';
    const trips = getRowNum(csvRow, 'trips', 'num_trips', 'Num_Trips', 'Trips') ||
      parseFloat(csvRow.num_trips || csvRow.Num_Trips || csvRow.trips || csvRow.Trips || '0') || 0;
    const miles = getRowNum(csvRow, 'miles', 'total_miles', 'Total_Miles', 'Miles') ||
      parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0') || 0;

    let milesEffPct = getRowNum(csvRow, 'miles_eff', 'Miles_Eff_Pct', 'mileage_efficiency_pct', 'Miles Eff %');
    let revEffPct = getRowNum(csvRow, 'rev_eff', 'Rev_Eff_Pct', 'revenue_efficiency_pct', 'Rev Eff %');
    let timeEffPct = getRowNum(csvRow, 'time_eff', 'Time_Eff_Pct', 'time_efficiency_pct', 'Time Eff %');
    let loadEffPct = getRowNum(csvRow, 'load_eff', 'Load_Eff_Pct', 'load_efficiency_pct', 'Load Eff %');
    const isTruthRow = isTruthUtilisationCsvRow(csvRow);
    if (variant === 'utilisation-only' && !isTruthRow) {
      const productiveHours = getRowNum(csvRow, 'productive_hours', 'productive hours') ||
        (getRowNum(csvRow, 'active_hours', 'active_time') + getRowNum(csvRow, 'idle_hours', 'idle_hours'));
      const hasRevenue = csvRow.revenue_gbp != null && String(csvRow.revenue_gbp).trim() !== '';
      const revenueGbp = hasRevenue ? getRowNum(csvRow, 'revenue_gbp', 'revenue') : -1;
      const avgLadenRaw = csvRow.avg_laden_percentage ?? csvRow.avg_laden_pct ?? csvRow.load;
      const avgLaden = avgLadenRaw != null && String(avgLadenRaw).trim() !== '' && String(avgLadenRaw) !== 'Unavailable'
        ? getRowNum(csvRow, 'avg_laden_percentage', 'avg_laden_pct', 'load') : -1;
      const csvTargetMiles = getRowNum(csvRow, 'target_mileage_miles', 'target_mileage');
      const csvTargetDrive = getRowNum(csvRow, 'target_driving_hours', 'target_driving');
      const csvTargetWait = getRowNum(csvRow, 'target_waiting_hours', 'target_waiting');
      const csvTargetRev = getRowNum(csvRow, 'target_revenue_gbp', 'target_revenue');
      const targets = getTargetsForVehicleType(vehicleType);
      const targetMiles = csvTargetMiles > 0 ? csvTargetMiles : targets.targetMiles;
      const targetTimeHrs = (csvTargetDrive > 0 || csvTargetWait > 0) ? (csvTargetDrive + csvTargetWait) : targets.targetTimeHrs;
      const targetRev = csvTargetRev > 0 ? csvTargetRev : targets.targetRevenue;
      if (miles >= 0 && targetMiles > 0) {
        milesEffPct = Math.min(100, (miles / targetMiles) * 100);
      }
      if (productiveHours > 0 && targetTimeHrs > 0) {
        timeEffPct = Math.min(100, (productiveHours / targetTimeHrs) * 100);
      }
      if (serviceType !== 'emergency_services' && hasRevenue && revenueGbp >= 0 && targetRev > 0) {
        revEffPct = Math.min(100, (revenueGbp / targetRev) * 100);
      }
      if (serviceType !== 'emergency_services' && avgLaden >= 0 && avgLaden <= 100) {
        loadEffPct = Math.min(100, avgLaden);
      }
    }

    const csvOverall = parseFloat(csvRow.utilisation_score || csvRow.utilisation_rate || csvRow.Utilisation_Rate || csvRow.utilization_rate || '0') ||
      getRowNum(csvRow, 'overall_util', 'Overall_Util_Pct', 'Overall Util %') || 0;
    const hasAnyComponent =
      serviceType === 'emergency_services'
        ? (milesEffPct > 0) || (timeEffPct > 0)
        : (milesEffPct > 0) || (revEffPct > 0) || (timeEffPct > 0) || (loadEffPct > 0);
    const overallUtilPct = isTruthRow
      ? (Number(csvRow.utilisation_score) || 0)
      : (variant === 'utilisation-only' && hasAnyComponent)
        ? computeWeightedOverallUtil(milesEffPct || 0, revEffPct || 0, timeEffPct || 0, loadEffPct || 0)
        : csvOverall;

    const iso = utilisationCsvRowIsoDate(csvRow);
    const vehicleStd = standardiseVehicleId(vehicleId);

    return {
      vehicleType,
      vehicleId,
      vehicleStd,
      iso,
      trips,
      miles,
      productiveHours: getProductiveHoursFromCsvRow(csvRow),
      overallUtilPct,
    };
  }, [variant, serviceType, utilisationWeights, editedUtilTargetsRows, getUtilisationTargetsTableRows, computeWeightedOverallUtil, getVehicleTypeFromRow, getTargetsForVehicleType]);

  /** Vehicle-level metrics for calendar/trend: same type & lookup filters as the table, but all dates (not clipped by period filter). */
  const utilisationMetricRowsVizScope = useMemo(() => {
    let filtered = selectedVehicleType
      ? vehicleLevelCSV.filter(csvRow => getVehicleTypeFromRow(csvRow).toLowerCase() === selectedVehicleType!.toLowerCase())
      : vehicleLevelCSV;
    if (selectedVehicleIds.length > 0) {
      const selectedSet = new Set(selectedVehicleIds.map(id => standardiseVehicleId(id).toLowerCase()));
      filtered = filtered.filter(csvRow => {
        const vid = csvRow.vehicle || csvRow.Vehicle_ID || csvRow.VehicleID || csvRow.vehicle_id || csvRow.Vehicle || '';
        const std = standardiseVehicleId(vid).toLowerCase();
        return std && selectedSet.has(std);
      });
    }
    return filtered.map(mapCsvRowToTableUtilisationMetrics).filter(m => m.iso);
  }, [vehicleLevelCSV, selectedVehicleType, selectedVehicleIds, mapCsvRowToTableUtilisationMetrics, getVehicleTypeFromRow]);

  /** Same rows as Vehicle Summary (period + type + lookup filters). */
  const utilisationMetricRowsTableScope = useMemo(() => {
    let filtered = selectedVehicleType
      ? vehicleLevelCSV.filter(csvRow => getVehicleTypeFromRow(csvRow).toLowerCase() === selectedVehicleType!.toLowerCase())
      : vehicleLevelCSV;
    if (utilisationFilterWindow) {
      filtered = filtered.filter(csvRow => {
        const iso = utilisationCsvRowIsoDate(csvRow);
        if (!iso) return false;
        return iso >= utilisationFilterWindow.startDate && iso <= utilisationFilterWindow.endDate;
      });
    }
    if (selectedVehicleIds.length > 0) {
      const selectedSet = new Set(selectedVehicleIds.map(id => standardiseVehicleId(id).toLowerCase()));
      filtered = filtered.filter(csvRow => {
        const vid = csvRow.vehicle || csvRow.Vehicle_ID || csvRow.VehicleID || csvRow.vehicle_id || csvRow.Vehicle || '';
        const std = standardiseVehicleId(vid).toLowerCase();
        return std && selectedSet.has(std);
      });
    }
    return filtered.map(mapCsvRowToTableUtilisationMetrics).filter(m => m.iso);
  }, [vehicleLevelCSV, selectedVehicleType, selectedVehicleIds, utilisationFilterWindow, mapCsvRowToTableUtilisationMetrics, getVehicleTypeFromRow]);

  const topDatesFull = useMemo(() => {
    if (variant === 'utilisation-only' && utilisationMetricRowsVizScope.length > 0) {
      const byDate = new Map<string, { sum: number; count: number; miles: number; trips: number; productiveHrs: number }>();
      utilisationMetricRowsVizScope.forEach(m => {
        if (!m.iso) return;
        const cur = byDate.get(m.iso) ?? { sum: 0, count: 0, miles: 0, trips: 0, productiveHrs: 0 };
        byDate.set(m.iso, {
          sum: cur.sum + m.overallUtilPct,
          count: cur.count + 1,
          miles: cur.miles + m.miles,
          trips: cur.trips + m.trips,
          productiveHrs: cur.productiveHrs + (m.productiveHours ?? 0),
        });
      });
      let list = Array.from(byDate.entries()).map(([date, v]) => ({
        date,
        value: v.count > 0 ? v.sum / v.count : 0,
        trips: v.trips,
        miles: v.miles,
        productiveHours: v.productiveHrs,
      }));
      if (utilisationFilterWindow) {
        list = list.filter(x => x.date >= utilisationFilterWindow.startDate && x.date <= utilisationFilterWindow.endDate);
      }
      return list
        .sort((a, b) => b.value - a.value)
        .map((d, i) => ({
          rank: i + 1,
          date: d.date,
          utilisationScore: d.value,
          trips: d.trips,
          miles: d.miles,
          productiveHours: d.productiveHours,
        }));
    }
    if (!timeSeriesData?.utilization?.daily?.length) return [];
    const daily = timeSeriesData.utilization.daily as Array<{ date: string; avg_utilization?: number; avg_utilisation?: number; utilisation_score?: number }>;
    const tripsByDate = new Map<string, number>();
    (timeSeriesData.trips?.daily ?? []).forEach((d: { date?: string; trips?: number }) => {
      const dateStr = (d.date || '').slice(0, 10);
      if (dateStr) tripsByDate.set(dateStr, (d as any).trips ?? 0);
    });
    const milesByDate = new Map<string, number>();
    (timeSeriesData.mileage?.daily ?? []).forEach((d: { date?: string; total_miles?: number }) => {
      const dateStr = (d.date || '').slice(0, 10);
      if (dateStr) milesByDate.set(dateStr, (d as any).total_miles ?? 0);
    });
    let list = daily.map(d => {
      const dateStr = (d.date || '').slice(0, 10);
      const value = d.utilisation_score ?? d.avg_utilization ?? d.avg_utilisation ?? 0;
      return { date: dateStr, value, trips: tripsByDate.get(dateStr) ?? 0, miles: milesByDate.get(dateStr) ?? 0 };
    }).filter(x => x.date);
    if (utilisationFilterWindow) {
      list = list.filter(x => x.date >= utilisationFilterWindow!.startDate && x.date <= utilisationFilterWindow!.endDate);
    }
    return list
      .sort((a, b) => b.value - a.value)
      .map((d, i) => ({
        rank: i + 1,
        date: d.date,
        utilisationScore: d.value,
        trips: d.trips,
        miles: d.miles,
        productiveHours: 0,
      }));
  }, [variant, utilisationMetricRowsVizScope, utilisationFilterWindow, timeSeriesData]);

  const topPerformersFull = useMemo(() => {
    if (variant === 'utilisation-only' && utilisationMetricRowsTableScope.length > 0) {
      const byVehicle = new Map<string, { scores: number[]; displayName: string; miles: number; trips: number; productiveHrs: number; dates: Set<string> }>();
      utilisationMetricRowsTableScope.forEach(m => {
        if (!m.vehicleStd) return;
        const displayName = vinToCustomerNameMap.get(m.vehicleStd) || vinToCustomerNameMap.get(m.vehicleStd.toLowerCase()) || m.vehicleId;
        const score = m.overallUtilPct;
        if (!byVehicle.has(m.vehicleStd)) {
          byVehicle.set(m.vehicleStd, { scores: [], displayName, miles: 0, trips: 0, productiveHrs: 0, dates: new Set() });
        }
        const rec = byVehicle.get(m.vehicleStd)!;
        rec.scores.push(score);
        rec.miles += m.miles;
        rec.trips += m.trips;
        rec.productiveHrs += m.productiveHours ?? 0;
        if (m.iso) rec.dates.add(m.iso);
      });
      return Array.from(byVehicle.entries())
        .map(([vehicleId, { scores, displayName, miles, trips, productiveHrs, dates }]) => {
          const avgScore = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
          const days = dates.size;
          return { vehicleId, displayName, days, score: avgScore, miles, trips, productiveHrs };
        })
        .filter(x => x.vehicleId && x.score > 0)
        .sort((a, b) => b.score - a.score)
        .map((x, i) => ({
          rank: i + 1,
          vehicle: redactVehicleId(x.displayName, isDemoMode),
          vehicleId: x.vehicleId,
          utilisationScore: x.score,
          activeDays: x.days,
          miles: x.miles,
          trips: x.trips,
          productiveHours: x.productiveHrs,
        }));
    }
    return [];
  }, [variant, utilisationMetricRowsTableScope, vinToCustomerNameMap, isDemoMode]);

  const topPerformersDisplayed = useMemo(() => {
    if (topPerformersFull.length === 0) return [];
    if (performersShowBottom5) {
      const slice = topPerformersFull.slice(-5).reverse();
      return slice.map((p, i) => ({ ...p, rank: i + 1 }));
    }
    return topPerformersFull.slice(0, 5);
  }, [topPerformersFull, performersShowBottom5]);

  const topDatesDisplayed = useMemo(() => {
    if (topDatesFull.length === 0) return [];
    if (bestDaysShowBottom5) {
      const slice = topDatesFull.slice(-5).reverse();
      return slice.map((d, i) => ({ ...d, rank: i + 1 }));
    }
    return topDatesFull.slice(0, 5);
  }, [topDatesFull, bestDaysShowBottom5]);

  // Get available stations from CSV data
  const getAvailableStations = (): string[] => {
    const stations = new Set<string>();
    
    if (stationLevelCSV.length > 0) {
      stationLevelCSV.forEach(row => {
        const station = row.Station_Name || row.Station || row['STN #'] || row.StationName || '';
        if (station) {
          stations.add(station);
        }
      });
    }
    
    if (stations.size === 0 && vehicleLevelCSV.length > 0) {
      vehicleLevelCSV.forEach(row => {
        const station = row.Station_Name || row.Station || row['STN #'] || row.StationName || '';
        if (station) {
          stations.add(station);
        }
      });
    }
    
    return Array.from(stations).sort();
  };

  // Get unique vehicle IDs for temporal patterns
  const getTemporalVehicleIds = () => {
    if (!data || !data.utilization_patterns) return [];
    
    let vehicleIds = Object.keys(data.utilization_patterns).sort();
    
    if (selectedVehicleTypeForTrips && vehicleLevelCSV.length > 0) {
      const vehiclesOfType = vehicleLevelCSV
        .filter(row => getVehicleTypeFromRow(row).toLowerCase() === selectedVehicleTypeForTrips.toLowerCase())
        .map(row => row.vehicle || row.Vehicle_ID || row.VehicleID || row.vehicle_id || '')
        .filter(Boolean);
      
      vehicleIds = vehicleIds.filter(id => vehiclesOfType.includes(id));
    }
    
    if (selectedStationForTrips && vehicleLevelCSV.length > 0) {
      const vehiclesAtStation = vehicleLevelCSV
        .filter(row => {
          const rowStation = row.Station_Name || row.Station || row['STN #'] || row.StationName || '';
          return rowStation.toLowerCase() === selectedStationForTrips.toLowerCase();
        })
        .map(row => row.vehicle || row.Vehicle_ID || row.VehicleID || row.vehicle_id || '')
        .filter(Boolean);
      
      vehicleIds = vehicleIds.filter(id => vehiclesAtStation.includes(id));
    }
    
    return vehicleIds;
  };

  // Get score color for utilization metrics (red/yellow/green gradient)
  const getScoreColor = (score: number) => {
    if (score <= 50) {
      const ratio = score / 50;
      const r = 255;
      const g = Math.round(ratio * 255);
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      const ratio = (score - 50) / 50;
      const r = Math.round(255 * (1 - ratio));
      const g = 255;
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  // Fleet-wide utilisation: only red, amber, green. Green ≥70%, Amber 40–69%, Red 0–39%.
  const getCalendarUtilColor = (utilPct: number | null): { bg: string; text: string } => {
    if (utilPct == null || utilPct <= 0) return { bg: 'bg-secondary/30', text: 'text-muted-foreground' };
    if (utilPct >= 70) return { bg: 'bg-success/50', text: 'text-success' };
    if (utilPct >= 40) return { bg: 'bg-warning/50', text: 'text-warning' };
    return { bg: 'bg-destructive/50', text: 'text-destructive' };
  };

  // Color for Overall Util % progress bar in tables: Green 70–100%, Amber 40–69%, Red 0–39%.
  const getUtilisationBarColor = (pct: number): string => {
    if (pct >= 70) return STATUS_COLORS.success;
    if (pct >= 40) return STATUS_COLORS.warning;
    return STATUS_COLORS.destructive;
  };

  // Get detailed vehicle data with utilisation metrics (Vehicle, productive hrs / trips, miles, efficiency %)
  // When a calendar month is selected (effectiveDateWindow), only rows within that date range are shown.
  const getDetailedVehicleData = () => {
    let filteredData = selectedVehicleType
      ? vehicleLevelCSV.filter(csvRow => getVehicleTypeFromRow(csvRow).toLowerCase() === selectedVehicleType!.toLowerCase())
      : vehicleLevelCSV;
    
    if (variant === 'utilisation-only' ? utilisationFilterWindow : effectiveDateWindow) {
      const dateWindow = variant === 'utilisation-only' ? utilisationFilterWindow : effectiveDateWindow;
      filteredData = filteredData.filter(csvRow => {
        const iso = utilisationCsvRowIsoDate(csvRow);
        if (!iso) return false;
        return iso >= dateWindow!.startDate && iso <= dateWindow!.endDate;
      });
    }

    if (selectedVehicleIds.length > 0) {
      const selectedSet = new Set(selectedVehicleIds.map(id => id.toLowerCase()));
      filteredData = filteredData.filter(csvRow => {
        const vid = csvRow.vehicle || csvRow.Vehicle_ID || csvRow.VehicleID || csvRow.vehicle_id || csvRow.Vehicle || '';
        const std = standardiseVehicleId(vid).toLowerCase();
        return std && selectedSet.has(std);
      });
    }

    return filteredData.map(csvRow => {
      const vehicleType = getVehicleTypeFromRow(csvRow);
      const isSelectedType = selectedVehicleType && vehicleType.toLowerCase() === selectedVehicleType.toLowerCase();
      const vehicleId = csvRow.vehicle || csvRow.Vehicle_ID || csvRow.VehicleID || csvRow.vehicle_id || csvRow.Vehicle || '';

      const metrics = mapCsvRowToTableUtilisationMetrics(csvRow);
      const trips = metrics.trips;
      const miles = metrics.miles;
      const productiveHours = metrics.productiveHours ?? getProductiveHoursFromCsvRow(csvRow);

      let _productiveHours: number | undefined;
      let _revenueGbp: number | undefined;
      let _avgLaden: number | undefined;
      let _targetMiles: number | undefined;
      let _targetRevenue: number | undefined;
      let _targetTimeHrs: number | undefined;

      let milesEffPct = getRowNum(csvRow, 'miles_eff', 'Miles_Eff_Pct', 'mileage_efficiency_pct', 'Miles Eff %');
      let revEffPct = getRowNum(csvRow, 'rev_eff', 'Rev_Eff_Pct', 'revenue_efficiency_pct', 'Rev Eff %');
      let timeEffPct = getRowNum(csvRow, 'time_eff', 'Time_Eff_Pct', 'time_efficiency_pct', 'Time Eff %');
      let loadEffPct = getRowNum(csvRow, 'load_eff', 'Load_Eff_Pct', 'load_efficiency_pct', 'Load Eff %');
      const isTruthRow = isTruthUtilisationCsvRow(csvRow);
      if (variant === 'utilisation-only' && !isTruthRow) {
        const hasRevenue = csvRow.revenue_gbp != null && String(csvRow.revenue_gbp).trim() !== '';
        const revenueGbp = hasRevenue ? getRowNum(csvRow, 'revenue_gbp', 'revenue') : -1;
        const avgLadenRaw = csvRow.avg_laden_percentage ?? csvRow.avg_laden_pct ?? csvRow.load;
        const avgLaden = avgLadenRaw != null && String(avgLadenRaw).trim() !== '' && String(avgLadenRaw) !== 'Unavailable'
          ? getRowNum(csvRow, 'avg_laden_percentage', 'avg_laden_pct', 'load') : -1;
        _productiveHours = productiveHours > 0 ? productiveHours : undefined;
        _revenueGbp = hasRevenue && revenueGbp >= 0 ? revenueGbp : undefined;
        _avgLaden = avgLaden >= 0 && avgLaden <= 100 ? avgLaden : undefined;
        const csvTargetMiles = getRowNum(csvRow, 'target_mileage_miles', 'target_mileage');
        const csvTargetDrive = getRowNum(csvRow, 'target_driving_hours', 'target_driving');
        const csvTargetWait = getRowNum(csvRow, 'target_waiting_hours', 'target_waiting');
        const csvTargetRev = getRowNum(csvRow, 'target_revenue_gbp', 'target_revenue');
        const targets = getTargetsForVehicleType(vehicleType);
        const targetMiles = csvTargetMiles > 0 ? csvTargetMiles : targets.targetMiles;
        const targetTimeHrs = (csvTargetDrive > 0 || csvTargetWait > 0) ? (csvTargetDrive + csvTargetWait) : targets.targetTimeHrs;
        const targetRev = csvTargetRev > 0 ? csvTargetRev : targets.targetRevenue;
        _targetMiles = targetMiles;
        _targetRevenue = targetRev;
        _targetTimeHrs = targetTimeHrs;
        if (miles >= 0 && targetMiles > 0) {
          milesEffPct = Math.min(100, (miles / targetMiles) * 100);
        }
        if (productiveHours > 0 && targetTimeHrs > 0) {
          timeEffPct = Math.min(100, (productiveHours / targetTimeHrs) * 100);
        }
        if (serviceType !== 'emergency_services' && hasRevenue && revenueGbp >= 0 && targetRev > 0) {
          revEffPct = Math.min(100, (revenueGbp / targetRev) * 100);
        }
        if (serviceType !== 'emergency_services' && avgLaden >= 0 && avgLaden <= 100) {
          loadEffPct = Math.min(100, avgLaden);
        }
      } else if (productiveHours > 0) {
        _productiveHours = productiveHours;
      }

      const overallUtilPct = metrics.overallUtilPct;

      const normalizedDate = normalizeUtilisationCsvDateForRow(csvRow);
      
      const rowId = `${vehicleId}|${normalizedDate.export}`;
      const esExtras =
        serviceType === 'emergency_services'
          ? {}
          : {
              rev_eff_pct: revEffPct > 0 ? `${formatNumber(revEffPct, 1)}%` : '—',
              load_eff_pct: loadEffPct > 0 ? `${formatNumber(loadEffPct, 1)}%` : '—',
            };
      return {
        _rowId: rowId,
        date: normalizedDate.display,
        date_sortKey: normalizedDate.sortKey,
        _dateExport: normalizedDate.export,
        _vehicleType: vehicleType,
        vehicle_id: (
          <div className="leading-tight">
            <div className="whitespace-nowrap">{redactVehicleId(vehicleId, isDemoMode)}</div>
            <div className={`text-xs whitespace-nowrap ${isSelectedType ? 'text-warning drop-shadow-glow-warning-lg' : 'text-muted-foreground'}`}>
              {redactVehicleType(vehicleType, isDemoMode)}
            </div>
          </div>
        ),
        vehicle_id_sortKey: vehicleId,
        ...(serviceType === 'emergency_services'
          ? { trips: formatCount(trips) }
          : {
              productive_hrs: productiveHours > 0 ? formatHours(productiveHours) : '—',
              productive_hrs_sortKey: productiveHours,
            }),
        miles: formatMiles(miles),
        miles_eff_pct: milesEffPct > 0 ? `${formatNumber(milesEffPct, 1)}%` : '—',
        ...esExtras,
        time_eff_pct: timeEffPct > 0 ? `${formatNumber(timeEffPct, 1)}%` : '—',
        overall_util_pct: overallUtilPct > 0 ? `${formatNumber(overallUtilPct, 1)}%` : '—',
        overall_util_pct_sortKey: overallUtilPct,
        _overallUtilValue: overallUtilPct,
        _overallUtilColor: getUtilisationBarColor(overallUtilPct),
        _overallUtilExport: overallUtilPct > 0 ? `${formatNumber(overallUtilPct, 1)}%` : '—',
        _milesEffValue: milesEffPct || 0,
        _revEffValue: revEffPct || 0,
        _timeEffValue: timeEffPct || 0,
        _loadEffValue: loadEffPct || 0,
        _milesDone: miles,
        _tripsValue: trips,
        _productiveHours,
        _revenueGbp,
        _avgLaden,
        ...(variant === 'utilisation-only' ? {
          _targetMiles,
          _targetRevenue,
          _targetTimeHrs,
        } : {}),
      };
    });
  };

  // Get vehicle types with custom ordering (use same row accessor as Lookup filter)
  const getVehicleTypes = () => {
    const types = new Set<string>();
    vehicleLevelCSV.forEach(row => {
      const t = getVehicleTypeFromRow(row);
      if (t) types.add(t);
    });
    const typeArray = Array.from(types);
    
    const esOrder = ['Response car', 'Small van', 'Large van', 'Pumping appliance', 'Other appliance'];
    const logisticsOrder = ['Electric Truck', 'Diesel Truck', 'Electric', 'Diesel', 'Truck', 'Van'];
    
    const customOrder = serviceType === 'emergency_services' ? esOrder : logisticsOrder;
    
    return typeArray.sort((a, b) => {
      const findIndex = (vehicleType: string) => {
        const lowerType = vehicleType.toLowerCase();
        return customOrder.findIndex(order => order.toLowerCase() === lowerType);
      };
      
      const indexA = findIndex(a);
      const indexB = findIndex(b);
      
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  };

  // Get columns for individual vehicle table (Utilisation Metrics – new S3 output)
  const getIndividualVehicleColumns = () => {
    const base = [
      { key: 'date', label: 'Date' },
      { key: 'vehicle_id', label: 'Vehicle' },
      utilisationVolumeColumn,
      { key: 'miles', label: 'Miles', align: 'right' as const },
      { key: 'miles_eff_pct', label: 'Miles Eff %', align: 'right' as const },
    ];
    const logisticsCols = [
      { key: 'rev_eff_pct', label: 'Rev Eff %', align: 'right' as const },
      { key: 'time_eff_pct', label: 'Time Eff %', align: 'right' as const },
      { key: 'load_eff_pct', label: 'Load Eff %', align: 'right' as const },
    ];
    const esCols = [{ key: 'time_eff_pct', label: 'Time Eff %', align: 'right' as const }];
    const tail = [{ key: 'overall_util_pct', label: 'Overall Util %', align: 'right' as const }];
    return [
      ...base,
      ...(serviceType === 'emergency_services' ? esCols : logisticsCols),
      ...tail,
    ];
  };

  // Get columns for aggregated vehicle type table (same as vehicle-level: Overall Util % includes status)
  const getAggregatedVehicleColumns = () => {
    const base = [
      { key: 'date', label: 'Date' },
      { key: 'vehicle_type', label: 'Type' },
      utilisationVolumeColumn,
      { key: 'miles', label: 'Miles', align: 'right' as const },
      { key: 'miles_eff_pct', label: 'Miles Eff %', align: 'right' as const },
    ];
    const logisticsCols = [
      { key: 'rev_eff_pct', label: 'Rev Eff %', align: 'right' as const },
      { key: 'time_eff_pct', label: 'Time Eff %', align: 'right' as const },
      { key: 'load_eff_pct', label: 'Load Eff %', align: 'right' as const },
    ];
    const esCols = [{ key: 'time_eff_pct', label: 'Time Eff %', align: 'right' as const }];
    const tail = [{ key: 'overall_util_pct', label: 'Overall Util %', align: 'right' as const }];
    return [
      ...base,
      ...(serviceType === 'emergency_services' ? esCols : logisticsCols),
      ...tail,
    ];
  };

  // Get station summary columns
  const getStationSummaryColumns = () => {
    return [
      { key: 'date', label: 'Date' },
      { key: 'station_name', label: 'Station' },
      { key: 'vehicles', label: 'Vehicles', align: 'right' as const },
      { key: 'utilisation_rate', label: 'Utilisation %', align: 'right' as const },
      { key: 'trips_per_day', label: 'Trips/Day', align: 'right' as const },
      { key: 'miles_per_day', label: 'Miles/Day', align: 'right' as const },
      { key: 'total_miles', label: 'Miles', align: 'right' as const },
      { key: 'active_hours', label: 'Active Hrs', align: 'right' as const },
      { key: 'downtime_hours', label: 'Downtime Hrs', align: 'right' as const },
    ];
  };

  // Get station summary data
  const getStationSummaryData = (): Record<string, any>[] => {
    if (serviceType !== 'emergency_services') return [];

    if (stationLevelCSV.length > 0) {
      let sourceRows = stationLevelCSV;
      if (effectiveDateWindow) {
        sourceRows = stationLevelCSV.filter(csvRow => {
          const nd = normalizeUtilisationCsvDateForRow(csvRow);
          if (!nd.export) return false;
          return nd.export >= effectiveDateWindow.startDate && nd.export <= effectiveDateWindow.endDate;
        });
      }
      return sourceRows.map(csvRow => {
        const normalizedDate = normalizeUtilisationCsvDateForRow(csvRow);
        const utilisationRate = parseFloat(csvRow.utilisation_rate || '0');
        const utilisationColor = getScoreColor(utilisationRate);
        const stationName = csvRow.Station_Name || csvRow.Station || csvRow['STN #'] || csvRow.StationName || '';

        const getVehicleCount = () => {
          const vehiclesFromCSV = parseFloat(
            String(
              csvRow.vehicles_count ??
                csvRow.Vehicles_Count ??
                csvRow.vehicles_dispatched ??
                csvRow.vehicle_dispatched ??
                csvRow.Vehicles_Dispatched ??
                csvRow.Vehicle_Dispatched ??
                csvRow.vehicles ??
                csvRow.Vehicles ??
                '0'
            )
          );
          
          if (vehiclesFromCSV > 0) {
            return vehiclesFromCSV;
          }
          
          if (vehicleLevelCSV.length > 0 && stationName) {
            const vehiclesAtStation = vehicleLevelCSV.filter(row => {
              const rowStation = row.Station_Name || row.Station || row['STN #'] || row.StationName || '';
              return rowStation.toLowerCase() === stationName.toLowerCase();
            });
            return vehiclesAtStation.length;
          }
          
          return 0;
        };
        
        const vehicleCount = getVehicleCount();
        
        const getDays = () => {
          const daysFromCSV = parseFloat(
            csvRow.total_days || csvRow.Total_Days || csvRow.days || csvRow.Days || '0'
          );
          
          if (daysFromCSV > 0) {
            return daysFromCSV;
          }

          // ES utilisation_station_level_{period}.csv is one row per calendar day × station (no total_days column).
          if (normalizeUtilisationCsvDateForRow(csvRow).export) {
            return 1;
          }
          
          if (vehicleLevelCSV.length > 0 && stationName) {
            const vehiclesAtStation = vehicleLevelCSV.filter(row => {
              const rowStation = row.Station_Name || row.Station || row['STN #'] || row.StationName || '';
              return rowStation.toLowerCase() === stationName.toLowerCase();
            });
            
            if (vehiclesAtStation.length > 0) {
              const totalDays = vehiclesAtStation.reduce((sum, row) => {
                return sum + parseFloat(row.total_days || row.Total_Days || row.days || row.Days || '0');
              }, 0);
              return totalDays / vehiclesAtStation.length;
            }
          }
          
          return 0;
        };
        
        const days = getDays();
        
        const trips = parseFloat(csvRow.num_trips || csvRow.Num_Trips || csvRow.trips || csvRow.Trips || '0');
        const tripsPerDay = days > 0 && vehicleCount > 0
          ? (trips / vehicleCount) / days
          : 0;

        const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0');
        const milesPerDay = days > 0 && vehicleCount > 0
          ? (miles / vehicleCount) / days
          : 0;

        return {
          date: normalizedDate.display,
          date_sortKey: normalizedDate.sortKey,
          _dateExport: normalizedDate.export,
          station_name: redactStationName(stationName, isDemoMode),
          _station_sortKey: stationName,
          total_days: formatTotalDays(days),
          vehicles: formatCount(vehicleCount.toString()),
          num_trips: formatTotalDays(trips),
          trips_per_day: formatNumber(tripsPerDay, 1),
          total_miles: formatMiles(miles),
          miles_per_day: formatMiles(milesPerDay),
          utilisation_rate: formatPercentage(utilisationRate),
          active_hours: formatHours(csvRow.active_hours || '0'),
          downtime_hours: formatHours(csvRow.downtime_hours || '0'),
          _utilisationRateValue: utilisationRate,
          _utilisationRateColor: utilisationColor,
        };
      }).sort((a, b) =>
        (a.date_sortKey as number) - (b.date_sortKey as number) ||
        String(a._station_sortKey).localeCompare(String(b._station_sortKey))
      );
    }

    if (vehicleLevelCSV.length === 0) return [];

    const firstRow = vehicleLevelCSV[0];
    let hasStationColumn = false;
    let stationColumnName = '';
    
    for (const key in firstRow) {
      const upperKey = key.toUpperCase().trim();
      if (upperKey === 'STATION_NAME' || upperKey === 'STATIONNAME' || 
          upperKey === 'STATION' || upperKey === 'STN #' || upperKey === 'STN') {
        hasStationColumn = true;
        stationColumnName = key;
        break;
      }
    }

    if (!hasStationColumn) return [];

    const stationMap = new Map<string, {
      vehicles: number;
      totalDays: number[];
      totalTrips: number;
      totalMiles: number;
      totalActiveHours: number;
      totalDowntimeHours: number;
      utilisationRates: number[];
    }>();

    const hasDateColumn = vehicleLevelCSV.some((row: Record<string, any>) => !!utilisationCsvRowIsoDate(row));
    const rowsToAggregate = kpiDateWindow && hasDateColumn
      ? vehicleLevelCSV.filter((row: Record<string, any>) => {
          const iso = utilisationCsvRowIsoDate(row);
          return !!iso && iso >= kpiDateWindow!.startDate && iso <= kpiDateWindow!.endDate;
        })
      : vehicleLevelCSV;

    rowsToAggregate.forEach(row => {
      let station = '';
      if (stationColumnName && row[stationColumnName]) {
        station = row[stationColumnName].trim();
      } else {
        for (const key in row) {
          const upperKey = key.toUpperCase().trim();
          if (upperKey === 'STATION_NAME' || upperKey === 'STATIONNAME' || 
              upperKey === 'STATION' || upperKey === 'STN #' || upperKey === 'STN') {
            station = (row[key] || '').trim();
            break;
          }
        }
      }

      if (!station) return;

      if (!stationMap.has(station)) {
        stationMap.set(station, {
          vehicles: 0,
          totalDays: [],
          totalTrips: 0,
          totalMiles: 0,
          totalActiveHours: 0,
          totalDowntimeHours: 0,
          utilisationRates: [],
        });
      }

      const stationData = stationMap.get(station)!;
      stationData.vehicles += 1;

      const days = parseFloat(
        row.total_days || row.Total_Days || row.days || row.Days || '0'
      );
      const trips = parseFloat(
        row.num_trips || row.Num_Trips || row.trips || row.Trips || '0'
      );
      // Avoid 0 days with non-zero trips: treat rows with trips but 0 days as 1 active day
      if (days > 0) {
        stationData.totalDays.push(days);
      } else if (trips > 0) {
        stationData.totalDays.push(1);
      }

      stationData.totalTrips += trips;

      const miles = parseFloat(
        row.total_miles || row.Total_Miles || row.miles || row.Miles || '0'
      );
      stationData.totalMiles += miles;

      const activeHours = parseFloat(row.active_hours || '0');
      stationData.totalActiveHours += activeHours;

      const downtimeHours = parseFloat(row.downtime_hours || '0');
      stationData.totalDowntimeHours += downtimeHours;

      const utilisationRate = parseFloat(row.utilisation_rate || '0');
      if (utilisationRate > 0) {
        stationData.utilisationRates.push(utilisationRate);
      }
    });

    type StationSummaryRow = {
      vehicles: number;
      totalDays: number[];
      totalTrips: number;
      totalMiles: number;
      totalActiveHours: number;
      totalDowntimeHours: number;
      utilisationRates: number[];
    };
    return Array.from(stationMap.entries())
      .map(([stationName, data]: [string, StationSummaryRow]) => {
        const avgDays = data.totalDays.length > 0
          ? data.totalDays.reduce((sum: number, d: number) => sum + d, 0) / data.totalDays.length
          : 0;
        
        const avgUtilisationRate = data.utilisationRates.length > 0
          ? data.utilisationRates.reduce((sum: number, r: number) => sum + r, 0) / data.utilisationRates.length
          : 0;

        const utilisationColor = getScoreColor(avgUtilisationRate);

        const tripsPerDay = avgDays > 0 && data.vehicles > 0
          ? (data.totalTrips / data.vehicles) / avgDays
          : 0;

        const milesPerDay = avgDays > 0 && data.vehicles > 0
          ? (data.totalMiles / data.vehicles) / avgDays
          : 0;

        return {
          date: '—',
          date_sortKey: 0,
          _dateExport: '',
          station_name: redactStationName(stationName, isDemoMode),
          _station_sortKey: stationName,
          total_days: formatTotalDays(avgDays),
          vehicles: formatCount(data.vehicles),
          num_trips: formatTotalDays(data.totalTrips),
          trips_per_day: formatNumber(tripsPerDay, 1),
          total_miles: formatMiles(data.totalMiles),
          miles_per_day: formatMiles(milesPerDay),
          utilisation_rate: formatPercentage(avgUtilisationRate),
          active_hours: formatHours(data.totalActiveHours),
          downtime_hours: formatHours(data.totalDowntimeHours),
          _utilisationRateValue: avgUtilisationRate,
          _utilisationRateColor: utilisationColor,
        };
      })
      .sort((a: { _station_sortKey: string }, b: { _station_sortKey: string }) => a._station_sortKey.localeCompare(b._station_sortKey));
  };

  // Get station performance columns
  const getStationPerformanceColumns = () => {
    return [
      { key: 'station', label: 'Station' },
      { key: 'performance_rank', label: 'Busy Rank', align: 'right' as const },
      { key: 'trips_per_day', label: 'Average Trips/Day', align: 'right' as const },
      { key: 'performance_score', label: 'Average Trips/Hour', align: 'right' as const },
      { key: 'downtime_median', label: 'Median Downtime (hours/day)', align: 'right' as const },
    ];
  };

  // Get station performance data
  const getStationPerformanceData = () => {
    if (busyStationsCSV.length === 0 || serviceType !== 'emergency_services') {
      return [];
    }

    const stationScores = busyStationsCSV.map(row => {
      const station = row.Station || '';
      const performanceScoreMedian = parseFloat(
        row['Busy Score_Q2'] || row['Busy Score(Q2)'] || row['BusyScore_Q2'] || '0'
      );
      return { station, score: performanceScoreMedian };
    });

    const sortedByScore = [...stationScores].sort((a, b) => b.score - a.score);
    
    const denseRankMap = new Map<string, number>();
    let currentRank = 1;
    let previousScore: number | null = null;
    
    for (const { station, score } of sortedByScore) {
      if (previousScore === null || score !== previousScore) {
        if (previousScore !== null) {
          currentRank++;
        }
        previousScore = score;
      }
      denseRankMap.set(station, currentRank);
    }

    return busyStationsCSV.map(row => {
      const station = row.Station || '';
      const redactedStation = redactStationName(station, isDemoMode);
      
      const tripsPerDay = parseFloat(
        row['Trip Density (trips/day)_tripdensity'] ||
        row['Trip Density trips/day_tripdensity'] ||
        row['Trip Density (trips/day)_trip_density'] || 
        row['Trip Density (trips/day)'] ||
        row['Trip Density_tripdensity'] ||
        '0'
      );
      
      const downtimeMedian = parseFloat(
        row['Downtime (hours)_Q2'] ||
        row['Downtime hours_Q2'] ||
        row['Downtime (hours)(Q2)'] ||
        row['Downtime_Q2'] ||
        '0'
      );
      
      const performanceScoreMedian = parseFloat(
        row['Busy Score_Q2'] ||
        row['Busy Score(Q2)'] ||
        row['BusyScore_Q2'] ||
        '0'
      );
      
      const denseRank = denseRankMap.get(station) || 0;

      const getStationDays = () => {
        if (stationLevelCSV.length > 0 && station) {
          const stationRow = stationLevelCSV.find(row => {
            const rowStation = row.Station_Name || row.Station || row['STN #'] || row.StationName || '';
            return rowStation.toLowerCase() === station.toLowerCase();
          });
          
          if (stationRow) {
            const days = parseFloat(
              stationRow.total_days || stationRow.Total_Days || stationRow.days || stationRow.Days || '0'
            );
            if (days > 0) {
              return days;
            }
          }
        }
        
        if (vehicleLevelCSV.length > 0 && station) {
          const vehiclesAtStation = vehicleLevelCSV.filter(row => {
            const rowStation = row.Station_Name || row.Station || row['STN #'] || row.StationName || '';
            return rowStation.toLowerCase() === station.toLowerCase();
          });
          
          if (vehiclesAtStation.length > 0) {
            const totalDays = vehiclesAtStation.reduce((sum, row) => {
              return sum + parseFloat(row.total_days || row.Total_Days || row.days || row.Days || '0');
            }, 0);
            const avgDays = totalDays / vehiclesAtStation.length;
            if (avgDays > 0) {
              return avgDays;
            }
          }
        }
        
        return 30;
      };
      
      const stationDays = getStationDays();
      const downtimeMedianPerDay = stationDays > 0 ? downtimeMedian / stationDays : downtimeMedian;

      const maxDenseRank = Math.max(...Array.from(denseRankMap.values()));
      const minDenseRank = 1;
      
      const rankScore = denseRank > 0 && maxDenseRank > minDenseRank
        ? 100 - ((denseRank - minDenseRank) / (maxDenseRank - minDenseRank)) * 100
        : 0;
      const rankColor = getScoreColor(rankScore);

      return {
        station: redactedStation,
        _station_sortKey: station,
        trips_per_day: formatNumber(tripsPerDay, 2),
        downtime_median: formatNumber(downtimeMedianPerDay, 2),
        performance_score: formatNumber(performanceScoreMedian, 2),
        performance_rank: denseRank.toString(),
        _performanceScoreValue: performanceScoreMedian,
        _performanceRankValue: denseRank,
        _rankValue: rankScore,
        _rankColor: rankColor,
      };
    }).sort((a, b) => a._performanceRankValue - b._performanceRankValue);
  };

  // Get aggregated vehicle data (Vehicle Type Summary – same column set as vehicle-level)
  // On Utilisation tab, derive from vehicle-level data so Customise targets apply to both tables
  const getAggregatedVehicleData = () => {
    if (variant === 'utilisation-only' && vehicleLevelCSV.length > 0) {
      const detailed = getDetailedVehicleData();
      const byKey = new Map<string, {
        date: string;
        dateSortKey: number;
        type: string;
        trips: number;
        productiveHrs: number;
        miles: number;
        scores: number[];
        m: number[];
        r: number[];
        t: number[];
        l: number[];
      }>();
      detailed.forEach(row => {
        const key = `${(row as any)._dateExport}|${(row as any)._vehicleType}`;
        const r = row as any;
        if (!byKey.has(key)) {
          byKey.set(key, {
            date: r.date,
            dateSortKey: r.date_sortKey ?? 0,
            type: r._vehicleType ?? '',
            trips: 0,
            productiveHrs: 0,
            miles: 0,
            scores: [],
            m: [],
            r: [],
            t: [],
            l: [],
          });
        }
        const agg = byKey.get(key)!;
        agg.trips += (r as any)._tripsValue ?? (parseFloat(String(r.trips).replace(/,/g, '')) || 0);
        agg.productiveHrs += (r as any)._productiveHours ?? r.productive_hrs_sortKey ?? 0;
        agg.miles += (r as any)._milesDone ?? (parseFloat(String(r.miles).replace(/,/g, '')) || 0);
        if (r._overallUtilValue != null && Number.isFinite(r._overallUtilValue)) {
          agg.scores.push(r._overallUtilValue);
        }
        if (r._milesEffValue != null) agg.m.push(r._milesEffValue);
        if (r._revEffValue != null) agg.r.push(r._revEffValue);
        if (r._timeEffValue != null) agg.t.push(r._timeEffValue);
        if (r._loadEffValue != null) agg.l.push(r._loadEffValue);
      });
      return Array.from(byKey.values()).map(agg => {
        const milesEffPct = agg.m.length ? agg.m.reduce((a, b) => a + b, 0) / agg.m.length : 0;
        const revEffPct = agg.r.length ? agg.r.reduce((a, b) => a + b, 0) / agg.r.length : 0;
        const timeEffPct = agg.t.length ? agg.t.reduce((a, b) => a + b, 0) / agg.t.length : 0;
        const loadEffPct = agg.l.length ? agg.l.reduce((a, b) => a + b, 0) / agg.l.length : 0;
        const overallUtilPct = agg.scores.length
          ? agg.scores.reduce((a, b) => a + b, 0) / agg.scores.length
          : 0;
        const isSelectedType = selectedVehicleType && agg.type.toLowerCase() === selectedVehicleType.toLowerCase();
        const esAggExtras =
          serviceType === 'emergency_services'
            ? {}
            : {
                rev_eff_pct: revEffPct > 0 ? `${formatNumber(revEffPct, 1)}%` : '—',
                load_eff_pct: loadEffPct > 0 ? `${formatNumber(loadEffPct, 1)}%` : '—',
              };
        return {
          date: agg.date,
          date_sortKey: agg.dateSortKey,
          _dateExport: agg.date,
          vehicle_type: (
            <span className={isSelectedType ? 'text-warning drop-shadow-glow-warning-lg' : ''}>
              {redactVehicleType(agg.type, isDemoMode)}
            </span>
          ),
          _vehicle_type_sortKey: agg.type,
          ...(serviceType === 'emergency_services'
            ? { trips: formatCount(agg.trips) }
            : {
                productive_hrs: agg.productiveHrs > 0 ? formatHours(agg.productiveHrs) : '—',
                productive_hrs_sortKey: agg.productiveHrs,
              }),
          miles: formatMiles(agg.miles),
          miles_eff_pct: milesEffPct > 0 ? `${formatNumber(milesEffPct, 1)}%` : '—',
          ...esAggExtras,
          time_eff_pct: timeEffPct > 0 ? `${formatNumber(timeEffPct, 1)}%` : '—',
          overall_util_pct: overallUtilPct > 0 ? `${formatNumber(overallUtilPct, 1)}%` : '—',
          overall_util_pct_sortKey: overallUtilPct,
          _overallUtilValue: overallUtilPct,
          _overallUtilColor: getUtilisationBarColor(overallUtilPct),
          _overallUtilExport: overallUtilPct > 0 ? `${formatNumber(overallUtilPct, 1)}%` : '—',
        };
      }).sort((a, b) => (a.date_sortKey as number) - (b.date_sortKey as number) || String(a._vehicle_type_sortKey).localeCompare(String(b._vehicle_type_sortKey)));
    }

    let source = vehicleTypeLevelCSV;
    if (effectiveDateWindow) {
      source = source.filter(csvRow => {
        const iso = utilisationCsvRowIsoDate(csvRow);
        if (!iso) return false;
        return iso >= effectiveDateWindow.startDate && iso <= effectiveDateWindow.endDate;
      });
    }
    return source.map(csvRow => {
      const vehicleType = getVehicleTypeFromRow(csvRow);
      const isSelectedType = selectedVehicleType && vehicleType.toLowerCase() === selectedVehicleType.toLowerCase();

      const milesEffPct = parseFloat(csvRow.mileage_efficiency_pct || csvRow.Miles_Eff_Pct || csvRow['Miles Eff %'] || '0') || 0;
      const revEffPct = parseFloat(csvRow.revenue_efficiency_pct || csvRow.Rev_Eff_Pct || csvRow['Rev Eff %'] || '0') || 0;
      const timeEffPct = parseFloat(csvRow.time_efficiency_pct || csvRow.Time_Eff_Pct || csvRow['Time Eff %'] || '0') || 0;
      const loadEffPct = parseFloat(csvRow.load_efficiency_pct || csvRow.Load_Eff_Pct || csvRow['Load Eff %'] || '0') || 0;
      const csvOverall = parseFloat(csvRow.utilisation_score || csvRow.utilisation_rate || csvRow.Utilisation_Rate || csvRow.utilization_rate || '0') || 0;
      const hasAnyComponent =
        serviceType === 'emergency_services'
          ? (milesEffPct > 0) || (timeEffPct > 0)
          : (milesEffPct > 0) || (revEffPct > 0) || (timeEffPct > 0) || (loadEffPct > 0);
      const overallUtilPct = (variant === 'utilisation-only' && hasAnyComponent)
        ? computeWeightedOverallUtil(milesEffPct, revEffPct, timeEffPct, loadEffPct)
        : csvOverall;
      const trips = parseFloat(csvRow.num_trips || csvRow.Num_Trips || csvRow.trips || csvRow.Trips || '0') || 0;
      const miles = parseFloat(csvRow.total_miles || csvRow.Total_Miles || csvRow.miles || csvRow.Miles || '0') || 0;
      const productiveHrs = getProductiveHoursFromCsvRow(csvRow);

      const normalizedDate = normalizeUtilisationCsvDateForRow(csvRow);

      const esTypeExtras =
        serviceType === 'emergency_services'
          ? {}
          : {
              rev_eff_pct: revEffPct > 0 ? `${formatNumber(revEffPct, 1)}%` : '—',
              load_eff_pct: loadEffPct > 0 ? `${formatNumber(loadEffPct, 1)}%` : '—',
            };

      return {
        date: normalizedDate.display,
        date_sortKey: normalizedDate.sortKey,
        _dateExport: normalizedDate.export,
        vehicle_type: (
          <span className={isSelectedType ? 'text-warning drop-shadow-glow-warning-lg' : ''}>
            {redactVehicleType(vehicleType, isDemoMode)}
          </span>
        ),
        _vehicle_type_sortKey: vehicleType,
        ...(serviceType === 'emergency_services'
          ? { trips: formatCount(trips) }
          : {
              productive_hrs: productiveHrs > 0 ? formatHours(productiveHrs) : '—',
              productive_hrs_sortKey: productiveHrs,
            }),
        miles: formatMiles(miles),
        miles_eff_pct: milesEffPct > 0 ? `${formatNumber(milesEffPct, 1)}%` : '—',
        ...esTypeExtras,
        time_eff_pct: timeEffPct > 0 ? `${formatNumber(timeEffPct, 1)}%` : '—',
        overall_util_pct: overallUtilPct > 0 ? `${formatNumber(overallUtilPct, 1)}%` : '—',
        overall_util_pct_sortKey: overallUtilPct,
        _overallUtilValue: overallUtilPct,
        _overallUtilColor: getUtilisationBarColor(overallUtilPct),
        _overallUtilExport: overallUtilPct > 0 ? `${formatNumber(overallUtilPct, 1)}%` : '—',
      };
    });
  };

  // Get available vehicles for dropdown
  const getAvailableVehicles = () => {
    const vehicles = new Set<{ id: string; type: string }>();
    vehicleLevelCSV.forEach(row => {
      const vehicleId = row.vehicle || row.Vehicle_ID || row.VehicleID || row.vehicle_id || '';
      const vehicleType = row.VehicleType || row.Vehicle_Type || row.vehicle_type || '';
      if (vehicleId) {
        vehicles.add({ id: vehicleId, type: vehicleType });
      }
    });
    return Array.from(vehicles).sort((a, b) => a.id.localeCompare(b.id));
  };

  // Handle manual trip form submission
  const handleTripSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingTrip(true);
    setTripSubmitSuccess(false);
    setTripSubmitError(null);

    try {
      const { API_ENDPOINTS } = await import('../config/api');
      
      const formData = {
        ...manualTripForm,
        vehicle_id: manualTripForm.vehicle_id === '__NEW_VEHICLE__' ? newVehicleId : manualTripForm.vehicle_id
      };
      
      if (manualTripForm.vehicle_id === '__NEW_VEHICLE__' && !newVehicleId.trim()) {
        setTripSubmitError('Please enter a vehicle ID for the new vehicle');
        setSubmittingTrip(false);
        return;
      }
      
      const response = await authenticatedFetch(API_ENDPOINTS.manualUtilizationSubmit(serviceType), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit trip entry');
      }

      await response.json();
      setTripSubmitSuccess(true);
      setTripSubmitError(null);
      
      setManualTripForm({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        vehicle_id: '',
        vehicle_type: '',
        distance: '',
        duration: '',
        start_location: '',
        end_location: '',
        notes: ''
      });
      setNewVehicleId('');
      setNewVehicleType('');
      setShowNewVehicleTypeInput(false);
      setSelectedEntries(new Set());

      setTimeout(() => {
        setTripSubmitSuccess(false);
        setShowManualTripForm(false);
      }, 3000);
      
      fetchManualTripEntries();
    } catch (error: any) {
      setTripSubmitError(error.message || 'Failed to submit trip entry');
      setTripSubmitSuccess(false);
    } finally {
      setSubmittingTrip(false);
    }
  };

  // Fetch manual trip entries
  const fetchManualTripEntries = useCallback(async () => {
    try {
      const { API_ENDPOINTS } = await import('../config/api');
      const response = await authenticatedFetch(API_ENDPOINTS.manualUtilizationGet(serviceType));
      if (response.ok) {
        const entries = await response.json();
        setManualTripEntries(entries);
      } else {
        setManualTripEntries([]);
      }
    } catch (error) {
      logger.error('Error fetching manual trip entries:', error);
      setManualTripEntries([]);
    }
  }, [serviceType]);

  // Load manual entries on mount
  useEffect(() => {
    fetchManualTripEntries();
  }, [fetchManualTripEntries]);

  // Handle checkbox selection
  const handleCheckboxChange = (entryIndex: number) => {
    setSelectedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryIndex)) {
        newSet.delete(entryIndex);
      } else {
        newSet.add(entryIndex);
      }
      return newSet;
    });
  };

  // Handle select all checkbox
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedEntries(new Set(manualTripEntries.map((_, idx) => idx)));
    } else {
      setSelectedEntries(new Set());
    }
  };

  // Handle delete selected entries
  const handleDeleteSelected = async () => {
    if (selectedEntries.size === 0) return;
    
    const indicesToDelete = Array.from(selectedEntries).sort((a, b) => b - a);
    setDeleteConfirm(indicesToDelete);
  };

  // Confirm and delete entries
  const confirmDeleteEntries = async () => {
    if (!deleteConfirm || deleteConfirm.length === 0) return;

    try {
      const { API_ENDPOINTS } = await import('../config/api');
      
      for (const entryIndex of deleteConfirm.sort((a, b) => b - a)) {
        const response = await authenticatedFetch(API_ENDPOINTS.manualUtilizationDelete(serviceType), {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ entryIndex }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete entry');
        }
      }

      await fetchManualTripEntries();
      setSelectedEntries(new Set());
      setDeleteConfirm(null);
    } catch (error: any) {
      logger.error('Error deleting entries:', error);
      alert(`Failed to delete entries: ${error.message}`);
    }
  };

  // Update vehicle type when vehicle is selected
  const handleVehicleSelect = (vehicleId: string) => {
    if (vehicleId === '__NEW_VEHICLE__') {
      setManualTripForm({
        ...manualTripForm,
        vehicle_id: '__NEW_VEHICLE__',
        vehicle_type: ''
      });
      setNewVehicleId('');
      setNewVehicleType('');
      setShowNewVehicleTypeInput(false);
    } else {
      const vehicle = getAvailableVehicles().find(v => v.id === vehicleId);
      setManualTripForm({
        ...manualTripForm,
        vehicle_id: vehicleId,
        vehicle_type: vehicle?.type || ''
      });
      setNewVehicleId('');
      setNewVehicleType('');
      setShowNewVehicleTypeInput(false);
    }
  };

  // Get display value for selected vehicle
  const getSelectedVehicleDisplay = () => {
    if (manualTripForm.vehicle_id === '__NEW_VEHICLE__') {
      return newVehicleId || '';
    } else if (manualTripForm.vehicle_id) {
      const vehicle = getAvailableVehicles().find(v => v.id === manualTripForm.vehicle_id);
      return vehicle ? redactVehicleId(vehicle.id, isDemoMode) : '';
    }
    return '';
  };

  // Calendar and trend chart: utilisation-only uses Vehicle Summary formula (viz scope: type + lookup, all dates); full fleet falls back to time series.
  const fleetDailyForCalendar = useMemo(() => {
    const map = new Map<string, number>();
    if (variant === 'utilisation-only' && utilisationMetricRowsVizScope.length > 0) {
      const byDate = new Map<string, { sum: number; count: number }>();
      utilisationMetricRowsVizScope.forEach(m => {
        if (!m.iso) return;
        const cur = byDate.get(m.iso) ?? { sum: 0, count: 0 };
        byDate.set(m.iso, { sum: cur.sum + m.overallUtilPct, count: cur.count + 1 });
      });
      byDate.forEach((v, k) => { map.set(k, v.count > 0 ? v.sum / v.count : 0); });
      return map;
    }
    if (selectedVehicleIds.length > 0 && vehicleLevelCSV.length > 0) {
      const selectedSet = new Set(selectedVehicleIds.map(id => id.toLowerCase()));
      const byDate = new Map<string, { sum: number; count: number }>();
      vehicleLevelCSV.forEach(row => {
        const vid = (row.vehicle || row.Vehicle_ID || row.vehicle_id || row.VehicleID || '').toString().trim();
        if (!selectedSet.has(vid.toLowerCase())) return;
        const dateStr = utilisationCsvRowIsoDate(row);
        if (!dateStr) return;
        const v = parseFloat((row.utilisation_score ?? row['Overall Util %'] ?? row.Overall_Util_Pct ?? '0').toString()) || 0;
        const cur = byDate.get(dateStr) ?? { sum: 0, count: 0 };
        byDate.set(dateStr, { sum: cur.sum + v, count: cur.count + 1 });
      });
      byDate.forEach((v, k) => { map.set(k, v.count > 0 ? v.sum / v.count : 0); });
      return map;
    }
    if (!timeSeriesData?.utilization?.daily?.length) return map;
    (timeSeriesData.utilization.daily as Array<{ date: string; avg_utilization?: number; avg_utilisation?: number; utilisation_score?: number }>).forEach(d => {
      const dateStr = (d.date || '').slice(0, 10);
      if (dateStr) {
        const v = d.utilisation_score ?? d.avg_utilization ?? d.avg_utilisation ?? 0;
        map.set(dateStr, v);
      }
    });
    return map;
  }, [variant, utilisationMetricRowsVizScope, timeSeriesData, selectedVehicleIds, vehicleLevelCSV]);

  const fleetWeekForCalendar = useMemo(() => {
    if (periodType !== 'week') return new Map<string, number>();
    if (variant === 'utilisation-only' && utilisationMetricRowsVizScope.length > 0) {
      const weekUtil = new Map<string, { sum: number; count: number }>();
      utilisationMetricRowsVizScope.forEach(m => {
        const iso = m.iso;
        if (!iso) return;
        const week = availableWeeks.find(w => iso >= w.startDate && iso <= w.endDate);
        if (week) {
          const key = `${week.startDate}|${week.endDate}`;
          const cur = weekUtil.get(key) ?? { sum: 0, count: 0 };
          weekUtil.set(key, { sum: cur.sum + m.overallUtilPct, count: cur.count + 1 });
        }
      });
      const out = new Map<string, number>();
      weekUtil.forEach((v, k) => { out.set(k, v.count > 0 ? v.sum / v.count : 0); });
      return out;
    }
    if (selectedVehicleIds.length > 0 && vehicleLevelCSV.length > 0) {
      const selectedSet = new Set(selectedVehicleIds.map(id => id.toLowerCase()));
      const weekUtil = new Map<string, { sum: number; count: number }>();
      vehicleLevelCSV.forEach(row => {
        const vid = (row.vehicle || row.Vehicle_ID || row.vehicle_id || row.VehicleID || '').toString().trim();
        if (!selectedSet.has(vid.toLowerCase())) return;
        const dateStr = utilisationCsvRowIsoDate(row);
        if (!dateStr) return;
        const week = availableWeeks.find(w => dateStr >= w.startDate && dateStr <= w.endDate);
        if (week) {
          const key = `${week.startDate}|${week.endDate}`;
          const v = parseFloat((row.utilisation_score ?? row['Overall Util %'] ?? row.Overall_Util_Pct ?? '0').toString()) || 0;
          const cur = weekUtil.get(key) ?? { sum: 0, count: 0 };
          weekUtil.set(key, { sum: cur.sum + v, count: cur.count + 1 });
        }
      });
      const out = new Map<string, number>();
      weekUtil.forEach((v, k) => { out.set(k, v.count > 0 ? v.sum / v.count : 0); });
      return out;
    }
    if (!timeSeriesData?.utilization?.daily?.length) return new Map<string, number>();
    const weekUtil = new Map<string, { sum: number; count: number }>();
    (timeSeriesData.utilization.daily as Array<{ date: string; avg_utilization?: number; avg_utilisation?: number; utilisation_score?: number }>).forEach(d => {
      const dateStr = (d.date || '').slice(0, 10);
      if (!dateStr) return;
      const week = availableWeeks.find(w => dateStr >= w.startDate && dateStr <= w.endDate);
      if (week) {
        const key = `${week.startDate}|${week.endDate}`;
        const v = d.utilisation_score ?? d.avg_utilization ?? d.avg_utilisation ?? (d as any).utilisation_score ?? 0;
        const cur = weekUtil.get(key) ?? { sum: 0, count: 0 };
        weekUtil.set(key, { sum: cur.sum + v, count: cur.count + 1 });
      }
    });
    const out = new Map<string, number>();
    weekUtil.forEach((v, k) => { out.set(k, v.count > 0 ? v.sum / v.count : 0); });
    return out;
  }, [variant, utilisationMetricRowsVizScope, timeSeriesData, periodType, availableWeeks, selectedVehicleIds, vehicleLevelCSV]);

  const fleetMonthForCalendar = useMemo(() => {
    if (periodType !== 'month' || availableMonths.length === 0) return new Map<string, number>();
    if (variant === 'utilisation-only' && utilisationMetricRowsVizScope.length > 0) {
      const monthUtil = new Map<string, { sum: number; count: number }>();
      utilisationMetricRowsVizScope.forEach(m => {
        if (!m.iso) return;
        const yearMonth = m.iso.slice(0, 7);
        const month = availableMonths.find(mo => mo && mo.startDate && String(mo.startDate).startsWith(yearMonth));
        if (month) {
          const key = `${month.startDate}|${month.endDate}`;
          const cur = monthUtil.get(key) ?? { sum: 0, count: 0 };
          monthUtil.set(key, { sum: cur.sum + m.overallUtilPct, count: cur.count + 1 });
        }
      });
      const out = new Map<string, number>();
      monthUtil.forEach((v, k) => { out.set(k, v.count > 0 ? v.sum / v.count : 0); });
      return out;
    }
    if (selectedVehicleIds.length > 0 && vehicleLevelCSV.length > 0) {
      const selectedSet = new Set(selectedVehicleIds.map(id => id.toLowerCase()));
      const monthUtil = new Map<string, { sum: number; count: number }>();
      vehicleLevelCSV.forEach(row => {
        const vid = (row.vehicle || row.Vehicle_ID || row.vehicle_id || row.VehicleID || '').toString().trim();
        if (!selectedSet.has(vid.toLowerCase())) return;
        const dateStr = utilisationCsvRowIsoDate(row);
        if (!dateStr) return;
        const yearMonth = dateStr.slice(0, 7);
        const month = availableMonths.find(m => m && m.startDate && String(m.startDate).startsWith(yearMonth));
        if (month) {
          const key = `${month.startDate}|${month.endDate}`;
          const v = parseFloat((row.utilisation_score ?? row['Overall Util %'] ?? row.Overall_Util_Pct ?? '0').toString()) || 0;
          const cur = monthUtil.get(key) ?? { sum: 0, count: 0 };
          monthUtil.set(key, { sum: cur.sum + v, count: cur.count + 1 });
        }
      });
      const out = new Map<string, number>();
      monthUtil.forEach((v, k) => { out.set(k, v.count > 0 ? v.sum / v.count : 0); });
      return out;
    }
    if (!timeSeriesData?.utilization?.daily?.length) return new Map<string, number>();
    const monthUtil = new Map<string, { sum: number; count: number }>();
    (timeSeriesData.utilization.daily as Array<{ date: string; avg_utilization?: number; avg_utilisation?: number; utilisation_score?: number }>).forEach(d => {
      const dateStr = (d.date || '').slice(0, 10);
      if (!dateStr) return;
      const yearMonth = dateStr.slice(0, 7);
      const month = availableMonths.find(m => m && m.startDate && String(m.startDate).startsWith(yearMonth));
      if (month) {
        const key = `${month.startDate}|${month.endDate}`;
        const v = d.utilisation_score ?? d.avg_utilization ?? d.avg_utilisation ?? (d as any).utilisation_score ?? 0;
        const cur = monthUtil.get(key) ?? { sum: 0, count: 0 };
        monthUtil.set(key, { sum: cur.sum + v, count: cur.count + 1 });
      }
    });
    const out = new Map<string, number>();
    monthUtil.forEach((v, k) => { out.set(k, v.count > 0 ? v.sum / v.count : 0); });
    return out;
  }, [variant, utilisationMetricRowsVizScope, timeSeriesData, periodType, availableMonths, selectedVehicleIds, vehicleLevelCSV]);

  // Utilisation trend and side-by-side util charts: prefer vehicle-level CSV (same source as Utilisation tab) when rows exist; else time series.
  const fleetUtilisationChartSeries = useMemo(() => {
    const aggregateChartList = (list: { date: string; value: number; periodLabel: string }[]): Array<{ label: string; value: number }> => {
      if (list.length === 0) return [];
      const displayedMonth = (() => {
        if (periodType === 'day' && list.length > 0) {
          if (calendarPageMonth) return calendarPageMonth;
          const ref = list[list.length - 1].date;
          const y = parseInt(ref.slice(0, 4), 10);
          const m = parseInt(ref.slice(5, 7), 10) - 1;
          return { year: y, month: m };
        }
        if (periodType === 'week' && list.length > 0) {
          if (calendarPageMonth) return calendarPageMonth;
          const ref = list[list.length - 1].date;
          const y = parseInt(ref.slice(0, 4), 10);
          const m = parseInt(ref.slice(5, 7), 10) - 1;
          return { year: y, month: m };
        }
        if (periodType === 'month' && calendarPageMonth) return calendarPageMonth;
        return null;
      })();
      const ymStr = displayedMonth ? `${displayedMonth.year}-${String(displayedMonth.month + 1).padStart(2, '0')}` : null;
      let agg = [...list];
      if (ymStr) {
        if (periodType === 'day' || periodType === 'week') {
          agg = agg.filter(x => x.date.startsWith(ymStr));
        }
      }
      if (periodType === 'week') {
        const byWeek = new Map<string, { sum: number; count: number; date: string }>();
        agg.forEach(x => {
          const w = availableWeeks.find(w => x.date >= w.startDate && x.date <= w.endDate);
          if (w) {
            const key = w.label;
            const cur = byWeek.get(key);
            if (!cur) byWeek.set(key, { sum: x.value, count: 1, date: w.startDate });
            else { cur.sum += x.value; cur.count += 1; }
          }
        });
        agg = Array.from(byWeek.entries()).map(([periodLabel, v]) => ({ date: v.date, value: v.count > 0 ? v.sum / v.count : 0, periodLabel }));
      } else if (periodType === 'month') {
        const byMonth = new Map<string, { sum: number; count: number; date: string }>();
        agg.forEach(x => {
          const ym = x.date!.slice(0, 7);
          const m = availableMonths.find(m => m && m.startDate && m.startDate.startsWith(ym));
          const label = m ? m.label.split(' (')[0] : ym;
          if (m) {
            const cur = byMonth.get(label);
            if (!cur) byMonth.set(label, { sum: x.value, count: 1, date: m.startDate });
            else { cur.sum += x.value; cur.count += 1; }
          }
        });
        agg = Array.from(byMonth.entries()).map(([periodLabel, v]) => ({ date: v.date, value: v.count > 0 ? v.sum / v.count : 0, periodLabel }));
        if (ymStr) agg = agg.filter(x => x.date.startsWith(ymStr));
      }
      agg.sort((a, b) => a.date.localeCompare(b.date));
      return agg.map(x => ({ label: x.periodLabel, value: x.value }));
    };

    if (utilisationMetricRowsVizScope.length > 0) {
      const byDate = new Map<string, { sum: number; count: number }>();
      utilisationMetricRowsVizScope.forEach(m => {
        if (!m.iso) return;
        const cur = byDate.get(m.iso) ?? { sum: 0, count: 0 };
        byDate.set(m.iso, { sum: cur.sum + m.overallUtilPct, count: cur.count + 1 });
      });
      const list = Array.from(byDate.entries()).map(([dateStr, x]) => ({
        date: dateStr,
        value: x.count > 0 ? x.sum / x.count : 0,
        periodLabel: dateStr,
      }));
      return aggregateChartList(list);
    }

    const getUtilFromRow = (row: Record<string, string | undefined>): number =>
      parseFloat((row.utilisation_score ?? row['Overall Util %'] ?? row.Overall_Util_Pct ?? '0').toString()) || 0;

    if (selectedVehicleIds.length > 0 && vehicleLevelCSV.length > 0) {
      const selectedSet = new Set(selectedVehicleIds.map(id => id.toLowerCase()));
      const byDate = new Map<string, { sum: number; count: number }>();
      vehicleLevelCSV.forEach(row => {
        const vid = (row.vehicle || row.Vehicle_ID || row.vehicle_id || row.VehicleID || '').toString().trim();
        if (!selectedSet.has(vid.toLowerCase())) return;
        const dateStr = utilisationCsvRowIsoDate(row);
        if (!dateStr) return;
        const v = getUtilFromRow(row);
        const cur = byDate.get(dateStr) ?? { sum: 0, count: 0 };
        byDate.set(dateStr, { sum: cur.sum + v, count: cur.count + 1 });
      });
      const list = Array.from(byDate.entries()).map(([dateStr, x]) => ({
        date: dateStr,
        value: x.count > 0 ? x.sum / x.count : 0,
        periodLabel: dateStr
      }));
      return aggregateChartList(list);
    }

    if (!timeSeriesData?.utilization) return [];
    const daily = (timeSeriesData.utilization.daily || []) as Array<{ date?: string; avg_utilization?: number; avg_utilisation?: number; utilisation_score?: number }>;
    const list = daily.map(d => {
      const dateStr = (d.date || '').toString().slice(0, 10);
      const value = d.utilisation_score ?? d.avg_utilization ?? d.avg_utilisation ?? 0;
      return { date: dateStr, value, periodLabel: dateStr };
    }).filter(x => x.date);
    return aggregateChartList(list);
  }, [variant, utilisationMetricRowsVizScope, timeSeriesData, periodType, availableWeeks, availableMonths, calendarPageMonth, selectedVehicleIds, vehicleLevelCSV]);

  /** Side-by-side Daily Utilisation line: same CSV-backed series as Utilisation trend, clipped to lookup date window when labels are ISO dates. */
  const sideBySideFleetUtilisationLineData = useMemo((): Array<{ label: string; value: number }> | null => {
    if (utilisationMetricRowsVizScope.length === 0) return null;
    let series = fleetUtilisationChartSeries;
    if (effectiveDateWindow && series.length > 0) {
      const clipped = series.filter(pt => {
        const lab = pt.label;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(lab)) return true;
        return lab >= effectiveDateWindow.startDate && lab <= effectiveDateWindow.endDate;
      });
      if (clipped.length > 0) series = clipped;
    }
    return series.length > 0 ? series : null;
  }, [utilisationMetricRowsVizScope, fleetUtilisationChartSeries, effectiveDateWindow]);

  // Label for the utilisation chart vertical line: must match a chart data label.
  const selectedXLabelForUtilChart = useMemo(() => {
    if (!selectedPeriodRange) return null;
    if (periodType === 'day' && selectedPeriodRange.startDate === selectedPeriodRange.endDate) return selectedPeriodRange.startDate;
    if (periodType === 'week') {
      const w = availableWeeks.find(w => selectedPeriodRange.startDate >= w.startDate && selectedPeriodRange.startDate <= w.endDate);
      return w ? w.label : null;
    }
    if (periodType === 'month') {
      const m = availableMonths.find(m => m && selectedPeriodRange.startDate >= m.startDate && selectedPeriodRange.endDate <= m.endDate);
      return m ? (m.label || '').split(' (')[0] : null;
    }
    return null;
  }, [selectedPeriodRange, periodType, availableWeeks, availableMonths]);

  return (
    <div className="w-full min-w-0 space-y-8">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">{variant === 'utilisation-only' ? 'Vehicle Utilisation' : 'Fleet Report'}</h2>
          <p className="text-muted-foreground text-sm">
            {variant === 'utilisation-only'
              ? 'Monitor how much vehicles are used. Helps identify underused vehicles and plan fleet size to save money.'
              : 'Monitor fleet performance to identify areas for improvement. Helps with planning and cost management.'}
          </p>
          {getDisplayDateRange() && (
            <p className="text-muted-foreground text-xs mt-1">
              Data range: {getDisplayDateRange()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-4">
          {monthOptions.length > 0 && (
            <MonthPicker
              months={monthOptions}
              selected={chartMonth}
              onSelect={selectMonthOption}
              formatLabel={formatMonthLabel}
            />
          )}
        </div>
      </div>

      {/* Top 5 vehicles + Top 5 Best days (static tables, same pattern as Profit page) */}
      {variant === 'utilisation-only' && (
      <>
      {((variant === 'utilisation-only' && vehicleLevelCSV.length > 0) || timeSeriesData) && (topPerformersFull.length > 0 || topDatesFull.length > 0) && (
        <section className="mb-6 flex gap-4">
          {topPerformersFull.length > 0 && (
            <div className="w-1/2 bg-card border border-border rounded-xl overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-warning shrink-0" />
                  <span className="text-sm font-bold tracking-widest text-warning uppercase">
                    {performersShowBottom5 ? 'Bottom 5 Performers' : 'Top 5 Performers'}
                  </span>
                  <button
                    type="button"
                    className="p-1 rounded-md text-warning/90 hover:text-warning hover:bg-warning/10 border border-transparent hover:border-warning/30 transition-colors"
                    title={performersShowBottom5 ? 'Show top 5 performers' : 'Show bottom 5 performers'}
                    aria-pressed={performersShowBottom5}
                    onClick={() => setPerformersShowBottom5((v) => !v)}
                  >
                    <ArrowDownUp className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
                <span className="text-xs text-muted-foreground hover:text-warning cursor-pointer transition-colors">Click to filter →</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-foreground">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">#</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Vehicle</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Util %</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Miles</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Days</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">
                        {serviceType === 'emergency_services' ? 'Trips' : 'Productive hrs'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPerformersDisplayed.map((p) => {
                      const isSelected =
                        selectedVehicleIds.length === 1 &&
                        selectedVehicleIds.some(
                          (id) =>
                            standardiseVehicleId(id).toLowerCase() ===
                            standardiseVehicleId(p.vehicleId).toLowerCase(),
                        );
                      return (
                        <tr
                          key={p.vehicleId}
                          className={`border-b border-border/80 hover:bg-secondary/50 cursor-pointer transition-colors${
                            isSelected ? ' bg-warning/10 ring-1 ring-inset ring-warning/30' : ''
                          }`}
                          title={
                            isSelected
                              ? `Click again to clear filter for ${p.vehicle}`
                              : `Click to filter by ${p.vehicle} and jump to trend`
                          }
                          onClick={() => {
                            if (isSelected) {
                              setSelectedVehicleIds([]);
                              return;
                            }
                            setSelectedVehicleIds([p.vehicleId]);
                            setTimeout(() => trendSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
                          }}
                        >
                          <td className="px-3 py-2.5 text-foreground tabular-nums">{p.rank}</td>
                          <td className="px-3 py-2.5 font-medium text-ring">{p.vehicle}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1 min-w-28 justify-end">
                              <div className="flex-1 h-2.5 bg-accent rounded-full overflow-hidden max-w-18">
                                <div
                                  className="h-full rounded-full transition-all duration-700"
                                  style={{ width: `${Math.min(Math.max(p.utilisationScore ?? 0, 0), 100)}%`, backgroundColor: getScoreColor(p.utilisationScore ?? 0) }}
                                />
                              </div>
                              <span className="text-sm font-semibold text-foreground w-11 text-right tabular-nums">{typeof p.utilisationScore === 'number' ? p.utilisationScore.toFixed(1) : p.utilisationScore}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-right text-foreground tabular-nums">{(p.miles ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td className="px-3 py-2.5 text-right text-foreground tabular-nums">{p.activeDays}</td>
                          <td className="px-3 py-2.5 text-right text-foreground tabular-nums">
                            {serviceType === 'emergency_services'
                              ? (p.trips ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })
                              : (p.productiveHours ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {topDatesFull.length > 0 && (
            <div className="w-1/2 bg-card border border-border rounded-xl overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-warning shrink-0" />
                  <span className="text-sm font-bold tracking-widest text-warning uppercase">
                    {bestDaysShowBottom5 ? 'Bottom 5 Days' : 'Top 5 Best Days'}
                  </span>
                  <button
                    type="button"
                    className="p-1 rounded-md text-warning/90 hover:text-warning hover:bg-warning/10 border border-transparent hover:border-warning/30 transition-colors"
                    title={bestDaysShowBottom5 ? 'Show top 5 best days' : 'Show bottom 5 days'}
                    aria-pressed={bestDaysShowBottom5}
                    onClick={() => setBestDaysShowBottom5((v) => !v)}
                  >
                    <ArrowDownUp className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
                <span className="text-xs text-muted-foreground hover:text-warning cursor-pointer transition-colors">Click to filter →</span>
              </div>
              <div>
                <table className="min-w-full text-sm text-foreground">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground w-0 whitespace-nowrap">#</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground whitespace-nowrap w-0">Date</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground w-0 whitespace-nowrap">Util %</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Miles</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">
                        {serviceType === 'emergency_services' ? 'Trips' : 'Productive hrs'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {topDatesDisplayed.map((d) => (
                      <tr
                        key={d.date}
                        className="border-b border-border/80 hover:bg-secondary/50 cursor-pointer transition-colors"
                        title={`Click to filter by ${d.date} and jump to trend`}
                        onClick={() => {
                          setSelectedPeriodRange({ startDate: d.date, endDate: d.date });
                          setSelectedSummaryDate(d.date);
                          setTimeout(() => trendSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
                        }}
                      >
                        <td className="px-3 py-2.5 text-foreground tabular-nums w-0">{d.rank}</td>
                        <td className="px-3 py-2.5 font-medium text-ring whitespace-nowrap w-0">
                          {new Date(d.date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        </td>
                        <td className="px-3 py-2.5 w-0">
                          <div className="flex items-center gap-1 min-w-28 justify-end">
                            <div className="flex-1 min-w-0 h-2.5 bg-accent rounded-full overflow-hidden max-w-18">
                              <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{ width: `${Math.min(Math.max(d.utilisationScore ?? 0, 0), 100)}%`, backgroundColor: getScoreColor(d.utilisationScore ?? 0) }}
                              />
                            </div>
                            <span className="text-sm font-semibold text-foreground w-11 text-right tabular-nums">{typeof d.utilisationScore === 'number' ? d.utilisationScore.toFixed(1) : d.utilisationScore}%</span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right text-foreground tabular-nums">{(d.miles ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td className="px-3 py-2.5 text-right text-foreground tabular-nums">
                          {serviceType === 'emergency_services'
                            ? (d.trips ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })
                            : (d.productiveHours ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Lookup selection: vehicle + period type + filter by date */}
      {timeSeriesData && (
        <section className="mb-6 bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">Lookup selection</h3>
            <button
              onClick={() => {
                setSelectedVehicleIds([]);
                setVehicleIdInputValue('');
                setSelectedLookupVehicleType(null);
                setSelectedSummaryDate('');
                setSelectedPeriodRange(null);
              }}
              className="px-3 py-1.5 text-xs font-medium text-foreground bg-secondary hover:bg-accent border border-border rounded-md transition-colors flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" /> Clear filters
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="fleet-vehicle-lookup" className="block text-xs font-medium text-muted-foreground mb-1">Vehicle Reg</label>
              <input
                id="fleet-vehicle-lookup"
                list="fleet-vehicle-id-list"
                value={vehicleIdInputValue}
                onChange={(e) => {
                  const raw = e.target.value;
                  setVehicleIdInputValue(raw);
                  const value = standardiseVehicleId(raw);
                  if (value && vehicleOptionsForLookup.some(opt => opt.toLowerCase() === value.toLowerCase()) && !selectedVehicleIds.some(id => id.toLowerCase() === value.toLowerCase())) {
                    setSelectedVehicleIds([...selectedVehicleIds, value]);
                    setVehicleIdInputValue('');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const value = standardiseVehicleId((e.target as HTMLInputElement).value);
                    if (value && !selectedVehicleIds.some(id => id.toLowerCase() === value.toLowerCase())) {
                      setSelectedVehicleIds([...selectedVehicleIds, value]);
                      setVehicleIdInputValue('');
                    }
                  }
                }}
                onBlur={() => {
                  const value = standardiseVehicleId(vehicleIdInputValue);
                  if (value && !selectedVehicleIds.some(id => id.toLowerCase() === value.toLowerCase())) {
                    setSelectedVehicleIds([...selectedVehicleIds, value]);
                    setVehicleIdInputValue('');
                  }
                }}
                placeholder="Type or select..."
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                autoComplete="off"
              />
              <datalist id="fleet-vehicle-id-list">
                {vehicleOptionsForLookup.filter(id => !selectedVehicleIds.includes(id)).map(id => (
                  <option key={id} value={id} />
                ))}
              </datalist>
              <div className="min-h-13 mt-2 flex flex-wrap gap-1.5 bg-secondary/30 rounded p-2">
                {selectedVehicleIds.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-warning/20 text-warning border-warning/60 rounded">
                    {redactVehicleId(vinToCustomerNameMap.get(id) || vinToCustomerNameMap.get(id.toLowerCase()) || id, isDemoMode)}
                    <button type="button" onClick={() => setSelectedVehicleIds(selectedVehicleIds.filter(v => v !== id))} className="hover:text-warning" aria-label={`Remove ${id}`}><X className="w-3.5 h-3.5" /></button>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="fleet-lookup-vehicle-type" className="block text-xs font-medium text-muted-foreground mb-1">Vehicle Type</label>
              <select
                id="fleet-lookup-vehicle-type"
                value={selectedLookupVehicleType ?? ''}
                onChange={(e) => setSelectedLookupVehicleType(e.target.value === '' ? null : e.target.value)}
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">All types</option>
                {getVehicleTypes().map(type => (
                  <option key={type} value={type} className="bg-card text-foreground">{redactVehicleType(type, isDemoMode)}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="fleet-period-type" className="block text-xs font-medium text-muted-foreground mb-1">Period type</label>
              <select
                id="fleet-period-type"
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value as 'day' | 'week' | 'month')}
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </div>
            <div>
              <label htmlFor="fleet-filter-by-date" className="block text-xs font-medium text-muted-foreground mb-1">Filter by {periodType === 'day' ? 'Date' : periodType === 'week' ? 'Week' : 'Month'}</label>
              <select
                id="fleet-filter-by-date"
                value={selectedSummaryDate}
                onChange={(e) => {
                  if (e.target.value === '') {
                    setSelectedSummaryDate('');
                    setSelectedPeriodRange(null);
                  } else {
                    const opt = periodOptions.find(o => o.label === e.target.value);
                    if (opt) {
                      setSelectedSummaryDate(opt.label);
                      setSelectedPeriodRange({ startDate: opt.startDate, endDate: opt.endDate });
                    }
                  }
                }}
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">All dates</option>
                {periodOptions.map((opt, idx) => (
                  <option key={opt?.startDate ?? idx} value={opt?.label ?? ''}>{opt?.label ?? ''}</option>
                ))}
              </select>
            </div>
          </div>
        </section>
      )}
      </>
      )}

      <div className="mb-8">
        {/* KPI Cards - 4 cards in a row, styled like ProfitabilityReport - only on Fleet Report */}
        {variant !== 'utilisation-only' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <FleetStyleKpiCard
            title="TCO per Year"
            value="—"
            subtitle="Coming soon"
            accent="green"
          />
          <FleetStyleKpiCard
            title="Average Utilisation"
            value={getAverageUtilization()}
            subtitle={getFleetKpiDailySubtitle()}
            accent="blue"
          />
          <FleetStyleKpiCard
            title="Total Miles"
            value={getTotalMiles()}
            subtitle={getFleetKpiDailySubtitle()}
            accent="amber"
          />
          <FleetStyleKpiCard
            key={`active-vehicles-kpi-${chartMonth?.year ?? ''}-${chartMonth?.month ?? ''}-${kpiDayIso ?? ''}`}
            title="Active Vehicles"
            value={getActiveVehicles()}
            subtitle={getFleetKpiDailySubtitle()}
            accent="purple"
          />
        </div>
        )}
          {variant === 'utilisation-only' && (fleetDailyForCalendar.size > 0 || fleetWeekForCalendar.size > 0 || fleetMonthForCalendar.size > 0 || vehicleLevelCSV.length > 0) && (
            <div ref={trendSectionRef} className="w-full min-w-0 mb-6 flex flex-col lg:flex-row gap-4 lg:items-stretch">
              {/* Calendar - left */}
              <div className="w-full lg:w-1/2 min-w-0 bg-card border border-border rounded-lg p-5 h-full">
                <h4 className="text-base font-semibold text-foreground mb-4">
                  {periodType === 'day' ? 'Daily Utilisation Calendar' : periodType === 'week' ? 'Weekly Utilisation Calendar' : 'Monthly Utilisation Calendar'}
                </h4>
                {periodType === 'day' && (() => {
                  const dates = Array.from(fleetDailyForCalendar.keys()).sort();
                  if (dates.length === 0) return <div className="text-sm text-muted-foreground">No daily data.</div>;
                  const firstDate = new Date(dates[0] + 'T12:00:00');
                  const lastDate = new Date(dates[dates.length - 1] + 'T12:00:00');
                  const startMonth = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
                  const endMonth = new Date(lastDate.getFullYear(), lastDate.getMonth() + 1, 0);
                  const allDates: string[] = [];
                  const d = new Date(startMonth);
                  while (d <= endMonth) {
                    allDates.push(d.toISOString().slice(0, 10));
                    d.setDate(d.getDate() + 1);
                  }
                  const months: { year: number; month: number; dates: string[] }[] = [];
                  let currentMonth: { year: number; month: number; dates: string[] } | null = null;
                  for (const dateStr of allDates) {
                    const date = new Date(dateStr + 'T12:00:00');
                    const year = date.getFullYear();
                    const month = date.getMonth();
                    if (!currentMonth || currentMonth.year !== year || currentMonth.month !== month) {
                      currentMonth = { year, month, dates: [] };
                      months.push(currentMonth);
                    }
                    currentMonth.dates.push(dateStr);
                  }
                  const currentIndex = calendarPageMonth == null ? Math.max(0, months.length - 1) : months.findIndex(m => m.year === calendarPageMonth.year && m.month === calendarPageMonth.month);
                  const displayIndex = currentIndex < 0 ? 0 : currentIndex;
                  const monthData = months[displayIndex];
                  if (!monthData) return <div className="text-sm text-muted-foreground">No date data.</div>;
                  const monthName = new Date(monthData.year, monthData.month, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
                  const firstDayOfMonth = new Date(monthData.year, monthData.month, 1);
                  const dayOfWeek = firstDayOfMonth.getDay();
                  const daysInMonth = new Date(monthData.year, monthData.month + 1, 0).getDate();
                  return (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <button type="button" onClick={() => { if (displayIndex > 0) setCalendarPageMonth({ year: months[displayIndex - 1].year, month: months[displayIndex - 1].month }); else setCalendarPageMonth(null); }} disabled={displayIndex === 0}
                          className="p-2 rounded-lg border border-border bg-secondary/50 text-foreground hover:bg-accent disabled:opacity-40 text-sm font-medium">‹ Prev</button>
                        <span className="text-sm font-semibold text-foreground min-w-35 text-center">{monthName}</span>
                        <button type="button" onClick={() => { if (displayIndex < months.length - 1) setCalendarPageMonth({ year: months[displayIndex + 1].year, month: months[displayIndex + 1].month }); else setCalendarPageMonth(null); }} disabled={displayIndex >= months.length - 1}
                          className="p-2 rounded-lg border border-border bg-secondary/50 text-foreground hover:bg-accent disabled:opacity-40 text-sm font-medium">Next ›</button>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground mb-1">
                        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(day => <div key={day}>{day}</div>)}
                      </div>
                      <div className="grid grid-cols-7 gap-1" style={{ gridTemplateRows: `repeat(${Math.ceil((dayOfWeek + daysInMonth) / 7)}, minmax(0, 1fr))` }}>
                        {Array.from({ length: dayOfWeek }, (_, i) => <div key={`pad-${i}`} />)}
                        {monthData.dates.map(dateStr => {
                          const utilPct = fleetDailyForCalendar.get(dateStr) ?? null;
                          const isSelected = selectedPeriodRange?.startDate === dateStr && selectedPeriodRange?.endDate === dateStr;
                          const { bg: utilBg, text: utilText } = getCalendarUtilColor(utilPct);
                          const baseBg = utilPct != null && utilPct > 0 ? utilBg : 'bg-secondary/30';
                          const baseText = utilPct != null && utilPct > 0 ? utilText : 'text-muted-foreground';
                          return (
                            <button key={dateStr} type="button" onClick={() => {
                              if (isSelected) {
                                setSelectedPeriodRange(null);
                                setSelectedSummaryDate('');
                              } else {
                                setSelectedPeriodRange({ startDate: dateStr, endDate: dateStr });
                                setSelectedSummaryDate(dateStr);
                              }
                            }}
                              className={`aspect-square w-full ${isSelected ? 'ring-2 ring-warning ' + baseBg : baseBg} ${baseText} border border-border rounded text-center p-1 text-xs hover:brightness-110`}>
                              {new Date(dateStr + 'T12:00:00').getDate()}
                              {utilPct != null && utilPct > 0 && <div className="font-semibold">{utilPct.toFixed(0)}%</div>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                {periodType === 'week' && (
                  <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto">
                    {availableWeeks.map(week => {
                      const key = `${week.startDate}|${week.endDate}`;
                      const utilPct = fleetWeekForCalendar.get(key) ?? null;
                      const isSelected = selectedPeriodRange?.startDate === week.startDate && selectedPeriodRange?.endDate === week.endDate;
                      const { bg: utilBg, text: utilText } = getCalendarUtilColor(utilPct);
                      const baseBg = utilPct != null ? utilBg : 'bg-secondary/30';
                      const baseText = utilPct != null ? utilText : 'text-muted-foreground';
                      return (
                        <button key={key} type="button" onClick={() => {
                          if (isSelected) {
                            setSelectedPeriodRange(null);
                            setSelectedSummaryDate('');
                          } else {
                            setSelectedPeriodRange({ startDate: week.startDate, endDate: week.endDate });
                            setSelectedSummaryDate(week.label);
                          }
                        }}
                          className={`p-3 rounded-lg border text-left ${baseBg} border-border ${baseText} ${isSelected ? 'ring-2 ring-warning' : ''}`}>
                          <span className="text-sm font-medium">{week.label.split(' (')[0]}</span>
                          {utilPct != null && <span className="block text-xs mt-1">{utilPct.toFixed(1)}% utilisation</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
                {periodType === 'month' && (
                  <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                    {availableMonths.map(month => {
                      const key = `${month.startDate}|${month.endDate}`;
                      const utilPct = fleetMonthForCalendar.get(key) ?? null;
                      const isSelected = selectedPeriodRange?.startDate === month.startDate && selectedPeriodRange?.endDate === month.endDate;
                      const { bg: utilBg, text: utilText } = getCalendarUtilColor(utilPct);
                      const baseBg = utilPct != null ? utilBg : 'bg-secondary/30';
                      const baseText = utilPct != null ? utilText : 'text-muted-foreground';
                      return (
                        <button key={key} type="button" onClick={() => {
                          if (isSelected) {
                            setSelectedPeriodRange(null);
                            setSelectedSummaryDate('');
                          } else {
                            setSelectedPeriodRange({ startDate: month.startDate, endDate: month.endDate });
                            setSelectedSummaryDate(month.label);
                          }
                        }}
                          className={`p-3 rounded-lg border text-left ${baseBg} border-border ${baseText} ${isSelected ? 'ring-2 ring-warning' : ''}`}>
                          <span className="text-sm font-medium">{(month.label || '').split(' (')[0]}</span>
                          {utilPct != null && <span className="block text-xs mt-1">{utilPct.toFixed(1)}% utilisation</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Trend line chart - right */}
              <div className="min-w-0 flex-1 bg-card border border-border rounded-lg overflow-hidden">
                {fleetUtilisationChartSeries.length > 0 ? (
                  <LineChart
                    title="Utilisation Tracker"
                    series={[{ name: 'Utilisation', data: fleetUtilisationChartSeries, color: STATUS_COLORS.warning }]}
                    yAxisLabel=""
                    xAxisLabel={periodType === 'day' ? 'Day' : periodType === 'week' ? 'Week' : 'Month'}
                    color={STATUS_COLORS.warning}
                    heightClass="min-h-80"
                    chartHeight={265}
                    defaultZoomed
                    formatValueAsInteger={false}
                    gradientFillSeriesName="Utilisation"
                    horizontalReferenceLine={{ value: 70, color: STATUS_COLORS.success, strokeDasharray: '4,4' }}
                    formatXLabel={(label) => {
                      if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
                        return String(parseInt(label.slice(8, 10), 10));
                      }
                      return label;
                    }}
                    xAxisLabelInterval={periodType === 'day' ? 7 : undefined}
                    selectedXLabel={selectedXLabelForUtilChart}
                    hideDataPoints
                  />
                ) : (
                  <div className="flex items-center justify-center min-h-80 text-muted-foreground">No utilisation trend for selected window.</div>
                )}
              </div>
            </div>
          )}
          {variant !== 'utilisation-only' && (timeSeriesData || fleetReportDailyMileageSeries.length > 0 || fleetReportDailyUtilSeries.length > 0) ? (
            (() => {
              const monthTitleSuffix = chartMonth
                ? ` (${new Date(chartMonth.year, chartMonth.month - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })})`
                : '';

              // Prefer vehicle-day rows for the selected month; fall back to month-filtered time-series only.
              if (serviceType === 'emergency_services') {
                const dailyUtilizationData =
                  fleetReportDailyUtilSeries.length > 0
                    ? fleetReportDailyUtilSeries
                    : (sideBySideFleetUtilisationLineData ?? getDailyUtilizationData());
                const dailyMileageData =
                  fleetReportDailyMileageSeries.length > 0
                    ? fleetReportDailyMileageSeries
                    : getDailyMileageData();
                const hasMileageData = dailyMileageData.length > 0;
                const hasUtilizationData = dailyUtilizationData.length > 0;
                const hasData = hasMileageData || hasUtilizationData;

                if (hasData) {
                  return (
                    <div className="fleet-report-charts col-span-full h-full w-full min-w-0 flex gap-4" key={`daily-charts-${chartMonth?.year ?? ''}-${chartMonth?.month ?? ''}`}>
                      {hasMileageData ? (
                        <div className="fleet-report-chart-panel flex-1 min-w-0">
                          <LineChart
                            title={`Daily Mileage${monthTitleSuffix}`}
                            series={[{ name: 'Mileage', data: dailyMileageData, color: STATUS_COLORS.success }]}
                            yAxisLabel=""
                            xAxisLabel="Day of Month"
                            color={STATUS_COLORS.success}
                            heightClass="min-h-80"
                            chartHeight={265}
                            defaultZoomed
                            formatValueAsInteger={true}
                            gradientFillSeriesName="Mileage"
                            formatXLabel={(label) => {
                              if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
                                return String(parseInt(label.slice(8, 10), 10));
                              }
                              return label;
                            }}
                            xAxisLabelInterval={7}
                            hideDataPoints
                          />
                        </div>
                      ) : (
                        <div className="fleet-report-chart-panel flex-1 min-w-0 bg-card rounded-lg shadow-sm p-6 border border-border flex items-center justify-center">
                          <p className="text-muted-foreground">No mileage data available</p>
                        </div>
                      )}

                      {hasUtilizationData ? (
                        <div className="fleet-report-chart-panel flex-1 min-w-0">
                          <LineChart
                            title={`Daily Utilisation (%)${monthTitleSuffix}`}
                            series={[{ name: 'Utilisation', data: dailyUtilizationData, color: STATUS_COLORS.warning }]}
                            yAxisLabel=""
                            xAxisLabel="Day of Month"
                            color={STATUS_COLORS.warning}
                            heightClass="min-h-80"
                            chartHeight={265}
                            defaultZoomed
                            formatValueAsInteger={false}
                            gradientFillSeriesName="Utilisation"
                            horizontalReferenceLine={{ value: 70, color: STATUS_COLORS.success, strokeDasharray: '4,4' }}
                            formatXLabel={(label) => {
                              if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
                                return String(parseInt(label.slice(8, 10), 10));
                              }
                              return label;
                            }}
                            xAxisLabelInterval={7}
                            hideDataPoints
                          />
                        </div>
                      ) : (
                        <div className="fleet-report-chart-panel flex-1 min-w-0 bg-card rounded-lg shadow-sm p-6 border border-border flex items-center justify-center">
                          <p className="text-muted-foreground">No utilisation data available</p>
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <div className="col-span-full h-full bg-card rounded-lg shadow-sm p-6 border border-border flex items-center justify-center">
                    <p className="text-muted-foreground">No daily data available for this period</p>
                  </div>
                );
              }

              // Logistics: side-by-side mileage and utilisation for the selected month
              const utilizationData =
                fleetReportDailyUtilSeries.length > 0
                  ? fleetReportDailyUtilSeries
                  : (sideBySideFleetUtilisationLineData ?? getUtilizationData());
              const mileageData =
                fleetReportDailyMileageSeries.length > 0
                  ? fleetReportDailyMileageSeries
                  : getMileageData();

              const hasMileageData = mileageData.length > 0;
              const hasUtilizationData = utilizationData.length > 0;
              const hasData = hasMileageData || hasUtilizationData;

              if (hasData) {
                return (
                  <div className="fleet-report-charts col-span-full h-full w-full min-w-0 flex gap-4" key={`logistics-charts-${chartMonth?.year ?? ''}-${chartMonth?.month ?? ''}`}>
                    {hasMileageData ? (
                      <div className="fleet-report-chart-panel flex-1 min-w-0">
                        <LineChart
                          title={`Daily Mileage${monthTitleSuffix}`}
                          series={[{ name: 'Mileage', data: mileageData, color: STATUS_COLORS.success }]}
                          yAxisLabel=""
                          xAxisLabel="Day of Month"
                          color={STATUS_COLORS.success}
                          heightClass="min-h-80"
                          chartHeight={265}
                          defaultZoomed
                          formatValueAsInteger={true}
                          gradientFillSeriesName="Mileage"
                          formatXLabel={(label) => {
                            if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
                              return String(parseInt(label.slice(8, 10), 10));
                            }
                            return label;
                          }}
                          xAxisLabelInterval={7}
                          hideDataPoints
                        />
                      </div>
                    ) : (
                      <div className="fleet-report-chart-panel flex-1 min-w-0 bg-card rounded-lg shadow-sm p-6 border border-border flex items-center justify-center">
                        <p className="text-muted-foreground">No mileage data available</p>
                      </div>
                    )}

                    {hasUtilizationData ? (
                      <div className="fleet-report-chart-panel flex-1 min-w-0">
                        <LineChart
                          title={`Daily Utilisation (%)${monthTitleSuffix}`}
                          series={[{ name: 'Utilisation', data: utilizationData, color: STATUS_COLORS.warning }]}
                          yAxisLabel=""
                          xAxisLabel="Day of Month"
                          color={STATUS_COLORS.warning}
                          heightClass="min-h-80"
                          chartHeight={265}
                          defaultZoomed
                          formatValueAsInteger={false}
                          gradientFillSeriesName="Utilisation"
                          horizontalReferenceLine={{ value: 70, color: STATUS_COLORS.success, strokeDasharray: '4,4' }}
                          formatXLabel={(label) => {
                            if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
                              return String(parseInt(label.slice(8, 10), 10));
                            }
                            return label;
                          }}
                          xAxisLabelInterval={7}
                          hideDataPoints
                        />
                      </div>
                    ) : (
                      <div className="fleet-report-chart-panel flex-1 min-w-0 bg-card rounded-lg shadow-sm p-6 border border-border flex items-center justify-center">
                        <p className="text-muted-foreground">No utilisation data available</p>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div className="col-span-full h-full bg-card rounded-lg shadow-sm p-6 border border-border flex items-center justify-center">
                  <p className="text-muted-foreground">No data available for this period</p>
                </div>
              );
            })()
          ) : null}
      </div>

      {/* Vehicle Trends, Map, Trip Patterns - only on Fleet Report */}
      {variant !== 'utilisation-only' && (
      <>
      {/* Vehicle Trends section - ES mode only */}
      {serviceType === 'emergency_services' && (
      <section className="mt-8 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Vehicle Trends</h2>
            <p className="text-muted-foreground text-sm">
              Track utilisation and mileage over time. Helps with scheduling and planning maintenance.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {loading ? (
            <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-80 flex items-center justify-center">
              <p className="text-muted-foreground">Loading time-series data...</p>
            </div>
          ) : timeSeriesData ? (
                <DonutChart
                  title="Vehicle Usage"
                  data={getVehicleTypeDistribution()}
                />
          ) : (
            <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-80 flex items-center justify-center">
              <p className="text-muted-foreground">No time-series data available</p>
            </div>
          )}
        </div>
      </section>
      )}

      <InteractiveServiceAreaMapSection getVehicleDisplayName={getVehicleDisplayName} selectedMonth={chartMonth} />

      <section className="mt-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Vehicle Statistics</h2>
            <p className="text-muted-foreground text-sm">
              Review trip patterns for duration, distance, and speed. Helps plan better routes and reduce costs.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedTripCharacteristic('duration')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border ${
                selectedTripCharacteristic === 'duration'
                  ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                  : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
              }`}
            >
              Duration
            </button>
            <button
              onClick={() => setSelectedTripCharacteristic('distance')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border ${
                selectedTripCharacteristic === 'distance'
                  ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                  : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
              }`}
            >
              Distance
            </button>
            <button
              onClick={() => setSelectedTripCharacteristic('speed')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border ${
                selectedTripCharacteristic === 'speed'
                  ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                  : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
              }`}
            >
              Speed
            </button>
          </div>
        </div>
        <div className="bg-card/50 rounded-lg p-6 border border-border">
        <div className="w-full space-y-6">
          {tripPatternLoading ? (
            <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-80 flex items-center justify-center">
              <p className="text-muted-foreground">Loading data...</p>
            </div>
          ) : tripPatternData ? (
            <>
              {/* Vehicle Level Summary Table */}
              <div>
                <div className="mb-4">
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <h3 className="text-lg font-semibold text-white shrink-0">Vehicle Summary</h3>
                    <div className="flex items-center gap-2 shrink-0">
                      {/* Vehicle Type Filter Dropdown */}
                      {getAvailableVehicleTypes().length > 0 && (
                        <select
                          value={selectedVehicleTypeFilter || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedVehicleTypeFilter(value === '' ? null : value);
                          }}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                            selectedVehicleTypeFilter
                              ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                              : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                          } focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring`}
                          style={{
                            appearance: 'none',
                            backgroundImage: selectedVehicleTypeFilter 
                              ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`
                              : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 0.75rem center',
                            paddingRight: '2.5rem'
                          }}
                        >
                          <option value="" className="bg-card text-foreground">All Vehicle Types</option>
                          {getAvailableVehicleTypes().map(vehicleType => (
                            <option key={vehicleType} value={vehicleType} className="bg-card text-foreground">
                              {vehicleType}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        onClick={() => {
                          const data = getVehicleLevelSummaryStats(selectedTripCharacteristic);
                          const minLabel = selectedTripCharacteristic === 'duration' ? 'Shortest' : 
                                          selectedTripCharacteristic === 'distance' ? 'Shortest' : 'Slowest';
                          const maxLabel = selectedTripCharacteristic === 'duration' ? 'Longest' : 
                                          selectedTripCharacteristic === 'distance' ? 'Longest' : 'Fastest';
                          const headers = ['Vehicle', 'Days', 'Trips', 'Average', 'Median', 'Typical Range', minLabel, maxLabel];
                          const csv = [
                            headers.join(','),
                            ...data.map(row => [
                              row.vehicle_id_sortKey,
                              row.total_days,
                              row.sample_size,
                              row.mean,
                              row.median,
                              row.typical_range,
                              row.min,
                              row.max
                            ].join(','))
                          ].join('\n');
                          const blob = new Blob([csv], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `vehicle_summary_${selectedTripCharacteristic}${selectedVehicleTypeFilter ? `_${selectedVehicleTypeFilter.replace(/\s+/g, '_')}` : ''}.csv`;
                          link.click();
                          window.URL.revokeObjectURL(url);
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
                      >
                        <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                        <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
                      </button>
                    </div>
                  </div>
                </div>
                <DataTable
                  key={`vehicle-summary-${selectedVehicleTypeFilter || 'all'}-${selectedTripCharacteristic}`}
                  title=""
                  columns={[
                    { key: 'vehicle_id', label: 'Vehicle' },
                    { key: 'total_days', label: 'Days', align: 'right' as const },
                    { key: 'sample_size', label: 'Trips', align: 'right' as const },
                    { key: 'mean', label: 'Average', align: 'right' as const },
                    { key: 'median', label: 'Median', align: 'right' as const },
                    { key: 'typical_range', label: <TypicalRangeLabel />, align: 'right' as const },
                    { key: 'min', label: selectedTripCharacteristic === 'duration' ? 'Shortest' : selectedTripCharacteristic === 'distance' ? 'Shortest' : 'Slowest', align: 'right' as const },
                    { key: 'max', label: selectedTripCharacteristic === 'duration' ? 'Longest' : selectedTripCharacteristic === 'distance' ? 'Longest' : 'Fastest', align: 'right' as const },
                  ]}
                  data={getVehicleLevelSummaryStats(selectedTripCharacteristic)}
                  hideExport
                  customColumnWidths={{ 
                    vehicle_id: { minWidth: 10, maxWidth: 14 },
                    total_days: { minWidth: 5, maxWidth: 7 },
                    sample_size: { minWidth: 5, maxWidth: 7 },
                    mean: { minWidth: 6, maxWidth: 8 },
                    median: { minWidth: 6, maxWidth: 8 },
                    typical_range: { minWidth: 10, maxWidth: 14 },
                    min: { minWidth: 6, maxWidth: 8 },
                    max: { minWidth: 6, maxWidth: 8 }
                  }}
                />
              </div>

              {/* Vehicle Type Summary Table */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Vehicle Type Summary</h3>
                  <button
                    onClick={() => {
                      const data = getVehicleTypeSummaryStats(selectedTripCharacteristic);
                      const minLabel = selectedTripCharacteristic === 'duration' ? 'Shortest' : 
                                      selectedTripCharacteristic === 'distance' ? 'Shortest' : 'Slowest';
                      const maxLabel = selectedTripCharacteristic === 'duration' ? 'Longest' : 
                                      selectedTripCharacteristic === 'distance' ? 'Longest' : 'Fastest';
                      const headers = ['Vehicle Type', 'Days', 'Trips', 'Average', 'Median', 'Typical Range', minLabel, maxLabel];
                      const csv = [
                        headers.join(','),
                        ...data.map(row => [
                          (row as any)._vehicle_type_sortKey || row.vehicle_type, // Use sort key for export
                          row.total_days,
                          row.sample_size,
                          row.mean,
                          row.median,
                          row.typical_range,
                          row.min,
                          row.max
                        ].join(','))
                      ].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `vehicle_type_summary_${selectedTripCharacteristic}.csv`;
                      link.click();
                      window.URL.revokeObjectURL(url);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
                  >
                    <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-125 transition-all duration-300" />
                    <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
                  </button>
                </div>
                <DataTable
                  key={`vehicle-type-summary-${selectedVehicleTypeFilter || 'all'}-${selectedTripCharacteristic}`}
                  title=""
                  columns={[
                    { key: 'vehicle_type', label: 'Vehicle Type' },
                    { key: 'total_days', label: 'Days', align: 'right' as const },
                    { key: 'sample_size', label: 'Trips', align: 'right' as const },
                    { key: 'mean', label: 'Average', align: 'right' as const },
                    { key: 'median', label: 'Median', align: 'right' as const },
                    { key: 'typical_range', label: <TypicalRangeLabel />, align: 'right' as const },
                    { key: 'min', label: selectedTripCharacteristic === 'duration' ? 'Shortest' : selectedTripCharacteristic === 'distance' ? 'Shortest' : 'Slowest', align: 'right' as const },
                    { key: 'max', label: selectedTripCharacteristic === 'duration' ? 'Longest' : selectedTripCharacteristic === 'distance' ? 'Longest' : 'Fastest', align: 'right' as const },
                  ]}
                  data={getVehicleTypeSummaryStats(selectedTripCharacteristic)}
                  hideExport
                  customColumnWidths={{ 
                    vehicle_type: { minWidth: 10, maxWidth: 14 },
                    total_days: { minWidth: 5, maxWidth: 7 },
                    sample_size: { minWidth: 5, maxWidth: 7 },
                    mean: { minWidth: 6, maxWidth: 8 },
                    median: { minWidth: 6, maxWidth: 8 },
                    typical_range: { minWidth: 10, maxWidth: 14 },
                    min: { minWidth: 6, maxWidth: 8 },
                    max: { minWidth: 6, maxWidth: 8 }
                  }}
                />
              </div>

              {/* Station Summary Table (Emergency Services only) */}
              {serviceType === 'emergency_services' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Station Summary</h3>
                    <button
                      onClick={() => {
                        const data = getStationSummaryStats(selectedTripCharacteristic);
                        const minLabel = selectedTripCharacteristic === 'duration' ? 'Shortest' : 
                                        selectedTripCharacteristic === 'distance' ? 'Shortest' : 'Slowest';
                        const maxLabel = selectedTripCharacteristic === 'duration' ? 'Longest' : 
                                        selectedTripCharacteristic === 'distance' ? 'Longest' : 'Fastest';
                        const headers = ['Station', 'Days', 'Trips', 'Average', 'Median', 'Typical Range', minLabel, maxLabel];
                        const csv = [
                          headers.join(','),
                          ...data.map(row => [
                            row._station_sortKey || row.station,
                            row.total_days,
                            row.sample_size,
                            row.mean,
                            row.median,
                            row.typical_range,
                            row.min,
                            row.max
                          ].join(','))
                        ].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `station_summary_${selectedTripCharacteristic}.csv`;
                        link.click();
                        window.URL.revokeObjectURL(url);
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
                    >
                      <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-125 transition-all duration-300" />
                      <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
                    </button>
                  </div>
                  <DataTable
                    key={`station-summary-${selectedTripCharacteristic}`}
                    title=""
                    columns={[
                      { key: 'station', label: 'Station' },
                      { key: 'total_days', label: 'Days', align: 'right' as const },
                      { key: 'sample_size', label: 'Trips', align: 'right' as const },
                      { key: 'mean', label: 'Average', align: 'right' as const },
                      { key: 'median', label: 'Median', align: 'right' as const },
                      { key: 'typical_range', label: <TypicalRangeLabel />, align: 'right' as const },
                      { key: 'min', label: selectedTripCharacteristic === 'duration' ? 'Shortest' : selectedTripCharacteristic === 'distance' ? 'Shortest' : 'Slowest', align: 'right' as const },
                      { key: 'max', label: selectedTripCharacteristic === 'duration' ? 'Longest' : selectedTripCharacteristic === 'distance' ? 'Longest' : 'Fastest', align: 'right' as const },
                    ]}
                    data={getStationSummaryStats(selectedTripCharacteristic)}
                    hideExport
                    customColumnWidths={{ 
                      station: { minWidth: 10, maxWidth: 14 },
                      total_days: { minWidth: 5, maxWidth: 7 },
                      sample_size: { minWidth: 5, maxWidth: 7 },
                      mean: { minWidth: 6, maxWidth: 8 },
                      median: { minWidth: 6, maxWidth: 8 },
                      typical_range: { minWidth: 10, maxWidth: 14 },
                      min: { minWidth: 6, maxWidth: 8 },
                      max: { minWidth: 6, maxWidth: 8 }
                    }}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-80 flex items-center justify-center">
              <p className="text-muted-foreground">Error loading data</p>
            </div>
          )}
        </div>
        </div>
      </section>
      </>
      )}

      {/* Vehicle Utilisation section - only on Utilisation tab */}
      {variant === 'utilisation-only' && (
      <>
      {/* UTILIZATION SECTION - Moved from Utilization tab */}
      {/* Utilisation (moved from Utilization tab) */}
      <section className="mt-10 space-y-10">

        {/* Utilisation Metrics */}
        <section ref={utilisationMetricsSectionRef} className="bg-card/50 rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Utilisation Metrics</h2>
              <p className="text-muted-foreground text-sm">
                Detailed view of how each vehicle and vehicle type is used. Helps identify ways to improve efficiency and plan the fleet.
              </p>
              <p className="text-muted-foreground text-xs mt-1.5">
                Overall Util %: <span className="text-success">Green 70–100%</span> · <span className="text-warning">Amber 40–69%</span> · <span className="text-destructive">Red 0–39%</span>
              </p>
            </div>
            <div className="flex gap-2">
              <select
                value={selectedVehicleType || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedVehicleType(value === '' ? null : value);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                  selectedVehicleType
                    ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                    : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                } focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring`}
                style={{
                  appearance: 'none',
                  backgroundImage: selectedVehicleType 
                    ? "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%27000%27 d=%27M6 9L1 4h10z%27/%3E%3C/svg%3E')"
                    : "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%239ca3af d=%27M6 9L1 4h10z%27/%3E%3C/svg%3E')",
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="" className="bg-card text-foreground">All Vehicle Types</option>
                {getVehicleTypes().map(vehicleType => (
                  <option key={vehicleType} value={vehicleType} className="bg-card text-foreground">
                    {vehicleType}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Vehicle Summary</h3>
              <div className="flex items-center gap-2">
                <button
                onClick={() => {
                  const data = getDetailedVehicleData();
                  const columns = getIndividualVehicleColumns();
                  const headers = columns.map(col => col.label);
                  const csv = [
                    headers.join(','),
                    ...data.map(row => 
                      columns.map(col => {
                        if (col.key === 'vehicle_id') {
                          return (row as any).vehicle_id_sortKey ?? (row as any)._vehicleId ?? '';
                        }
                        if (col.key === 'date') {
                          return (row as any)._dateExport ?? (row as any).date_sortKey ?? (row as any).date ?? '';
                        }
                        if (col.key === 'overall_util_pct') {
                          return (row as any)._overallUtilExport ?? '';
                        }
                        const value = (row as any)[col.key];
                        return String(value || '').replace(/,/g, ';');
                      }).join(',')
                    )
                  ].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `vehicle_summary_${selectedVehicleType || 'all'}.csv`;
                  link.click();
                  window.URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
              >
                <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
              </button>
              </div>
            </div>
            <DataTable
              key={`utilization-vehicle-summary-${selectedVehicleType || 'all'}-${editedUtilTargetsRows ? 'custom' : 'default'}`}
              title=""
              columns={getIndividualVehicleColumns()}
              data={getDetailedVehicleData()}
              hideExport
              defaultSort={{ column: 'date', direction: 'asc' }}
              customColumnWidths={{ 
                date: { minWidth: 8, maxWidth: 12 },
                vehicle_id: serviceType === 'logistics' 
                  ? { minWidth: 12, maxWidth: 18 } 
                  : { minWidth: 8, maxWidth: 12 } 
              }}
              selectedRowId={selectedVehicleSummaryRowId}
              rowIdKey="_rowId"
              onRowClick={(row) => {
                const r = row as {
                  _rowId?: string; _vehicleType?: string; _milesEffValue?: number; _revEffValue?: number;
                  _timeEffValue?: number; _loadEffValue?: number; _milesDone?: number;
                  _revenueGbp?: number; _productiveHours?: number; _avgLaden?: number;
                  _targetMiles?: number; _targetRevenue?: number; _targetTimeHrs?: number;
                };
                const rowId = r._rowId;
                const type = r._vehicleType;
                if (rowId && selectedVehicleSummaryRowId === rowId) {
                  setSelectedVehicleSummaryRowId(null);
                  setSelectedVehicleTypeForTargets(null);
                  setSelectedRowEfficiencyValues(null);
                } else if (rowId && type) {
                  setSelectedVehicleSummaryRowId(rowId);
                  setSelectedVehicleTypeForTargets(type);
                  setSelectedRowEfficiencyValues({
                    milesEff: r._milesEffValue ?? 0,
                    revEff: r._revEffValue ?? 0,
                    timeEff: r._timeEffValue ?? 0,
                    loadEff: r._loadEffValue ?? 0,
                    actualMiles: r._milesDone != null ? r._milesDone : undefined,
                    actualRevenue: r._revenueGbp != null && r._revenueGbp >= 0 ? r._revenueGbp : undefined,
                    actualTimeHrs: r._productiveHours != null && r._productiveHours > 0 ? r._productiveHours : undefined,
                    actualLoadPct: r._avgLaden != null && r._avgLaden >= 0 && r._avgLaden <= 100 ? r._avgLaden : undefined,
                    rowTargetMiles: r._targetMiles != null && r._targetMiles > 0 ? r._targetMiles : undefined,
                    rowTargetRevenue: r._targetRevenue != null && r._targetRevenue > 0 ? r._targetRevenue : undefined,
                    rowTargetTimeHrs: r._targetTimeHrs != null && r._targetTimeHrs > 0 ? r._targetTimeHrs : undefined,
                  });
                }
              }}
            />
          </div>


          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Vehicle Type Summary</h3>
              <button
                onClick={() => {
                  const data = getAggregatedVehicleData();
                  const columns = getAggregatedVehicleColumns();
                  const headers = columns.map(col => col.label);
                  const csv = [
                    headers.join(','),
                    ...data.map(row => 
                      columns.map(col => {
                        if (col.key === 'vehicle_type') {
                          return (row as any)._vehicle_type_sortKey || '';
                        }
                        if (col.key === 'date') {
                          return (row as any)._dateExport ?? (row as any).date_sortKey ?? (row as any).date ?? '';
                        }
                        if (col.key === 'overall_util_pct') {
                          return (row as any)._overallUtilExport ?? '';
                        }
                        const value = (row as any)[col.key];
                        return String(value || '').replace(/,/g, ';');
                      }).join(',')
                    )
                  ].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `vehicle_type_summary_${selectedVehicleType || 'all'}.csv`;
                  link.click();
                  window.URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
              >
                <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
              </button>
            </div>
            <DataTable
              key={`utilization-vehicle-type-summary-${selectedVehicleType || 'all'}-${editedUtilTargetsRows ? 'custom' : 'default'}`}
              title=""
              columns={getAggregatedVehicleColumns()}
              data={getAggregatedVehicleData()}
              hideExport
              customColumnWidths={{ date: { minWidth: 8, maxWidth: 12 }, vehicle_type: { minWidth: 8, maxWidth: 12 } }}
            />
          </div>

          {serviceType === 'emergency_services' && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Station Summary</h3>
                <button
                  onClick={() => {
                    const data = getStationSummaryData();
                    const columns = getStationSummaryColumns();
                    const headers = columns.map(col => col.label);
                    const csv = [
                      headers.join(','),
                      ...data.map(row => 
                        columns.map(col => {
                          if (col.key === 'station_name') {
                            return (row as any)._station_sortKey || '';
                          }
                          if (col.key === 'date') {
                            return (row as any)._dateExport || (row as any).date || '';
                          }
                          const value = (row as any)[col.key];
                          return String(value || '').replace(/,/g, ';');
                        }).join(',')
                      )
                    ].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `station_summary_${selectedVehicleType || 'all'}.csv`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
                >
                  <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                  <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
                </button>
              </div>
              <DataTable
                key={`utilization-station-summary-${selectedVehicleType || 'all'}`}
                title=""
                columns={getStationSummaryColumns()}
                data={getStationSummaryData()}
                hideExport
                customColumnWidths={{
                  date: { minWidth: 8, maxWidth: 12 },
                  station_name: { minWidth: 8, maxWidth: 12 },
                }}
              />
            </div>
          )}
        </section>

        {/* Station Performance (ES only) */}
        {serviceType === 'emergency_services' && (
          <section className="bg-card/50 rounded-lg p-6 border border-border">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Station Performance</h2>
                <p className="text-muted-foreground text-sm">
                  Compare station performance based on trip frequency and downtime. Helps identify busy stations and plan resource allocation.
                </p>
              </div>
              <button
                onClick={() => {
                  const data = getStationPerformanceData();
                  const columns = getStationPerformanceColumns();
                  const headers = columns.map(col => col.label);
                  const csv = [
                    headers.join(','),
                    ...data.map(row =>
                      columns.map(col => {
                        if (col.key === 'station') {
                          return (row as any)._station_sortKey || '';
                        }
                        const value = (row as any)[col.key];
                        return String(value || '').replace(/,/g, ';');
                      }).join(',')
                    )
                  ].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `station_performance_${selectedPeriod}.csv`;
                  link.click();
                  window.URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
              >
                <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
              </button>
            </div>
            <DataTable
              key={`utilization-station-performance-${selectedPeriod}`}
              title=""
              columns={getStationPerformanceColumns()}
              data={getStationPerformanceData()}
              hideExport
              customColumnWidths={{ 
                station: { minWidth: 4, maxWidth: 14 },
                performance_rank: { minWidth: 6, maxWidth: 14 }
              }}
            />
          </section>
        )}

        {/* Manual Trip Entry Modal */}
        {showManualTripForm && createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4"
            onClick={() => setShowManualTripForm(false)}
          >
            <div
              className="bg-popover rounded-lg shadow-xl border border-border max-w-4xl w-full max-h-screen-90 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-popover border-b border-border px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Plus className="w-5 h-5 text-success" />
                  <div>
                    <h2 className="text-xl font-bold text-white">Log a Trip</h2>
                    <p className="text-sm text-muted-foreground">Manually enter trip data for vehicles without telematics</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowManualTripForm(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors text-2xl leading-none"
                >
                  x
                </button>
              </div>
              <div className="p-6">
                <form onSubmit={handleTripSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Date <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="date"
                        required
                        value={manualTripForm.date}
                        onChange={(e) => setManualTripForm({ ...manualTripForm, date: e.target.value })}
                        className="w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring hover:border-accent transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Time <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="time"
                        required
                        value={manualTripForm.time}
                        onChange={(e) => setManualTripForm({ ...manualTripForm, time: e.target.value })}
                        className="w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring hover:border-accent transition-all"
                      />
                    </div>

                    <div className="md:col-span-1">
                      <div className="flex items-center justify-between mb-2 relative">
                        <label className="text-sm font-medium text-white">
                          Vehicle ID <span className="text-destructive">*</span>
                        </label>
                        <div className="absolute right-0" style={{ width: '28px', height: '28px' }}>
                          {manualTripForm.vehicle_id === '__NEW_VEHICLE__' && (
                            <button
                              type="button"
                              onClick={() => {
                                setManualTripForm({ ...manualTripForm, vehicle_id: '', vehicle_type: '' });
                                setNewVehicleId('');
                                setNewVehicleType('');
                                setShowNewVehicleTypeInput(false);
                              }}
                              className="px-2 py-1 bg-accent text-foreground rounded-md border border-border hover:bg-border transition-all"
                              title="Go back to selecting existing vehicles"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {manualTripForm.vehicle_id === '__NEW_VEHICLE__' ? (
                        <input
                          type="text"
                          required
                          value={newVehicleId}
                          onChange={(e) => setNewVehicleId(e.target.value)}
                          className="w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring hover:border-accent transition-all"
                          placeholder="Enter vehicle ID"
                        />
                      ) : (
                        <div className="relative">
                          {manualTripForm.vehicle_id && manualTripForm.vehicle_id !== '' ? (
                            <input
                              type="text"
                              readOnly
                              value={getSelectedVehicleDisplay()}
                              className="w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border pointer-events-none"
                            />
                          ) : null}
                          <select
                            required
                            value={manualTripForm.vehicle_id}
                            onChange={(e) => handleVehicleSelect(e.target.value)}
                            className={`w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring hover:border-accent transition-all cursor-pointer ${manualTripForm.vehicle_id && manualTripForm.vehicle_id !== '' ? 'absolute inset-0 opacity-0' : ''}`}
                            style={manualTripForm.vehicle_id && manualTripForm.vehicle_id !== '' ? { zIndex: 10 } : {}}
                          >
                            <option value="">Select a vehicle</option>
                            {getAvailableVehicles().map(vehicle => (
                              <option key={vehicle.id} value={vehicle.id}>
                                {redactVehicleId(vehicle.id, isDemoMode)} {vehicle.type ? `(${redactVehicleType(vehicle.type, isDemoMode)})` : ''}
                              </option>
                            ))}
                            <option value="__NEW_VEHICLE__">+ Add a new vehicle</option>
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-1">
                      <label className="block text-sm font-medium text-white mb-2">
                        Vehicle Type
                      </label>
                      {manualTripForm.vehicle_id === '__NEW_VEHICLE__' ? (
                        <>
                          {!showNewVehicleTypeInput ? (
                            <select
                              value={manualTripForm.vehicle_type}
                              onChange={(e) => {
                                if (e.target.value === '__NEW_VEHICLE_TYPE__') {
                                  setShowNewVehicleTypeInput(true);
                                  setManualTripForm({ ...manualTripForm, vehicle_type: '' });
                                } else {
                                  setManualTripForm({ ...manualTripForm, vehicle_type: e.target.value });
                                }
                              }}
                              className="w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring hover:border-accent transition-all cursor-pointer"
                            >
                              <option value="">Select vehicle type</option>
                              {getVehicleTypes().map(vehicleType => (
                                <option key={vehicleType} value={vehicleType}>
                                  {redactVehicleType(vehicleType, isDemoMode)}
                                </option>
                              ))}
                              <option value="__NEW_VEHICLE_TYPE__">+ Add a new vehicle type</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={newVehicleType}
                              onChange={(e) => {
                                setNewVehicleType(e.target.value);
                                setManualTripForm({ ...manualTripForm, vehicle_type: e.target.value });
                              }}
                              className="w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring hover:border-accent transition-all"
                              placeholder="Enter new vehicle type"
                            />
                          )}
                        </>
                      ) : (
                        <select
                          value={manualTripForm.vehicle_type}
                          disabled
                          className="w-full px-3 py-2 bg-card text-muted-foreground rounded-md border border-border cursor-not-allowed"
                        >
                          <option value={manualTripForm.vehicle_type}>
                            {manualTripForm.vehicle_type ? redactVehicleType(manualTripForm.vehicle_type, isDemoMode) : 'N/A'}
                          </option>
                        </select>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Distance (miles) <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.1"
                        value={manualTripForm.distance}
                        onChange={(e) => setManualTripForm({ ...manualTripForm, distance: e.target.value })}
                        className="w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring hover:border-accent transition-all"
                        placeholder="0.0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Duration (hours) <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.1"
                        value={manualTripForm.duration}
                        onChange={(e) => setManualTripForm({ ...manualTripForm, duration: e.target.value })}
                        className="w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring hover:border-accent transition-all"
                        placeholder="0.0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Start Location
                      </label>
                      <input
                        type="text"
                        value={manualTripForm.start_location}
                        onChange={(e) => setManualTripForm({ ...manualTripForm, start_location: e.target.value })}
                        className="w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring hover:border-accent transition-all"
                        placeholder="City or postcode"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        End Location
                      </label>
                      <input
                        type="text"
                        value={manualTripForm.end_location}
                        onChange={(e) => setManualTripForm({ ...manualTripForm, end_location: e.target.value })}
                        className="w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring hover:border-accent transition-all"
                        placeholder="City or postcode"
                      />
                    </div>

                    <div className="md:col-span-4">
                      <label className="block text-sm font-medium text-white mb-2">
                        Notes
                      </label>
                      <textarea
                        value={manualTripForm.notes}
                        onChange={(e) => setManualTripForm({ ...manualTripForm, notes: e.target.value })}
                        className="w-full px-3 py-2 bg-secondary text-foreground rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring hover:border-accent transition-all"
                        rows={2}
                        placeholder="Optional notes about this trip"
                      />
                    </div>
                  </div>

                  {tripSubmitSuccess && (
                    <div className="flex items-center gap-2 p-3 bg-success/20 border border-success/50 rounded-md text-success">
                      <CheckCircle2 className="w-5 h-5" />
                      <span>Trip entry logged successfully. Your entry has been added to the table below.</span>
                    </div>
                  )}

                  {tripSubmitError && (
                    <div className="flex items-center gap-2 p-3 bg-destructive/20 border border-destructive/50 rounded-md text-destructive">
                      <XCircle className="w-5 h-5" />
                      <span>{tripSubmitError}</span>
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowManualTripForm(false)}
                      className="px-4 py-2 text-sm font-medium text-foreground bg-secondary rounded-md hover:bg-accent transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submittingTrip}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {submittingTrip ? 'Submitting...' : 'Log Trip'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Vehicle Trips */}
        <section className="bg-card/50 rounded-lg p-6 border border-border">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">Vehicle Trips</h2>
            <p className="text-muted-foreground text-sm mb-4">
              View when trips happen throughout the day, week, and month. Helps plan shifts and allocate vehicles.
            </p>

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-muted-foreground whitespace-nowrap">View:</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedTemporalView('hourly')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border ${
                      selectedTemporalView === 'hourly'
                        ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                        : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                    }`}
                  >
                    Hourly
                  </button>
                  <button
                    onClick={() => setSelectedTemporalView('daily')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border ${
                      selectedTemporalView === 'daily'
                        ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                        : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                    }`}
                  >
                    Daily
                  </button>
                  <button
                    onClick={() => setSelectedTemporalView('monthly')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border ${
                      selectedTemporalView === 'monthly'
                        ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                        : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
                {serviceType === 'emergency_services' && getAvailableStations().length > 0 && (
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <label className="text-sm text-muted-foreground whitespace-nowrap">Station:</label>
                    <select
                      value={selectedStationForTrips || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedStationForTrips(value === '' ? null : value);
                        setSelectedTemporalVehicle('all');
                      }}
                      className={`flex-1 sm:w-48 px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                        selectedStationForTrips
                          ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                          : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                      } focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring`}
                      style={{
                        appearance: 'none',
                        backgroundImage: selectedStationForTrips 
                          ? "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%27000%27 d=%27M6 9L1 4h10z%27/%3E%3C/svg%3E')"
                          : "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%239ca3af d=%27M6 9L1 4h10z%27/%3E%3C/svg%3E')",
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center',
                        paddingRight: '2.5rem'
                      }}
                    >
                      <option value="" className="bg-card text-foreground">All Stations</option>
                      {getAvailableStations().map(station => (
                        <option key={station} value={station} className="bg-card text-foreground">
                          {redactStationName(station, isDemoMode)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {getVehicleTypes().length > 0 && (
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <label className="text-sm text-muted-foreground whitespace-nowrap">Vehicle Type:</label>
                    <select
                      value={selectedVehicleTypeForTrips || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedVehicleTypeForTrips(value === '' ? null : value);
                        setSelectedTemporalVehicle('all');
                      }}
                      className={`flex-1 sm:w-48 px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                        selectedVehicleTypeForTrips
                          ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                          : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                      } focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring`}
                      style={{
                        appearance: 'none',
                        backgroundImage: selectedVehicleTypeForTrips 
                          ? "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%27000%27 d=%27M6 9L1 4h10z%27/%3E%3C/svg%3E')"
                          : "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%239ca3af d=%27M6 9L1 4h10z%27/%3E%3C/svg%3E')",
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center',
                        paddingRight: '2.5rem'
                      }}
                    >
                      <option value="" className="bg-card text-foreground">All Vehicle Types</option>
                      {getVehicleTypes().map(vehicleType => (
                        <option key={vehicleType} value={vehicleType} className="bg-card text-foreground">
                          {redactVehicleType(vehicleType, isDemoMode)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <label className="text-sm text-muted-foreground whitespace-nowrap">Vehicle:</label>
                  <select
                    value={selectedTemporalVehicle || 'all'}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedTemporalVehicle(value === 'all' ? 'all' : value);
                    }}
                    className={`flex-1 sm:w-48 px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                      selectedTemporalVehicle && selectedTemporalVehicle !== 'all'
                        ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                        : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
                    } focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring`}
                    style={{
                      appearance: 'none',
                      backgroundImage: selectedTemporalVehicle && selectedTemporalVehicle !== 'all'
                        ? "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%27000%27 d=%27M6 9L1 4h10z%27/%3E%3C/svg%3E')"
                        : "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%239ca3af d=%27M6 9L1 4h10z%27/%3E%3C/svg%3E')",
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      paddingRight: '2.5rem'
                    }}
                  >
                    <option value="all" className="bg-card text-foreground">All Vehicles</option>
                    {getTemporalVehicleIds().map(vehicleId => (
                      <option key={vehicleId} value={vehicleId} className="bg-card text-foreground">
                        {redactVehicleId(vehicleId, isDemoMode)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full">
            {loading ? (
              <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-80 flex items-center justify-center">
                <p className="text-muted-foreground">Loading data...</p>
              </div>
            ) : data ? (
              <>
                {selectedTemporalView === 'hourly' && (
                  <BarChart
                    key={`hourly-${selectedVehicleTypeForTrips || 'all'}-${selectedStationForTrips || 'all'}-${selectedTemporalVehicle || 'all'}`}
                    title={selectedTemporalVehicle === 'all' 
                      ? (selectedStationForTrips 
                          ? (selectedVehicleTypeForTrips 
                              ? `Hourly Trips - ${selectedStationForTrips} - ${selectedVehicleTypeForTrips}` 
                              : `Hourly Trips - ${selectedStationForTrips}`)
                          : (selectedVehicleTypeForTrips 
                              ? `Hourly Trips - ${selectedVehicleTypeForTrips}` 
                              : 'Hourly Trips - All Vehicles'))
                      : 'Hourly Trips'}
                    data={getTemporalData('hourly')}
                    height="h-80"
                  />
                )}
                {selectedTemporalView === 'daily' && (
                  <BarChart
                    key={`daily-${selectedVehicleTypeForTrips || 'all'}-${selectedStationForTrips || 'all'}-${selectedTemporalVehicle || 'all'}`}
                    title={selectedTemporalVehicle === 'all' 
                      ? (selectedStationForTrips 
                          ? (selectedVehicleTypeForTrips 
                              ? `Daily Trips - ${selectedStationForTrips} - ${selectedVehicleTypeForTrips}` 
                              : `Daily Trips - ${selectedStationForTrips}`)
                          : (selectedVehicleTypeForTrips 
                              ? `Daily Trips - ${selectedVehicleTypeForTrips}` 
                              : 'Daily Trips - All Vehicles'))
                      : 'Daily Trips'}
                    data={getTemporalData('daily')}
                    height="h-80"
                  />
                )}
                {selectedTemporalView === 'monthly' && (
                  <BarChart
                    key={`monthly-${selectedVehicleTypeForTrips || 'all'}-${selectedStationForTrips || 'all'}-${selectedTemporalVehicle || 'all'}`}
                    title={selectedTemporalVehicle === 'all' 
                      ? (selectedStationForTrips 
                          ? (selectedVehicleTypeForTrips 
                              ? `Monthly Trips - ${selectedStationForTrips} - ${selectedVehicleTypeForTrips}` 
                              : `Monthly Trips - ${selectedStationForTrips}`)
                          : (selectedVehicleTypeForTrips 
                              ? `Monthly Trips - ${selectedVehicleTypeForTrips}` 
                              : 'Monthly Trips - All Vehicles'))
                      : 'Monthly Trips'}
                    data={getTemporalData('monthly')}
                    height="h-80"
                  />
                )}
              </>
            ) : (
              <div className="bg-card rounded-lg shadow-sm p-6 border border-border h-80 flex items-center justify-center">
                <p className="text-muted-foreground">Error loading data</p>
              </div>
            )}
          </div>
        </section>
        {/* Manual Trip Entries */}
        <section className="bg-card/50 rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-white">Manual Trip Entries</h4>
            <div className="flex items-center gap-3">
              {selectedEntries.size > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-destructive hover:bg-destructive hover:border-destructive hover:shadow-lg hover:shadow-destructive/50 transition-all border border-transparent group"
                  title={`Delete ${selectedEntries.size} selected ${selectedEntries.size === 1 ? 'entry' : 'entries'}`}
                >
                  <Trash2 className="w-4 h-4 group-hover:text-destructive group-hover:drop-shadow-glow-critical-lg group-hover:scale-150 transition-all duration-300" />
                  <span className="group-hover:text-destructive group-hover:drop-shadow-glow-critical-md transition-all">Delete ({selectedEntries.size})</span>
                </button>
              )}
              <button
                onClick={() => setShowManualTripForm(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
              >
                <Plus className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Trip</span>
              </button>
              <button
                onClick={() => {
                  const headers = ['Date', 'Time', 'Vehicle ID', 'Vehicle Type', 'Distance (miles)', 'Duration (hours)', 'Start Location', 'End Location', 'Notes', 'Logged At'];
                  const csv = [
                    headers.join(','),
                    ...manualTripEntries.map(entry => [
                      entry.date || '',
                      entry.time || '',
                      entry.vehicle_id || '',
                      entry.vehicle_type || '',
                      entry.distance || '0.0',
                      entry.duration || '0.0',
                      entry.start_location || '',
                      entry.end_location || '',
                      (entry.notes || '').replace(/"/g, '""'),
                      entry.created_at ? new Date(entry.created_at).toLocaleString('en-GB') : ''
                    ].map(val => `"${val}"`).join(',') )
                  ].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `manual_trip_entries_${new Date().toISOString().split('T')[0]}.csv`;
                  link.click();
                  window.URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
              >
                <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
                <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
              </button>
            </div>
          </div>
          {manualTripEntries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-card border-b border-border">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground w-12">
                      <input
                        type="checkbox"
                        checked={selectedEntries.size === manualTripEntries.length && manualTripEntries.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 rounded border-border bg-secondary text-success focus:ring-2 focus:ring-ring"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Datetime</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Vehicle ID</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Vehicle Type</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Distance (miles)</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Duration (hours)</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Start Location</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">End Location</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Notes</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Logged At</th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-border">
                  {manualTripEntries.map((entry, idx) => {
                    const dateTimeStr = entry.date && entry.time 
                      ? `${entry.date} ${entry.time}`
                      : entry.date || entry.time || '';
                    const dateTime = dateTimeStr ? new Date(dateTimeStr) : null;
                    const formattedDateTime = dateTime && !isNaN(dateTime.getTime())
                      ? dateTime.toLocaleString('en-GB')
                      : dateTimeStr || '-';
                    
                    return (
                      <tr
                        key={idx}
                        className="hover:bg-transparent transition-colors"
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedEntries.has(idx)}
                            onChange={() => handleCheckboxChange(idx)}
                            className="w-4 h-4 rounded border-border bg-secondary text-success focus:ring-2 focus:ring-ring"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-white">{formattedDateTime}</td>
                        <td className="px-4 py-3 text-sm text-white">{redactVehicleId(entry.vehicle_id || '', isDemoMode)}</td>
                        <td className="px-4 py-3 text-sm text-white">{redactVehicleType(entry.vehicle_type || '', isDemoMode)}</td>
                        <td className="px-4 py-3 text-sm text-white text-right">{entry.distance ? parseFloat(entry.distance).toFixed(1) : '0.0'}</td>
                        <td className="px-4 py-3 text-sm text-white text-right">{entry.duration ? parseFloat(entry.duration).toFixed(1) : '0.0'}</td>
                        <td className="px-4 py-3 text-sm text-white">{entry.start_location || '-'}</td>
                        <td className="px-4 py-3 text-sm text-white">{entry.end_location || '-'}</td>
                        <td className="px-4 py-3 text-sm text-white">{entry.notes || '-'}</td>
                        <td className="px-4 py-3 text-sm text-white">{entry.created_at ? new Date(entry.created_at).toLocaleString('en-GB') : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No manual trip entries yet. Click "+ Trip" to log your first trip.</p>
            </div>
          )}
        </section>

        {/* Delete Confirmation Dialog */}
        {deleteConfirm && deleteConfirm.length > 0 && createPortal(
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)}>
            <div className="bg-popover border border-border rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-white mb-4">Confirm Deletion</h3>
              <p className="text-foreground mb-6">
                Are you sure you want to remove {deleteConfirm.length} selected {deleteConfirm.length === 1 ? 'entry' : 'entries'}?
                <br />
                <span className="text-sm text-muted-foreground mt-2 block">
                  This action cannot be undone.
                </span>
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-secondary rounded-md hover:bg-accent transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteEntries}
                  className="px-4 py-2 text-sm font-medium text-white bg-destructive rounded-md hover:bg-destructive transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </section>
      </>
      )}
    </div>
  );
}
